import { ensureKgcBaseStyles } from '../../../src/ui/install.js';
import {
  renderDsInlineGroup,
  renderDsMessage,
  renderDsPluginShellFrame,
  renderDsStack,
  renderDsTableCell,
  renderDsTableRowMarkup
} from '../../../src/ui/index.js';
import {
  getDefaultThemeModeId,
  getThemeModeDefinition,
  listThemeModes
} from '../../../src/ui/theme_modes.js';
import {
  DEFAULT_THEME_PACK_ID,
  getThemeDefinition,
  isThemeLifecycleSelectable
} from '../../../src/ui/theme-packs/registry.js';
import {
  applyThemeScope,
  canApplyThemeScope,
  listThemeConsumers,
  listThemeScopes,
  resolveThemeScope
} from '../../../src/ui/theme_consumers.js';
import {
  getPersistedPlatformThemeMode,
  hasPersistedPlatformThemeMode,
  isPlatformThemeScopeId,
  setPersistedPlatformThemeMode
} from '../../../src/ui/theme_scope_state.js';
import { resolveThemeRef } from '../../../src/ui/runtime/ui-resolution.js';
import { createSettingsModalController } from './ui/adapters/createSettingsModalController.js';
import { createSettingsSelectController } from './ui/adapters/createSettingsSelectController.js';
import {
  renderSettingsButton,
  setSettingsButtonRuntimeState
} from './ui/adapters/renderSettingsButton.js';
import { renderSettingsCheckbox } from './ui/adapters/renderSettingsCheckbox.js';
import { renderSettingsInput } from './ui/adapters/renderSettingsInput.js';
import { renderSettingsNotice } from './ui/adapters/renderSettingsNotice.js';
import { renderSettingsPanel } from './ui/adapters/renderSettingsPanel.js';
import { renderSettingsSubPanel } from './ui/adapters/renderSettingsSubPanel.js';
import { renderSettingsTable } from './ui/adapters/renderSettingsTable.js';
import { renderSettingsTabs } from './ui/adapters/renderSettingsTabs.js';
import { renderSettingsDateTimeField } from './ui/adapters/renderSettingsDateTimeField.js';
import { installSettingsDateTimeFields } from './ui/adapters/installSettingsDateTimeFields.js';
import {
  getSettingsFinalUiActionAttributes,
  isSettingsReferenceV2Active,
  isSettingsSaasFinalUiActive,
  resolveSettingsFinalUiAction
} from './ui/adapters/settingsPresentationMode.js';
import {
  applySettingsThemeMode,
  getPersistedSettingsThemeMode,
  registerSettingsThemeConsumer,
  setPersistedSettingsThemeMode,
  unregisterSettingsThemeConsumer
} from './ui/theme/applySettingsThemeMode.js';
import { SETTINGS_SHELL_STYLE_TEXT } from './ui/theme/settingsShellStyleText.js';
import { SETTINGS_STRUCTURED_INSPECTOR_STYLE_TEXT } from './ui/theme/settingsStructuredInspectorStyleText.js';
import { SETTINGS_PRETTY_OVERLAY_PRESET_STYLE_TEXT } from './ui/theme/settingsPrettyOverlayPresetStyleText.js';
import { SETTINGS_DARK_OVERLAY_PRESET_STYLE_TEXT } from './ui/theme/settingsDarkOverlayPresetStyleText.js';
import { SETTINGS_MODAL_SURFACE_STYLE_TEXT } from './ui/theme/settingsModalSurfaceStyleText.js';
import {
  resolveSettingsViewPolicy
} from './_shared/settingsPolicy.js';
import {
  createSettingsContributionRuntime
} from './_shared/settingsContributionRuntime.js';
import {
  TUTOR_DESK_SAAS_FINAL_UI_SETTINGS_STYLES
} from '../../../src/ui/tokens/tutor_desk_saas_final_ui_settings_styles.js';
import {
  TUTOR_DESK_SETTINGS_REFERENCE_V2_STYLES
} from '../../../src/ui/tokens/tutor_desk_saas_final_ui_settings_reference_v2_styles.js';
import {
  renderTutorDeskDestinationSkeleton
} from '../../../src/ui/tokens/tutor_desk_saas_final_ui_transition_skeleton_styles.js';

const STYLE_ID = 'kg-settings-style-v2';
const SETTINGS_ONLY_SCOPE_ID = 'settings-only';
const FULL_APP_SCOPE_ID = 'full-app';
const LEGACY_MARKUP_TAGS = Object.freeze(['div', 'header', 'li', 'tr', 'td']);
const SETTINGS_PANEL_LABELS = Object.freeze({
  users: 'Users',
  sessions: 'Active Sessions',
  audit: 'Audit',
  usage: 'Usage',
  appearance: 'Appearance',
  archive: 'Archive'
});
const TUTOR_DESK_SETTINGS_REFERENCE_V2_PANEL_IDS = Object.freeze([
  'appearance',
  'tutor-desk.profile-business',
  'tutor-desk.schedule-defaults',
  'tutor-desk.notifications',
  'tutor-desk.workspace-views',
  'tutor-desk.students-portal',
  'tutor-desk.payments-invoicing',
  'tutor-desk.branding',
  'tutor-desk.ai-automation'
]);
const TUTOR_DESK_SETTINGS_REFERENCE_V2_PANEL_SET = new Set(TUTOR_DESK_SETTINGS_REFERENCE_V2_PANEL_IDS);
const TUTOR_DESK_SETTINGS_REFERENCE_V2_GROUPS = Object.freeze([
  Object.freeze({
    id: 'personal',
    label: 'Personal',
    panelIds: Object.freeze([
      'appearance',
      'tutor-desk.profile-business',
      'tutor-desk.schedule-defaults'
    ])
  }),
  Object.freeze({
    id: 'operations',
    label: 'Operations',
    panelIds: Object.freeze([
      'tutor-desk.notifications',
      'tutor-desk.workspace-views',
      'tutor-desk.students-portal'
    ])
  }),
  Object.freeze({
    id: 'business',
    label: 'Business',
    panelIds: Object.freeze([
      'tutor-desk.payments-invoicing',
      'tutor-desk.branding',
      'tutor-desk.ai-automation'
    ])
  })
]);
const TUTOR_DESK_SETTINGS_REFERENCE_V2_ICONS = Object.freeze({
  appearance: 'appearance',
  'tutor-desk.profile-business': 'profile',
  'tutor-desk.schedule-defaults': 'schedule',
  'tutor-desk.notifications': 'notifications',
  'tutor-desk.workspace-views': 'workspace',
  'tutor-desk.students-portal': 'portal',
  'tutor-desk.payments-invoicing': 'payments',
  'tutor-desk.branding': 'branding',
  'tutor-desk.ai-automation': 'ai',
  users: 'users',
  sessions: 'sessions',
  audit: 'audit',
  usage: 'usage',
  archive: 'archive'
});
function hydrateLegacyMarkup(value = '') {
  let html = String(value == null ? '' : value);
  LEGACY_MARKUP_TAGS.forEach((tag) => {
    html = html.split(`[#${tag}`).join('<' + tag);
  });
  return html;
}

function setElementHtml(el, html = '') {
  if (!el) return;
  el.innerHTML = hydrateLegacyMarkup(html);
}

function setOuterHtml(el, html = '') {
  if (!el) return;
  el.outerHTML = hydrateLegacyMarkup(html);
}

function normalizeLegacyMarkupConfig(input) {
  if (!input || typeof input !== 'object') return input;
  const normalized = Array.isArray(input) ? input.slice() : { ...input };
  Object.keys(normalized).forEach((key) => {
    const value = normalized[key];
    if (typeof value === 'string') {
      normalized[key] = hydrateLegacyMarkup(value);
      return;
    }
    if (Array.isArray(value)) {
      normalized[key] = value.map((item) => (typeof item === 'string' ? hydrateLegacyMarkup(item) : item));
    }
  });
  return normalized;
}

function ensureSettingsStyles() {
  if (typeof document === 'undefined') return;
  ensureKgcBaseStyles();
  const finalUi = isSettingsSaasFinalUiActive(document);
  const referenceV2 = isSettingsReferenceV2Active(document);
  const presentationId = referenceV2 ? 'settings-reference-v2' : finalUi ? 'ttoria-saas-final-ui-v1' : 'legacy';
  let style = document.getElementById(STYLE_ID);
  if (style?.dataset?.kgSettingsPresentation === presentationId) return;
  const shouldAppendStyle = !style;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
  }
  const settingsStyleText = [SETTINGS_SHELL_STYLE_TEXT, `
.kgc-stage-filters.kg-settings-nav {
  margin-top: 0;
  margin-bottom: 22px;
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.kgc-stage-filters.kg-settings-nav > * { flex: 0 0 auto; }
.kgc-stage-filters.kg-settings-nav::-webkit-scrollbar { height: 6px; }
.kgc-stage-filters.kg-settings-nav::-webkit-scrollbar-thumb {
  background: color-mix(in srgb, var(--kg-settings-border, currentColor) 75%, transparent);
  border-radius: var(--ui-radius-999, 999px);
}
.kgc-stage-filters.kg-settings-nav::-webkit-scrollbar-track { background: transparent; }
.kg-settings-panels,
.kg-settings-panel,
.kg-settings-panel-body {
  width: 100%;
}
.kg-settings-panel {
  flex: 1 1 auto;
}
.kg-settings-panel > .kg-settings-panel-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}
.kg-settings-panels { flex: 1; min-height: 0; min-width: 0; display: flex; }
.kg-settings-panel { display: none; flex: 1; min-height: 0; min-width: 0; flex-direction: column; }
.kg-settings-panel[data-active="true"] { display: flex; }
.kg-settings-root[data-active-panel="users"] .kg-settings-panel[data-panel="users"],
.kg-settings-root[data-active-panel="sessions"] .kg-settings-panel[data-panel="sessions"],
.kg-settings-root[data-active-panel="audit"] .kg-settings-panel[data-panel="audit"],
.kg-settings-root[data-active-panel="usage"] .kg-settings-panel[data-panel="usage"],
.kg-settings-root[data-active-panel="appearance"] .kg-settings-panel[data-panel="appearance"],
.kg-settings-root[data-active-panel="archive"] .kg-settings-panel[data-panel="archive"] { display: flex; }
.kg-settings-contribution-host { min-width: 0; width: 100%; }
.kg-settings-appearance-contributions { display: grid; gap: var(--ui-space-16, 16px); margin-top: var(--ui-space-16, 16px); }
.kg-settings-root .kg-settings-panel[data-panel="users"] > .kg-settings-panel-body,
.kg-settings-root .kg-settings-panel[data-panel="sessions"] > .kg-settings-panel-body {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.kg-settings-root .kg-settings-panel[data-panel="users"] > .kg-settings-panel-body > *,
.kg-settings-root .kg-settings-panel[data-panel="sessions"] > .kg-settings-panel-body > * {
  min-height: 0;
}
.kg-settings-root .kg-settings-panel[data-panel="archive"] > .kg-settings-panel-body {
  display: flex;
  flex-direction: column;
}
.kg-settings-root .kg-settings-panel[data-panel="archive"] > .kg-settings-panel-body > * {
  min-width: 0;
}

.kg-settings-panel-body {
  flex: 1;
  min-height: 0;
  min-width: 0; /* allow wide children (tables) to shrink so inner overflow-x can scroll */
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
.kg-settings-panel-body > * { min-width: 0; }

/* Ensure wide tables scroll horizontally inside their wrapper on narrow viewports. */
.kg-settings-root .kg-settings-table-wrap { width: 100%; min-width: 0; max-width: 100%; overflow-x: auto; }
.kg-settings-root .kg-settings-table-panel {
  margin-top: 10px;
  width: 100%;
  align-items: stretch;
}
.kg-settings-root .kg-settings-table-panel > .ds-panel__body {
  width: 100%;
  align-self: stretch;
  box-sizing: border-box;
  margin: 0;
  padding: 10px;
}
.kg-settings-root .kg-settings-table {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  border-radius: var(--ui-radius-16, 16px);
  overflow: hidden;
}
.kg-settings-root .kg-settings-table__grid { gap: 0; }
.kg-settings-root .kg-settings-table__cell {
  padding: 10px 12px;
  min-width: 0;
  border-bottom: 1px solid color-mix(in srgb, var(--kg-settings-border, currentColor) 64%, transparent);
  overflow-wrap: anywhere;
  word-break: break-word;
  vertical-align: top;
}
.kg-settings-root .kg-settings-table__cell--header {
  background:
    linear-gradient(
      90deg,
      var(--kg-settings-shadow-base, var(--kg-settings-text, CanvasText)),
      var(--kg-settings-surface-3, var(--kg-settings-surface-2, Canvas))
    );
  color: var(--kg-settings-contrast, Canvas);
  font-size: var(--ui-font-size-11, 11px);
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  border-bottom: 1px solid color-mix(in srgb, var(--kg-settings-shadow-base, var(--kg-settings-text, CanvasText)) 90%, transparent);
}
.kg-settings-root .kg-settings-table__row--base > .kg-settings-table__cell--body {
  background: var(--kg-settings-surface-1, Canvas);
}
.kg-settings-root .kg-settings-table__row--alt > .kg-settings-table__cell--body {
  background: var(--kg-settings-surface-2, var(--kg-settings-surface-1, Canvas));
}
.kg-settings-root .kg-settings-table__row > .kg-settings-table__cell--body {
  color: var(--kg-settings-text, CanvasText);
}
.kg-settings-root .kg-settings-table__row:last-child > .kg-settings-table__cell--body {
  border-bottom: none;
}
.kg-settings-root .kg-settings-panel[data-panel="sessions"] .kg-settings-table-panel {
  margin-top: 0;
}
.kg-settings-root .kg-settings-panel[data-panel="audit"] > .kg-settings-panel-body {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.kg-settings-root .kg-settings-panel[data-panel="audit"] > .kg-settings-panel-body > * {
  min-height: 0;
}
.kg-settings-root .kg-settings-table--sessions {
  width: 100%;
}
.kg-settings-root .kg-settings-table--sessions .kg-settings-table__grid {
  grid-template-columns: minmax(220px, 2fr) minmax(150px, 1.1fr) minmax(180px, 1.35fr) minmax(180px, 1.35fr) minmax(180px, 1.35fr);
}
.kg-settings-root .kg-settings-table--users .kg-settings-table__grid {
  grid-template-columns: minmax(280px, 1.7fr) minmax(140px, 0.85fr) minmax(140px, 0.85fr) minmax(120px, 0.8fr) minmax(320px, 1.5fr);
}
.kg-settings-root .kg-settings-table--archive-latest .kg-settings-table__grid {
  grid-template-columns: minmax(160px, 1.2fr) minmax(220px, 1.45fr) minmax(100px, 0.8fr) minmax(280px, 2fr);
}
.kg-settings-root .kg-settings-table--archive-recent .kg-settings-table__grid {
  grid-template-columns: minmax(190px, 1.2fr) minmax(150px, 1fr) minmax(90px, 0.7fr) minmax(320px, 2.2fr) minmax(170px, 1.1fr) minmax(132px, 0.8fr);
}
.kg-settings-root .kg-settings-notice { margin-top: 8px; }
.kg-settings-root .kg-settings-notice-compact { margin-top: 0; }
.kg-settings-root pre { max-width: 100%; }

.kg-settings-box {
  border-radius: var(--ui-radius-18, 18px);
  border: 1px solid color-mix(in srgb, var(--kg-settings-shadow-base, var(--kg-settings-text, CanvasText)) 90%, transparent);
  background:
    linear-gradient(
      to bottom,
      var(--kg-settings-shadow-base, var(--kg-settings-text, CanvasText)),
      var(--kg-settings-surface-3, var(--kg-settings-surface-2, Canvas))
    );
  padding: var(--ui-space-12, 12px) var(--ui-space-12, 12px) var(--ui-space-10, 10px);
  margin-bottom: 10px;
}
.kg-settings-box-title {
  font-size: var(--ui-font-size-11, 11px);
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText));
  margin-bottom: 8px;
}
.kg-settings-root .ds-card.kg-settings-box .ds-card__title {
  font-size: var(--ui-font-size-11, 11px);
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText));
}
.kg-settings-root .ds-checkbox.kg-settings-inline-checkbox .ds-checkbox__label {
  font-size: var(--ui-font-size-12, 12px);
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText));
}
.kg-settings-root .ds-checkbox.kg-settings-inline-checkbox {
  cursor: pointer;
}
.kg-settings-root .ds-checkbox.kg-settings-inline-checkbox .ds-checkbox__control {
  position: relative;
}
.kg-settings-root .ds-checkbox.kg-settings-inline-checkbox .ds-checkbox__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}
.kg-settings-root .ds-checkbox.kg-settings-inline-checkbox .ds-checkbox__native:checked + .ds-checkbox__mark {
  background: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
  border-color: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
  color: var(--kg-settings-contrast, Canvas) !important;
}
.kg-settings-root .ds-checkbox.kg-settings-inline-checkbox .ds-checkbox__native:checked + .ds-checkbox__mark::before {
  content: "\\2713";
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
}
.kg-settings-modal-backdrop .ds-checkbox.kg-settings-access-checkbox .ds-checkbox__label {
  font-family: var(--ui-font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  font-size: var(--ui-font-size-12, 12px);
  color: var(--kg-settings-text, CanvasText);
  line-height: 1.45;
}
.kg-settings-modal-backdrop .ds-checkbox.kg-settings-access-checkbox {
  cursor: pointer;
  width: 100%;
  display: flex !important;
  justify-content: flex-start !important;
  align-items: center !important;
}
.kg-settings-modal-backdrop .ds-checkbox.kg-settings-access-checkbox .ds-checkbox__control {
  position: relative;
  flex: 0 0 auto;
}
.kg-settings-modal-backdrop .ds-checkbox.kg-settings-access-checkbox .ds-checkbox__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--kg-settings-control-surface, Canvas) !important;
  border-color: color-mix(in srgb, var(--kg-settings-border, currentColor) 80%, transparent) !important;
  color: transparent !important;
  font-size: 0 !important;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}
.kg-settings-modal-backdrop .ds-checkbox.kg-settings-access-checkbox .ds-checkbox__mark::before {
  content: '';
}
.kg-settings-modal-backdrop .ds-checkbox.kg-settings-access-checkbox .ds-checkbox__native:checked + .ds-checkbox__mark {
  background: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
  border-color: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
}
.kg-settings-modal-backdrop .ds-checkbox.kg-settings-access-checkbox .ds-checkbox__native:checked + .ds-checkbox__mark::before {
  content: "\\2713";
  color: var(--kg-settings-contrast, Canvas);
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
}
.kg-settings-modal-backdrop .ds-checkbox.kg-settings-access-checkbox .ds-checkbox__label {
  text-align: left;
  flex: 1 1 auto;
}
.kg-settings-modal-backdrop .kg-settings-modal-access-layout {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
  align-items: start;
  min-height: 0;
  flex: 1 1 auto;
}
.kg-settings-modal-backdrop .kg-settings-modal-access-shell {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  height: min(48vh, 520px);
  min-height: 0;
}
.kg-settings-modal-backdrop .kg-settings-modal-access-group {
  width: 100%;
  align-items: stretch;
  height: min(38vh, 420px);
  min-height: 200px;
}
.kg-settings-modal-backdrop .kg-settings-modal-access-group > .ds-sub-panel__body {
  width: 100%;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  overflow: auto;
  padding-right: 4px;
}
.kg-settings-modal-backdrop .kg-settings-modal-access-items {
  display: grid;
  gap: 6px;
  width: 100%;
  align-content: start;
}
.kg-settings-modal-backdrop .kg-settings-modal-access-item {
  display: flex;
  justify-content: flex-start;
  min-width: 0;
  padding: 2px 0;
}
.kg-settings-modal-backdrop .kg-settings-modal-access-footer {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
}
.kg-settings-modal-backdrop .kg-settings-modal-access-group .ds-sub-panel__title {
  margin-bottom: 8px;
}
.kg-settings-root .kg-settings-tabs .ds-tabs__tab {
  transition: color 120ms ease;
}
html:not([data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-ui-surfaces~="settings"]) .kg-settings-root .kg-settings-tabs .ds-tabs__tab.is-active,
html:not([data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-ui-surfaces~="settings"]) .kg-settings-root .kg-settings-tabs .ds-tabs__tab[aria-selected="true"] {
  color: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
  opacity: 1 !important;
  font-weight: 700;
}
.kg-settings-root .kg-settings-tabs .ds-tabs__tab.is-inactive,
.kg-settings-root .kg-settings-tabs .ds-tabs__tab[aria-selected="false"] {
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText)) !important;
  opacity: 1 !important;
}
.kg-settings-root .kg-settings-tabs--nav-only .ds-tabs__panel {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
  min-width: 0 !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  overflow: hidden !important;
}
.kg-settings-root .kg-settings-tabs--nav-only {
  width: 100%;
}
.kg-settings-root .kg-settings-tabs--nav-only .ds-tabs__list {
  overflow-x: auto;
  flex-wrap: nowrap;
  max-width: 100%;
}
.kg-settings-root .kg-settings-tabs--nav-only .ds-tabs__tab {
  flex: 0 0 auto;
}
.kg-settings-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;
  align-items: start;
  margin-bottom: 12px;
}
.kg-settings-root .kg-settings-compact-section {
  width: 100%;
  align-items: stretch;
}
.kg-settings-root .kg-settings-compact-section > .ds-sub-panel__body {
  width: 100%;
  align-self: stretch;
  margin: 0;
}
.kg-settings-root .kg-settings-compact-section > .ds-sub-panel__footer {
  display: none;
}
.kg-settings-root .kg-settings-compact-section .ds-sub-panel__label {
  color: var(--kg-settings-text, CanvasText);
  font-size: var(--ui-font-size-11, 11px);
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.kg-settings-root .kgc-top {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 12px;
}
.kg-settings-root .kgc-top-left {
  flex: 1 1 420px;
  min-width: 280px;
}
.kg-settings-root .kgc-top-right {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  align-items: flex-start;
}
.kg-settings-users-layout,
.kg-settings-sessions-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
}
.kg-settings-users-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  margin-top: 0;
  margin-left: auto;
  width: 100%;
}
.kg-settings-users-toolbar-right {
  display: grid;
  grid-template-columns: minmax(360px, max-content) minmax(116px, auto) 140px;
  gap: 12px;
  align-items: center;
  width: min(100%, max-content);
}
.kg-settings-users-search-row {
  min-width: 0;
  margin-top: 0;
}
.kg-settings-users-search {
  min-width: 0;
  width: 100%;
}
.kg-settings-users-search .ds-search,
.kg-settings-users-search .ds-input {
  width: 100%;
}
.kg-settings-root .kg-settings-users-tabs {
  width: max-content;
  min-width: 360px;
  max-width: none;
}
.kg-settings-root .kg-settings-users-tabs .ds-tabs__list {
  width: max-content;
}
.kg-settings-root .kg-settings-users-tabs .ds-tabs__tab {
  justify-content: center;
}
.kg-settings-users-count-slot,
.kg-settings-users-action-slot,
.kg-settings-sessions-count-slot {
  min-width: 0;
  display: flex;
  align-items: stretch;
}
.kg-settings-users-action-slot .ds-button {
  width: 100%;
  justify-content: center;
  min-height: 40px;
}
.kg-settings-toolbar-ghost {
  min-width: 0;
  min-height: 38px;
  visibility: hidden;
  pointer-events: none;
}
.kg-settings-users-count-pill {
  width: 100%;
  min-height: 40px;
  border-radius: var(--ui-radius-14, 14px);
  border: 1px solid color-mix(in srgb, var(--kg-settings-border, currentColor) 90%, transparent);
  background: var(--kg-settings-surface-0, var(--kg-settings-bg, Canvas));
  color: var(--kg-settings-text, CanvasText);
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 14px;
  font-size: var(--ui-font-size-12, 12px);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: 0 8px 22px color-mix(in srgb, var(--kg-settings-shadow-base, var(--kg-settings-text, CanvasText)) 5%, transparent);
}
.kg-settings-users-count-pill span {
  white-space: nowrap;
}
.kg-settings-users-count-pill .value {
  font-size: var(--ui-font-size-13, 13px);
  color: var(--kg-settings-accent, var(--kg-settings-primary));
}
.kg-settings-users-control-rail {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}
.kg-settings-users-panel {
  height: 100%;
  border: 1px solid color-mix(in srgb, var(--kg-settings-border, currentColor) 96%, transparent);
  border-radius: var(--ui-radius-20, 20px);
  background:
    linear-gradient(
      180deg,
      var(--kg-settings-surface-0, var(--kg-settings-bg, Canvas)),
      var(--kg-settings-surface-1, Canvas)
    );
  box-shadow: 0 18px 34px color-mix(in srgb, var(--kg-settings-shadow-base, var(--kg-settings-text, CanvasText)) 8%, transparent);
  overflow: hidden;
}
.kg-settings-root .kg-settings-users-panel .ds-sub-panel__header {
  padding: 20px 20px 0;
}
.kg-settings-root .kg-settings-users-panel .ds-sub-panel__label {
  color: var(--kg-settings-text, CanvasText);
  font-size: clamp(14px, 1vw, 18px);
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.kg-settings-root .kg-settings-users-panel--danger .ds-sub-panel__label {
  color: var(--kg-settings-danger, currentColor);
}
.kg-settings-users-panel > .ds-sub-panel__body {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 14px 20px 20px;
  gap: 14px;
}
.kg-settings-users-panel .kg-settings-form-row {
  gap: 10px;
}
.kg-settings-users-stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
}
.kg-settings-users-copy {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 92px;
}
.kg-settings-users-copy .kg-settings-muted {
  font-size: var(--ui-font-size-13, 13px);
  line-height: 1.55;
}
.kg-settings-users-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
  align-items: stretch;
}
.kg-settings-users-field,
.kg-settings-users-action {
  min-width: 0;
}
.kg-settings-users-field--span-2 {
  grid-column: 1 / -1;
}
.kg-settings-users-action {
  display: flex;
  align-items: stretch;
}
.kg-settings-users-action .ds-button {
  width: 100%;
  justify-content: center;
  min-height: 44px;
}
.kg-settings-users-panel .kg-settings-inline-ds-host,
.kg-settings-users-panel .kg-settings-inline-ds-host > *,
.kg-settings-users-panel .kg-settings-inline-ds-host .ds-select[data-ds-component="ds/select"],
.kg-settings-users-panel .kg-settings-inline-ds-host .ds-select__control {
  width: 100%;
}
.kg-settings-users-layout .ds-input__control,
.kg-settings-users-layout .ds-search__control,
.kg-settings-users-layout .ds-select__control {
  border-radius: var(--ui-radius-14, 14px);
  box-shadow: 0 8px 20px color-mix(in srgb, var(--kg-settings-shadow-base, var(--kg-settings-text, CanvasText)) 5%, transparent);
  min-height: 44px;
}
.kg-settings-users-layout .ds-button {
  border-radius: var(--ui-radius-14, 14px);
}
.kg-settings-users-checkbox-row {
  min-height: 40px;
  display: flex;
  align-items: center;
}
.kg-settings-users-feedback {
  min-height: 48px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: flex-start;
}
.kg-settings-users-feedback > * {
  margin-top: 0 !important;
}
.kg-settings-users-panel .kg-settings-muted[id$="Status"] {
  min-height: 18px;
}
.kg-settings-users-panel .kg-settings-notice-compact {
  margin-top: 0;
}
.kg-settings-users-table-region,
.kg-settings-sessions-table-region {
  flex: 1;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
.kg-settings-users-table-host,
.kg-settings-sessions-table-host {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}
.kg-settings-users-table-host > *,
.kg-settings-sessions-table-host > * {
  min-height: 100%;
}
.kg-settings-users-table-host .kg-settings-table-panel,
.kg-settings-sessions-table-host .kg-settings-table-panel {
  height: 100%;
  display: flex;
  align-items: stretch;
  margin-top: 0;
}
.kg-settings-users-table-host .kg-settings-table-panel {
  border: 1px solid color-mix(in srgb, var(--kg-settings-border, currentColor) 96%, transparent);
  border-radius: var(--ui-radius-20, 20px);
  background: var(--kg-settings-surface-0, var(--kg-settings-bg, Canvas));
  box-shadow: 0 20px 40px color-mix(in srgb, var(--kg-settings-shadow-base, var(--kg-settings-text, CanvasText)) 8%, transparent);
  overflow: hidden;
}
.kg-settings-users-table-host .kg-settings-table-panel > .ds-panel__body,
.kg-settings-sessions-table-host .kg-settings-table-panel > .ds-panel__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.kg-settings-users-table-host .kg-settings-table-panel > .ds-panel__body {
  padding: 18px;
}
.kg-settings-users-table-host .kg-settings-table-wrap,
.kg-settings-sessions-table-host .kg-settings-table-wrap {
  display: flex;
  flex: 1;
}
.kg-settings-users-table-host .kg-settings-table,
.kg-settings-sessions-table-host .kg-settings-table {
  flex: 1;
}
.kg-settings-sessions-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px 180px 136px;
  gap: 12px;
  align-items: stretch;
}
.kg-settings-sessions-summary {
  min-width: 0;
}
.kg-settings-sessions-summary .kg-settings-meta-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 16px;
  align-items: start;
}
.kg-settings-sessions-summary .kg-settings-meta-item {
  min-width: 0;
}
.kg-settings-sessions-summary .kg-settings-meta-label {
  display: block;
  font-size: var(--ui-font-size-11, 11px);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText));
  margin-bottom: 4px;
}
.kg-settings-sessions-summary .kg-settings-meta-value {
  color: var(--kg-settings-text, CanvasText);
  font-weight: 600;
}
.kg-settings-audit-layout {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
}
.kg-settings-audit-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px 152px 136px;
  gap: 12px;
  align-items: stretch;
}
.kg-settings-audit-search {
  min-width: 0;
}
.kg-settings-root .kg-settings-audit-tabs {
  width: 180px;
  min-width: 180px;
}
.kg-settings-root .kg-settings-audit-tabs .ds-tabs__list {
  width: 100%;
  overflow: hidden;
}
.kg-settings-root .kg-settings-audit-tabs .ds-tabs__tab {
  flex: 1 1 0;
  min-width: 0;
  justify-content: center;
}
.kg-settings-audit-button-slot {
  min-width: 0;
  display: flex;
  align-items: stretch;
}
.kg-settings-audit-button-slot:empty {
  min-height: 38px;
}
.kg-settings-audit-button-slot .ds-button {
  width: 100%;
  justify-content: center;
}
.kg-settings-audit-control-rail {
  display: grid;
  grid-template-columns: minmax(0, 1.85fr) minmax(340px, 1fr);
  gap: 12px;
  align-items: stretch;
}
.kg-settings-audit-control-rail.is-compact {
  grid-template-columns: minmax(0, 1fr);
  justify-content: stretch;
}
.kg-settings-audit-panel {
  height: 100%;
}
.kg-settings-audit-filter-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 12px;
  align-items: end;
}
.kg-settings-audit-field {
  min-width: 0;
}
.kg-settings-audit-filter-actions {
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.kg-settings-audit-export-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
  align-items: end;
}
.kg-settings-audit-export-field {
  min-width: 0;
}
.kg-settings-audit-export-field--action {
  display: flex;
  align-items: flex-end;
}
.kg-settings-audit-table-region {
  flex: 1;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
.kg-settings-audit-table-host {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}
.kg-settings-audit-table-host > * {
  min-height: 100%;
}
.kg-settings-audit-table-host .kg-settings-table-panel {
  height: 100%;
  display: flex;
  align-items: stretch;
}
.kg-settings-audit-table-host .kg-settings-table-panel > .ds-panel__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.kg-settings-audit-table-host .kg-settings-table-wrap {
  display: flex;
  flex: 1;
}
.kg-settings-audit-table-host .kg-settings-table {
  flex: 1;
}
.kg-settings-root .kg-settings-panel[data-panel="audit"] .kg-settings-table-panel {
  margin-top: 0;
}
.kg-settings-root .kg-settings-panel[data-panel="usage"] > .kg-settings-panel-body {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.kg-settings-root .kg-settings-panel[data-panel="usage"] > .kg-settings-panel-body > * {
  min-height: 0;
}
.kg-settings-usage-layout {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
}
.kg-settings-usage-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px 152px 136px 136px;
  gap: 12px;
  align-items: stretch;
}
.kg-settings-usage-search {
  min-width: 0;
}
.kg-settings-root .kg-settings-usage-tabs {
  width: 180px;
  min-width: 180px;
}
.kg-settings-root .kg-settings-usage-tabs .ds-tabs__list {
  width: 100%;
  overflow: hidden;
}
.kg-settings-root .kg-settings-usage-tabs .ds-tabs__tab {
  flex: 1 1 0;
  min-width: 0;
  justify-content: center;
}
.kg-settings-usage-button-slot {
  min-width: 0;
  display: flex;
  align-items: stretch;
}
.kg-settings-usage-button-slot:empty {
  min-height: 38px;
}
.kg-settings-usage-button-slot .ds-button {
  width: 100%;
  justify-content: center;
}
.kg-settings-usage-control-rail {
  display: grid;
  grid-template-columns: minmax(0, 1.85fr) minmax(340px, 1fr);
  gap: 12px;
  align-items: stretch;
}
.kg-settings-usage-control-rail.is-compact {
  grid-template-columns: minmax(0, 1fr);
  justify-content: stretch;
}
.kg-settings-usage-panel {
  height: 100%;
}
.kg-settings-usage-filter-grid,
.kg-settings-usage-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 12px;
  align-items: end;
}
.kg-settings-usage-field {
  min-width: 0;
}
.kg-settings-usage-filter-actions {
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.kg-settings-usage-export-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
  align-items: end;
}
.kg-settings-usage-export-field {
  min-width: 0;
}
.kg-settings-usage-export-field--action {
  display: flex;
  align-items: flex-end;
}
.kg-settings-usage-summary-grid .kg-settings-muted {
  grid-column: 1 / -1;
  margin-top: 0;
}
.kg-settings-usage-table-region {
  flex: 1;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
.kg-settings-usage-table-host {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}
.kg-settings-usage-table-host > * {
  min-height: 100%;
}
.kg-settings-usage-table-host .kg-settings-table-panel {
  height: 100%;
  display: flex;
  align-items: stretch;
}
.kg-settings-usage-table-host .kg-settings-table-panel > .ds-panel__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.kg-settings-usage-table-host .kg-settings-table-wrap {
  display: flex;
  flex: 1;
}
.kg-settings-usage-table-host .kg-settings-table {
  flex: 1;
}
.kg-settings-archive-layout {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.kg-settings-archive-summary {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(320px, 1fr);
  gap: 12px;
  align-items: stretch;
}
.kg-settings-archive-panel {
  height: 100%;
}
.kg-settings-archive-copy {
  display: grid;
  gap: 6px;
}
.kg-settings-archive-kv {
  display: grid;
  grid-template-columns: minmax(0, 180px) minmax(0, 1fr);
  gap: 8px 12px;
  align-items: start;
  margin-top: 6px;
}
.kg-settings-archive-kv > dt {
  margin: 0;
  font-size: var(--ui-font-size-11, 11px);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText));
}
.kg-settings-archive-kv > dd {
  margin: 0;
  color: var(--kg-settings-text, CanvasText);
}
.kg-settings-archive-status-stack {
  display: grid;
  gap: 8px;
}
.kg-settings-archive-status-row {
  border: 1px solid color-mix(in srgb, var(--kg-settings-border, currentColor) 75%, transparent);
  border-radius: var(--ui-radius-12, 12px);
  background:
    linear-gradient(
      180deg,
      var(--kg-settings-surface-1, Canvas),
      var(--kg-settings-surface-2, var(--kg-settings-surface-1, Canvas))
    );
  padding: 10px 12px;
}
.kg-settings-archive-status-row.is-warning {
  border-color: color-mix(in srgb, var(--kg-settings-warning, currentColor) 38%, transparent);
  background:
    linear-gradient(
      180deg,
      var(--kg-settings-pill-warning-bg, var(--kg-settings-surface-1, Canvas)),
      color-mix(in srgb, var(--kg-settings-pill-warning-bg, var(--kg-settings-surface-1, Canvas)) 82%, var(--kg-settings-surface-2, Canvas))
    );
}
.kg-settings-archive-status-row.is-critical {
  border-color: color-mix(in srgb, var(--kg-settings-danger, currentColor) 34%, transparent);
  background:
    linear-gradient(
      180deg,
      var(--kg-settings-pill-danger-bg, var(--kg-settings-surface-1, Canvas)),
      color-mix(in srgb, var(--kg-settings-pill-danger-bg, var(--kg-settings-surface-1, Canvas)) 82%, var(--kg-settings-surface-2, Canvas))
    );
}
.kg-settings-archive-status-label {
  display: block;
  margin-bottom: 3px;
  font-size: var(--ui-font-size-11, 11px);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText));
}
.kg-settings-archive-status-body {
  color: var(--kg-settings-text, CanvasText);
}
.kg-settings-archive-status-body .mono {
  color: var(--kg-settings-text, CanvasText);
}
.kg-settings-archive-tables {
  display: grid;
  gap: 12px;
}
.kg-settings-archive-empty {
  border: 1px dashed color-mix(in srgb, var(--kg-settings-border, currentColor) 66%, transparent);
  border-radius: var(--ui-radius-14, 14px);
  padding: 14px 16px;
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText));
  background: color-mix(in srgb, var(--kg-settings-surface-1, Canvas) 72%, transparent);
}
.kg-settings-form-row { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.kg-settings-form-col { flex: 1; min-width: 220px; }
.kg-settings-form-col.mini { flex: 0 0 200px; min-width: 200px; }
.kg-settings-datetime-field {
  width: min(100%, 360px);
}
.kg-settings-datetime-field--mini {
  width: min(100%, 360px);
}

.kg-settings-ds-field-host {
  min-height: 86px;
  display: grid;
  align-content: start;
}

.kg-settings-ds-preview-host {
  margin-top: 10px;
}

.kg-settings-inline-ds-host {
  display: grid;
  align-content: start;
}

.kg-settings-appearance-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(320px, 0.95fr);
  gap: 12px;
  align-items: start;
}
.kg-settings-appearance-main,
.kg-settings-appearance-side {
  min-width: 0;
}
.kg-settings-appearance-panel {
  height: 100%;
}
.kg-settings-appearance-panel > .ds-sub-panel__body {
  display: grid;
  gap: 12px;
}
.kg-settings-appearance-intro,
.kg-settings-appearance-note {
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText));
  line-height: 1.55;
  margin: 0;
}
.kg-settings-appearance-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-items: start;
}
.kg-settings-appearance-field {
  min-width: 0;
}
.kg-settings-appearance-actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.kg-settings-appearance-actions .ds-button {
  width: 100%;
  justify-content: center;
}
.kg-settings-appearance-inline-controls {
  display: grid;
  gap: 12px;
  padding-top: 12px;
}
.kg-settings-appearance-selection {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: baseline;
}
.kg-settings-appearance-status {
  min-height: 20px;
}
.kg-settings-appearance-summary-copy {
  margin-top: 2px;
}
.kg-settings-appearance-meta-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.kg-settings-pack-grid--appearance {
  width: 100%;
  max-width: none;
  margin: 0;
}
.kg-settings-pack-grid--appearance .kg-settings-pack-card {
  min-height: 188px;
}

.kg-settings-root .ds-input__label:empty,
.kg-settings-root .ds-input__helper:empty,
.kg-settings-root .ds-search__label:empty,
.kg-settings-root .ds-search__helper:empty,
.kg-settings-root .ds-select__label:empty,
.kg-settings-root .ds-select__helper:empty,
.kg-settings-root .ds-date-time-picker__label:empty,
.kg-settings-root .ds-date-time-picker__helper:empty {
  display: none;
}

.kg-settings-label {
  display:block;
  font-size: var(--ui-font-size-12, 12px);
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText));
  margin-bottom: 4px;
}
.kg-settings-input,
.kg-settings-select {
  width: 100%;
  border-radius: var(--ui-radius-999, 999px);
  border: 1px solid color-mix(in srgb, var(--kg-settings-border, currentColor) 45%, transparent);
  background: var(--kg-settings-control-surface, ButtonFace);
  color: var(--kg-settings-control-text, ButtonText);
  font-size: var(--ui-font-size-13, 13px);
  padding: var(--ui-space-08, 8px) var(--ui-space-14, 14px);
  outline: none;
}
.kg-settings-input::placeholder { color: var(--kg-settings-control-placeholder, GrayText); }
.kg-settings-select { padding-right: var(--ui-space-36, 36px); }
.kg-settings-input.mini { padding: var(--ui-space-07, 7px) var(--ui-space-12, 12px); font-size: var(--ui-font-size-12, 12px); }

.kg-settings-badges { display: inline-flex; gap: 6px; margin-left: 8px; }
.kg-settings-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: var(--ui-space-02, 2px) var(--ui-space-10, 10px);
  border-radius: var(--ui-radius-999, 999px);
  font-size: var(--ui-font-size-11, 11px);
  line-height: 1.2;
  border: 1px solid color-mix(in srgb, var(--kg-settings-border, currentColor) 66%, transparent);
  background: var(--kg-settings-surface-2, var(--kg-settings-surface-1, Canvas));
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText));
  white-space: nowrap;
}
.kg-settings-badge.admin,
.kg-settings-badge.mfa-on,
.kg-settings-badge.status-active,
.kg-settings-badge.current-user {
  border-color: color-mix(in srgb, var(--kg-settings-accent, var(--kg-settings-primary)) 28%, transparent);
  background: var(--kg-settings-accent-soft, var(--kg-settings-surface-2, Canvas));
  color: var(--kg-settings-accent, var(--kg-settings-primary));
}
.kg-settings-badge.mfa-off {
  border-color: color-mix(in srgb, var(--kg-settings-danger, currentColor) 34%, transparent);
  background: var(--kg-settings-pill-danger-bg, var(--kg-settings-surface-2, Canvas));
  color: var(--kg-settings-pill-danger-text, var(--kg-settings-danger, currentColor));
}
.kg-settings-user-email {
  font-weight: 600;
  line-height: 1.45;
  color: var(--kg-settings-text, CanvasText);
}
.kg-settings-user-role {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.kg-settings-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  justify-items: stretch;
  width: 100%;
  min-width: 0;
}
.kg-settings-actions > * { min-width: 0; }
.kg-settings-actions .ds-button {
  width: 100%;
  justify-content: center;
  min-height: 40px;
}
.kg-settings-actions .ds-button--intent-danger {
  grid-column: 1 / -1;
}
.kg-settings-actions [data-action] { padding: var(--ui-space-06, 6px) var(--ui-space-10, 10px); font-size: var(--ui-font-size-12, 12px); }

.kg-settings-root .ds-button--intent-primary { color: var(--kg-settings-contrast, Canvas) !important; }
.kg-settings-root .ds-button--intent-secondary { color: var(--kg-settings-text, CanvasText) !important; }
.kg-settings-root .ds-button--intent-danger {
  color: var(--kg-settings-pill-danger-text, var(--kg-settings-danger, ButtonText)) !important;
}
.kg-settings-root .ds-date-time-picker__input { color: var(--kg-settings-text, CanvasText) !important; }
.kg-settings-root .kg-settings-table--users .kg-settings-table__grid {
  grid-template-columns: minmax(280px, 1.7fr) minmax(220px, 1.25fr) minmax(140px, 0.85fr) minmax(150px, 0.9fr) minmax(320px, 1.55fr);
}
.kg-settings-root .kg-settings-table--users .kg-settings-table__cell {
  padding: 16px;
}

.kg-settings-muted {
  font-size: var(--ui-font-size-12, 12px);
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText));
}
.kg-settings-error {
  font-size: var(--ui-font-size-12, 12px);
  color: var(--kg-settings-danger, currentColor);
}
.kg-settings-ok {
  font-size: var(--ui-font-size-12, 12px);
  color: var(--kg-settings-success, currentColor);
}

@media (max-width: 1280px) {
  .kg-settings-sessions-toolbar {
    grid-template-columns: minmax(0, 1fr);
  }
  .kg-settings-root .kg-settings-users-tabs {
    width: 100%;
    min-width: 0;
    max-width: none;
  }
  .kg-settings-root .kg-settings-users-tabs .ds-tabs__list {
    width: 100%;
    overflow-x: auto;
  }
  .kg-settings-users-toolbar {
    justify-content: stretch;
    margin-top: 0;
    margin-left: 0;
  }
  .kg-settings-users-toolbar-right {
    grid-template-columns: minmax(0, 1fr);
    width: 100%;
  }
  .kg-settings-users-count-slot,
  .kg-settings-users-action-slot {
    width: 100%;
  }
  .kg-settings-users-control-rail {
    grid-template-columns: minmax(0, 1fr);
  }
  .kg-settings-users-form-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .kg-settings-users-field--span-2 {
    grid-column: auto;
  }
  .kg-settings-audit-toolbar {
    grid-template-columns: minmax(0, 1fr);
  }
  .kg-settings-root .kg-settings-audit-tabs {
    width: 180px;
    min-width: 180px;
  }
  .kg-settings-audit-control-rail,
  .kg-settings-audit-control-rail.is-compact {
    grid-template-columns: minmax(0, 1fr);
    justify-content: stretch;
  }
  .kg-settings-audit-filter-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .kg-settings-audit-export-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .kg-settings-usage-toolbar {
    grid-template-columns: minmax(0, 1fr);
  }
  .kg-settings-root .kg-settings-usage-tabs {
    width: 180px;
    min-width: 180px;
  }
  .kg-settings-usage-control-rail,
  .kg-settings-usage-control-rail.is-compact {
    grid-template-columns: minmax(0, 1fr);
    justify-content: stretch;
  }
  .kg-settings-usage-filter-grid,
  .kg-settings-usage-export-grid,
  .kg-settings-usage-summary-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .kg-settings-archive-summary,
  .kg-settings-archive-kv {
    grid-template-columns: minmax(0, 1fr);
  }
  .kg-settings-root .kg-settings-table__cell {
    padding: 8px 10px;
    font-size: var(--ui-font-size-12, 12px);
  }
  .kg-settings-root .kg-settings-table__cell--header {
    font-size: var(--ui-font-size-10, 10px);
  }
  .kg-settings-root .kg-settings-table--sessions .kg-settings-table__grid {
    grid-template-columns: minmax(190px, 2fr) minmax(130px, 1fr) minmax(150px, 1.2fr) minmax(150px, 1.2fr) minmax(150px, 1.2fr);
  }
  .kg-settings-actions {
    grid-template-columns: minmax(0, 1fr);
  }
}

.kg-settings-root .mono {
  font-family: ui-monospace, Menlo, Monaco, "Courier New", monospace;
  font-size: var(--ui-font-size-12, 12px);
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle, GrayText));
  word-break: break-all;
}

.kg-settings-pack-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  margin-top: 10px;
}
.kg-settings-pack-card {
  border: 1px solid color-mix(in srgb, var(--kg-settings-border, currentColor) 80%, transparent);
  border-radius: var(--ui-radius-12, 12px);
  background: color-mix(in srgb, var(--kg-settings-surface-3, Canvas) 65%, transparent);
  padding: var(--ui-space-10, 10px);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.kg-settings-pack-card.active {
  border-color: color-mix(in srgb, var(--kg-settings-accent, var(--kg-settings-primary)) 55%, transparent);
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--kg-settings-accent, var(--kg-settings-primary)) 25%, transparent) inset;
}
.kg-settings-pack-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: var(--kg-settings-contrast, Canvas);
  font-size: var(--ui-font-size-12, 12px);
  font-weight: 600;
}
.kg-settings-pack-description {
  font-size: var(--ui-font-size-11, 11px);
  color: var(--kg-settings-control-placeholder, GrayText);
}
.kg-settings-pack-swatches {
  display: flex;
  gap: 6px;
}
.kg-settings-pack-swatches span {
  width: 18px;
  height: 18px;
  border-radius: var(--ui-radius-999, 999px);
  border: 1px solid color-mix(in srgb, var(--kg-settings-border, currentColor) 72%, transparent);
}

${SETTINGS_MODAL_SURFACE_STYLE_TEXT}
@media (max-width: 1280px) {
  .kg-settings-modal-backdrop .kg-settings-modal-access-layout {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 900px) {
  .kg-settings-modal-frame-host {
    width: min(96vw, 720px);
    max-height: min(88vh, 760px);
  }
  .kg-settings-modal-frame-host > .ds-modal {
    width: min(96vw, 720px) !important;
    max-height: min(88vh, 760px);
  }
  .kg-settings-modal-backdrop .kg-settings-modal-access-shell {
    height: auto;
    max-height: none;
  }
  .kg-settings-modal-backdrop .kg-settings-modal-access-layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .kg-settings-modal-backdrop .kg-settings-modal-access-group {
    height: auto;
    min-height: 0;
    max-height: none;
  }
  .kg-settings-modal-backdrop .kg-settings-modal-access-group > .ds-sub-panel__body {
    max-height: none;
    overflow: visible;
  }
}

.kg-settings-root.kg-settings-shell-panel > .ds-panel__body,
.kg-settings-root .kg-settings-shell-panel > .ds-panel__body,
.kg-settings-root .kg-settings-shell-body,
.kg-settings-root .kg-settings-panel-body {
  background: transparent;
  color: var(--kg-settings-text);
}

.kg-settings-root .kg-settings-shell-title,
.kg-settings-root .kg-settings-table__row > .kg-settings-table__cell--body,
.kg-settings-root .kg-settings-access-summary,
.kg-settings-root .kg-settings-user-summary,
.kg-settings-root .kg-settings-modal-backdrop .ds-checkbox.kg-settings-access-checkbox .ds-checkbox__label,
.kg-settings-root .ds-date-time-picker__input {
  color: var(--kg-settings-text) !important;
}

.kg-settings-root .kg-settings-shell-sub,
.kg-settings-root .kg-settings-label,
.kg-settings-root .kg-settings-muted,
.kg-settings-root .kg-settings-box-title,
.kg-settings-root .ds-card.kg-settings-box .ds-card__title,
.kg-settings-root .ds-checkbox.kg-settings-inline-checkbox .ds-checkbox__label,
.kg-settings-root .kg-settings-modal-backdrop .modal-message,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-access-summary-label {
  color: var(--kg-settings-text-subtle) !important;
}

.kg-settings-root .kg-settings-error {
  color: var(--kg-settings-danger) !important;
}

.kg-settings-root .kg-settings-ok {
  color: var(--kg-settings-pill-positive-text) !important;
}

.kg-settings-root .kg-settings-box,
.kg-settings-root .kg-settings-table-panel > .ds-panel__body,
.kg-settings-root .kg-settings-pack-card,
.kg-settings-root .kg-settings-users-summary,
.kg-settings-root .kg-settings-user-card,
.kg-settings-root .kg-settings-meta-item,
.kg-settings-root .kg-settings-archive-panel > .ds-sub-panel__body,
.kg-settings-root .kg-settings-audit-panel > .ds-sub-panel__body,
.kg-settings-root .kg-settings-usage-panel > .ds-sub-panel__body,
.kg-settings-root .kg-settings-sessions-summary > .ds-sub-panel__body {
  background: var(--kg-settings-surface-1) !important;
  border-color: var(--kg-settings-border) !important;
  box-shadow: var(--kg-settings-shadow-card) !important;
  color: var(--kg-settings-text);
}

.kg-settings-root .kg-settings-table,
.kg-settings-root .kg-settings-table__cell,
.kg-settings-root .kg-settings-table__row > .kg-settings-table__cell--body {
  border-color: var(--kg-settings-table-row-border) !important;
}

.kg-settings-root .kg-settings-table__cell--header {
  background: var(--kg-settings-table-header-bg) !important;
  border-bottom-color: var(--kg-settings-border) !important;
  color: var(--kg-settings-table-header-text) !important;
}

.kg-settings-root .kg-settings-table__row--base > .kg-settings-table__cell--body {
  background: var(--kg-settings-surface-2) !important;
}

.kg-settings-root .kg-settings-table__row--alt > .kg-settings-table__cell--body {
  background: var(--kg-settings-surface-3) !important;
}

.kg-settings-root .kg-settings-badge,
.kg-settings-root .kg-settings-structured-note,
.kg-settings-root .kg-settings-structured-empty {
  background: var(--kg-settings-pill-default-bg) !important;
  border-color: var(--kg-settings-pill-default-border) !important;
  color: var(--kg-settings-pill-default-text) !important;
}

.kg-settings-root .kg-settings-badge.admin,
.kg-settings-root .kg-settings-badge.mfa-on {
  background: var(--kg-settings-pill-positive-bg) !important;
  border-color: var(--kg-settings-pill-positive-border) !important;
  color: var(--kg-settings-pill-positive-text) !important;
}

.kg-settings-root .kg-settings-badge.mfa-off,
.kg-settings-root .kg-settings-badge.status-active {
  background: var(--kg-settings-pill-warning-bg) !important;
  border-color: var(--kg-settings-pill-warning-border) !important;
  color: var(--kg-settings-pill-warning-text) !important;
}

.kg-settings-root .kg-settings-badge.current-user {
  background: var(--kg-settings-pill-current-bg) !important;
  border-color: var(--kg-settings-pill-current-border) !important;
  color: var(--kg-settings-pill-current-text) !important;
}

.kg-settings-root .kg-settings-input,
.kg-settings-root input:not([type="checkbox"]):not([type="radio"]),
.kg-settings-root select,
.kg-settings-root textarea,
.kg-settings-root .ds-date-time-picker__input,
.kg-settings-root .ds-select__trigger {
  background: var(--kg-settings-control-surface) !important;
  border-color: var(--kg-settings-control-border) !important;
  box-shadow: var(--kg-settings-shadow-control-idle) !important;
  color: var(--kg-settings-control-text) !important;
  transition: border-color var(--kg-settings-transition-fast), box-shadow var(--kg-settings-transition-fast), background var(--kg-settings-transition-fast), color var(--kg-settings-transition-fast) !important;
}

.kg-settings-root .kg-settings-input::placeholder,
.kg-settings-root input::placeholder,
.kg-settings-root textarea::placeholder {
  color: var(--kg-settings-control-placeholder) !important;
}

.kg-settings-root .kg-settings-input:focus,
.kg-settings-root input:not([type="checkbox"]):not([type="radio"]):focus,
.kg-settings-root select:focus,
.kg-settings-root textarea:focus,
.kg-settings-root .ds-date-time-picker__input:focus,
.kg-settings-root .ds-select__trigger:focus {
  box-shadow: var(--kg-settings-focus-ring) !important;
  outline: none;
}

.kg-settings-root .ds-button {
  transition: background var(--kg-settings-transition-fast), border-color var(--kg-settings-transition-fast), box-shadow var(--kg-settings-transition-fast), color var(--kg-settings-transition-fast) !important;
  box-shadow: var(--kg-settings-shadow-control-idle) !important;
}

.kg-settings-root .ds-button:hover {
  box-shadow: var(--kg-settings-shadow-control-hover) !important;
}

.kg-settings-root .ds-button:active {
  box-shadow: var(--kg-settings-shadow-control-active) !important;
}

.kg-settings-root .ds-button:focus-visible {
  box-shadow: var(--kg-settings-shadow-control-hover), var(--kg-settings-focus-ring) !important;
}

.kg-settings-root .ds-button--intent-primary {
  background: var(--kg-settings-primary) !important;
  border-color: var(--kg-settings-primary) !important;
  color: var(--kg-settings-primary-text) !important;
}

.kg-settings-root .ds-button--intent-primary:hover {
  background: var(--kg-settings-primary-hover) !important;
  border-color: var(--kg-settings-primary-hover) !important;
}

.kg-settings-root .ds-button--intent-primary:active {
  background: var(--kg-settings-primary-active) !important;
  border-color: var(--kg-settings-primary-active) !important;
}

.kg-settings-root .ds-button--intent-secondary {
  background: var(--kg-settings-control-surface) !important;
  border-color: var(--kg-settings-control-border) !important;
  color: var(--kg-settings-control-text) !important;
}

.kg-settings-root .ds-button--intent-secondary:hover {
  border-color: var(--kg-settings-primary-hover) !important;
}

.kg-settings-root .ds-button--intent-danger {
  background: var(--kg-settings-danger) !important;
  border-color: var(--kg-settings-danger) !important;
  color: var(--kg-settings-danger-text) !important;
}

.kg-settings-root .ds-button--intent-danger:hover {
  background: var(--kg-settings-danger-hover) !important;
  border-color: var(--kg-settings-danger-hover) !important;
}

.kg-settings-root .ds-button--intent-danger:active {
  background: var(--kg-settings-danger-active) !important;
  border-color: var(--kg-settings-danger-active) !important;
}

.kg-settings-root .ds-checkbox .ds-checkbox__mark {
  background: var(--kg-settings-control-surface) !important;
  border-color: var(--kg-settings-control-border) !important;
  color: transparent !important;
  transition: background-color var(--kg-settings-transition-fast), border-color var(--kg-settings-transition-fast), color var(--kg-settings-transition-fast) !important;
}

.kg-settings-root .ds-checkbox .ds-checkbox__native:checked + .ds-checkbox__mark {
  background: var(--kg-settings-primary) !important;
  border-color: var(--kg-settings-primary) !important;
  color: var(--kg-settings-primary-text) !important;
}

.kg-settings-root .kg-settings-modal-backdrop .modal-card,
.kg-settings-root .kg-settings-modal-backdrop .modal-shell,
.kg-settings-root .kg-settings-modal-backdrop .ds-sub-panel,
.kg-settings-root .kg-settings-modal-backdrop .ds-panel,
.kg-settings-root .kg-settings-modal-backdrop .ds-card {
  background: var(--kg-settings-modal-surface) !important;
  border-color: var(--kg-settings-modal-border) !important;
  color: var(--kg-settings-modal-text) !important;
  box-shadow: var(--kg-settings-shadow-card) !important;
}

.kg-settings-root.kg-settings-shell-panel > .ds-panel__body,
.kg-settings-root .kg-settings-shell-panel > .ds-panel__body,
.kg-settings-root .kg-settings-shell-body,
.kg-settings-root .kg-settings-panel-body {
  background: transparent !important;
  color: var(--kg-settings-text) !important;
}

.kg-settings-root {
  background: var(--kg-settings-surface-1) !important;
}

.kg-settings-root.kg-settings-shell-panel,
.kg-settings-root .kg-settings-shell-panel {
  border-color: var(--kg-settings-border) !important;
  box-shadow: var(--kg-settings-shadow-card) !important;
}

.kg-settings-root .ds-panel[data-ds-component="ds/panel"],
.kg-settings-root .ds-card[data-ds-component="ds/card"],
.kg-settings-root .ds-sub-panel[data-ds-component="ds/sub-panel"] {
  background: var(--kg-settings-surface-1) !important;
  border-color: var(--kg-settings-border) !important;
  box-shadow: var(--kg-settings-shadow-card) !important;
  color: var(--kg-settings-text) !important;
}

.kg-settings-root .ds-panel__body,
.kg-settings-root .ds-card__body,
.kg-settings-root .ds-sub-panel__body,
.kg-settings-root .ds-panel__footer,
.kg-settings-root .ds-card__footer,
.kg-settings-root .ds-sub-panel__footer {
  background: transparent !important;
  color: inherit !important;
}

.kg-settings-root .kg-settings-shell-title,
.kg-settings-root .kg-settings-access-summary,
.kg-settings-root .kg-settings-user-summary,
.kg-settings-root .kg-settings-user-email,
.kg-settings-root .kg-settings-meta-value,
.kg-settings-root .kg-settings-archive-kv > dd,
.kg-settings-root .kg-settings-archive-status-body,
.kg-settings-root .kg-settings-pack-title,
.kg-settings-root .kg-settings-modal-backdrop .modal-title,
.kg-settings-root .kg-settings-modal-backdrop .ds-checkbox.kg-settings-access-checkbox .ds-checkbox__label,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-key,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-value,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-table td,
.kg-settings-root .ds-panel__label,
.kg-settings-root .ds-sub-panel__label,
.kg-settings-root .ds-card__title,
.kg-settings-root .ds-button .ds-button__label,
.kg-settings-root .ds-input__value,
.kg-settings-root .ds-search__input,
.kg-settings-root .ds-select__native,
.kg-settings-root .ds-date-time-picker__input,
.kg-settings-root .mono {
  color: var(--kg-settings-text) !important;
}

.kg-settings-root .kg-settings-shell-sub,
.kg-settings-root .kg-settings-label,
.kg-settings-root .kg-settings-muted,
.kg-settings-root .kg-settings-box-title,
.kg-settings-root .kg-settings-meta-label,
.kg-settings-root .kg-settings-archive-kv > dt,
.kg-settings-root .kg-settings-archive-status-label,
.kg-settings-root .kg-settings-pack-description,
.kg-settings-root .ds-checkbox.kg-settings-inline-checkbox .ds-checkbox__label,
.kg-settings-root .ds-input__label,
.kg-settings-root .ds-input__helper,
.kg-settings-root .ds-search__label,
.kg-settings-root .ds-search__helper,
.kg-settings-root .ds-select__label,
.kg-settings-root .ds-select__helper,
.kg-settings-root .ds-date-time-picker__label,
.kg-settings-root .ds-date-time-picker__helper,
.kg-settings-root .kg-settings-modal-backdrop .modal-message,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-access-summary-label,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-note,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-empty,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-table th,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-summary {
  color: var(--kg-settings-text-subtle) !important;
}

.kg-settings-root .kg-settings-users-count-pill,
.kg-settings-root .kg-settings-users-panel,
.kg-settings-root .kg-settings-users-table-host .kg-settings-table-panel,
.kg-settings-root .kg-settings-audit-panel,
.kg-settings-root .kg-settings-usage-panel,
.kg-settings-root .kg-settings-archive-panel,
.kg-settings-root .kg-settings-sessions-summary,
.kg-settings-root .kg-settings-box,
.kg-settings-root .kg-settings-pack-card,
.kg-settings-root .kg-settings-meta-item,
.kg-settings-root .kg-settings-archive-status-row,
.kg-settings-root .kg-settings-archive-empty {
  background: var(--kg-settings-surface-2) !important;
  border-color: var(--kg-settings-border) !important;
  box-shadow: var(--kg-settings-shadow-card) !important;
  color: var(--kg-settings-text) !important;
}

.kg-settings-root .kg-settings-users-panel .ds-sub-panel__label,
.kg-settings-root .kg-settings-compact-section .ds-sub-panel__label {
  color: var(--kg-settings-text) !important;
}

.kg-settings-root .kg-settings-users-panel--danger .ds-sub-panel__label {
  color: var(--kg-settings-danger) !important;
}

.kg-settings-root .kg-settings-users-count-pill .value {
  color: var(--kg-settings-primary) !important;
}

.kg-settings-root .kg-settings-archive-status-row.is-warning {
  border-color: var(--kg-settings-pill-warning-border) !important;
}

.kg-settings-root .kg-settings-archive-status-row.is-critical {
  border-color: var(--kg-settings-danger) !important;
}

.kg-settings-root .kg-settings-table,
.kg-settings-root .kg-settings-table__cell,
.kg-settings-root .ds-table-advanced__cell,
.kg-settings-root .kg-settings-table__row > .ds-table-advanced__cell--body {
  border-color: var(--kg-settings-table-row-border) !important;
}

.kg-settings-root .kg-settings-table__cell--header {
  background: var(--kg-settings-table-header-bg) !important;
  border-bottom-color: var(--kg-settings-border) !important;
  color: var(--kg-settings-table-header-text) !important;
}

.kg-settings-root .kg-settings-table__row--base > .ds-table-advanced__cell--body {
  background: var(--kg-settings-surface-1) !important;
}

.kg-settings-root .kg-settings-table__row--alt > .ds-table-advanced__cell--body {
  background: var(--kg-settings-surface-2) !important;
}

.kg-settings-root .kg-settings-table__row:hover > .ds-table-advanced__cell--body {
  background: var(--kg-settings-surface-3) !important;
}

.kg-settings-root .kg-settings-table__row > .ds-table-advanced__cell--body {
  color: var(--kg-settings-text) !important;
}

.kg-settings-root .kg-settings-badge,
.kg-settings-root .kg-settings-structured-note,
.kg-settings-root .kg-settings-structured-empty,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-pill {
  background: var(--kg-settings-pill-default-bg) !important;
  border-color: var(--kg-settings-pill-default-border) !important;
  color: var(--kg-settings-pill-default-text) !important;
}

.kg-settings-root .kg-settings-badge.admin,
.kg-settings-root .kg-settings-badge.mfa-on {
  background: var(--kg-settings-pill-positive-bg) !important;
  border-color: var(--kg-settings-pill-positive-border) !important;
  color: var(--kg-settings-pill-positive-text) !important;
}

.kg-settings-root .kg-settings-badge.mfa-off,
.kg-settings-root .kg-settings-badge.status-active {
  background: var(--kg-settings-pill-warning-bg) !important;
  border-color: var(--kg-settings-pill-warning-border) !important;
  color: var(--kg-settings-pill-warning-text) !important;
}

.kg-settings-root .kg-settings-badge.current-user {
  background: var(--kg-settings-pill-current-bg) !important;
  border-color: var(--kg-settings-pill-current-border) !important;
  color: var(--kg-settings-pill-current-text) !important;
}

.kg-settings-root .kg-settings-tabs .ds-tabs__list {
  background: var(--kg-settings-surface-2) !important;
  border-color: var(--kg-settings-border) !important;
  box-shadow: none !important;
}

.kg-settings-root .kg-settings-tabs .ds-tabs__tab {
  color: var(--kg-settings-text-subtle) !important;
  border-color: var(--kg-settings-border) !important;
  transition: background var(--kg-settings-transition-fast), color var(--kg-settings-transition-fast), border-color var(--kg-settings-transition-fast), box-shadow var(--kg-settings-transition-fast) !important;
}

.kg-settings-root .kg-settings-tabs .ds-tabs__tab:hover {
  background: var(--kg-settings-surface-3) !important;
  color: var(--kg-settings-text) !important;
}

html:not([data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-ui-surfaces~="settings"]) .kg-settings-root .kg-settings-tabs .ds-tabs__tab.is-active,
html:not([data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-ui-surfaces~="settings"]) .kg-settings-root .kg-settings-tabs .ds-tabs__tab[aria-selected="true"] {
  background: var(--kg-settings-surface-1) !important;
  color: var(--kg-settings-primary) !important;
  box-shadow: inset 0 0 0 1px var(--kg-settings-border), var(--kg-settings-shadow-control-idle) !important;
}

.kg-settings-root .kg-settings-tabs .ds-tabs__tab:focus-visible {
  box-shadow: inset 0 0 0 1px var(--kg-settings-border), var(--kg-settings-focus-ring) !important;
  outline: none;
}

.kg-settings-root .kg-settings-input,
.kg-settings-root .kg-settings-select,
.kg-settings-root textarea,
.kg-settings-root .ds-input__control,
.kg-settings-root .ds-select__control,
.kg-settings-root .ds-date-time-picker__input,
.kg-settings-root .ds-search__input {
  background: var(--kg-settings-control-surface) !important;
  border-color: var(--kg-settings-control-border) !important;
  box-shadow: var(--kg-settings-shadow-control-idle) !important;
  color: var(--kg-settings-control-text) !important;
  transition: border-color var(--kg-settings-transition-fast), box-shadow var(--kg-settings-transition-fast), background var(--kg-settings-transition-fast), color var(--kg-settings-transition-fast) !important;
}

.kg-settings-root .ds-input__value,
.kg-settings-root .ds-search__input,
.kg-settings-root .ds-select__native,
.kg-settings-root .ds-date-time-picker__input,
.kg-settings-root .kg-settings-input,
.kg-settings-root textarea {
  background: transparent !important;
  color: var(--kg-settings-control-text) !important;
}

.kg-settings-root .kg-settings-input::placeholder,
.kg-settings-root .ds-input__value::placeholder,
.kg-settings-root .ds-search__input::placeholder,
.kg-settings-root .ds-date-time-picker__input::placeholder,
.kg-settings-root textarea::placeholder {
  color: var(--kg-settings-control-placeholder) !important;
}

.kg-settings-root .kg-settings-input:focus,
.kg-settings-root textarea:focus,
.kg-settings-root .ds-input__control:focus-within,
.kg-settings-root .ds-select__control:focus-within,
.kg-settings-root .ds-date-time-picker__input:focus,
.kg-settings-root .ds-search__input:focus {
  border-color: var(--kg-settings-primary) !important;
  box-shadow: var(--kg-settings-focus-ring) !important;
  outline: none;
}

.kg-settings-root .ds-button {
  transition: background var(--kg-settings-transition-fast), border-color var(--kg-settings-transition-fast), box-shadow var(--kg-settings-transition-fast), color var(--kg-settings-transition-fast), opacity var(--kg-settings-transition-fast) !important;
  box-shadow: var(--kg-settings-shadow-control-idle) !important;
}

.kg-settings-root .ds-button:hover {
  box-shadow: var(--kg-settings-shadow-control-hover) !important;
}

.kg-settings-root .ds-button:active {
  box-shadow: var(--kg-settings-shadow-control-active) !important;
}

.kg-settings-root .ds-button:focus-visible {
  box-shadow: var(--kg-settings-shadow-control-hover), var(--kg-settings-focus-ring) !important;
}

.kg-settings-root .ds-button--intent-primary {
  background: var(--kg-settings-primary) !important;
  border-color: var(--kg-settings-primary) !important;
}

.kg-settings-root .ds-button--intent-primary .ds-button__label {
  color: var(--kg-settings-primary-text) !important;
}

.kg-settings-root .ds-button--intent-primary:hover {
  background: var(--kg-settings-primary-hover) !important;
  border-color: var(--kg-settings-primary-hover) !important;
}

.kg-settings-root .ds-button--intent-primary:active {
  background: var(--kg-settings-primary-active) !important;
  border-color: var(--kg-settings-primary-active) !important;
}

.kg-settings-root .ds-button--intent-secondary {
  background: var(--kg-settings-control-surface) !important;
  border-color: var(--kg-settings-control-border) !important;
}

.kg-settings-root .ds-button--intent-secondary .ds-button__label {
  color: var(--kg-settings-control-text) !important;
}

.kg-settings-root .ds-button--intent-secondary:hover {
  background: var(--kg-settings-surface-3) !important;
  border-color: var(--kg-settings-primary-hover) !important;
}

.kg-settings-root .ds-button--intent-danger {
  background: var(--kg-settings-danger) !important;
  border-color: var(--kg-settings-danger) !important;
}

.kg-settings-root .ds-button--intent-danger .ds-button__label {
  color: var(--kg-settings-danger-text) !important;
}

.kg-settings-root .ds-button--intent-danger:hover {
  background: var(--kg-settings-danger-hover) !important;
  border-color: var(--kg-settings-danger-hover) !important;
}

.kg-settings-root .ds-button--intent-danger:active {
  background: var(--kg-settings-danger-active) !important;
  border-color: var(--kg-settings-danger-active) !important;
}

.kg-settings-root .ds-button:disabled,
.kg-settings-root .ds-button[disabled],
.kg-settings-root .ds-button[aria-disabled="true"] {
  opacity: 0.48 !important;
  box-shadow: none !important;
  cursor: not-allowed !important;
}

.kg-settings-root .kg-settings-panel[data-panel="appearance"] > .kg-settings-panel-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: auto;
  align-items: stretch;
}

.kg-settings-root .kg-settings-panel[data-panel="appearance"] .kg-settings-pack-grid {
  width: 100%;
  max-width: none;
  margin: 0;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.kg-settings-root .kg-settings-panel[data-panel="appearance"] .kg-settings-pack-card {
  min-height: 188px;
  padding: 16px;
  gap: 12px;
  justify-content: space-between;
  border-color: var(--kg-settings-border) !important;
  background: var(--kg-settings-surface-2) !important;
}

.kg-settings-root .kg-settings-panel[data-panel="appearance"] .kg-settings-pack-card.active {
  border-color: var(--kg-settings-primary) !important;
  box-shadow: var(--kg-settings-shadow-card), var(--kg-settings-focus-ring) !important;
}

.kg-settings-root .kg-settings-panel[data-panel="appearance"] .kg-settings-pack-swatches span {
  border-color: var(--kg-settings-border) !important;
}

.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-scroll,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-details,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-table-wrap {
  background: var(--kg-settings-modal-surface) !important;
  border-color: var(--kg-settings-modal-border) !important;
  color: var(--kg-settings-modal-text) !important;
  box-shadow: none !important;
}

.kg-settings-root .ds-panel[data-ds-component="ds/panel"],
.kg-settings-root .ds-card[data-ds-component="ds/card"],
.kg-settings-root .ds-sub-panel[data-ds-component="ds/sub-panel"] {
  background: var(--kg-settings-surface, var(--kg-settings-surface-1)) !important;
  box-shadow: var(--kg-settings-shadow-1, var(--kg-settings-shadow-card)) !important;
}

.kg-settings-root .kg-settings-users-panel,
.kg-settings-root .kg-settings-users-table-host .kg-settings-table-panel,
.kg-settings-root .kg-settings-audit-panel,
.kg-settings-root .kg-settings-usage-panel,
.kg-settings-root .kg-settings-archive-panel,
.kg-settings-root .kg-settings-sessions-summary,
.kg-settings-root .kg-settings-box,
.kg-settings-root .kg-settings-pack-card,
.kg-settings-root .kg-settings-meta-item,
.kg-settings-root .kg-settings-archive-status-row,
.kg-settings-root .kg-settings-users-count-pill,
.kg-settings-root .kg-settings-archive-empty {
  box-shadow: var(--kg-settings-shadow-1, var(--kg-settings-shadow-card)) !important;
  transition:
    box-shadow var(--kg-settings-dur-med, 180ms) var(--kg-settings-ease-standard, ease),
    background-color var(--kg-settings-dur-med, 180ms) var(--kg-settings-ease-standard, ease),
    border-color var(--kg-settings-dur-med, 180ms) var(--kg-settings-ease-standard, ease) !important;
}

.kg-settings-root .kg-settings-users-panel:hover,
.kg-settings-root .kg-settings-pack-card:hover,
.kg-settings-root .kg-settings-archive-status-row:hover,
.kg-settings-root .kg-settings-users-count-pill:hover {
  box-shadow: var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)) !important;
}

.kg-settings-root .kg-settings-modal-backdrop .modal-card,
.kg-settings-root .kg-settings-modal-backdrop .modal-shell,
.kg-settings-root .kg-settings-modal-backdrop .ds-sub-panel,
.kg-settings-root .kg-settings-modal-backdrop .ds-panel,
.kg-settings-root .kg-settings-modal-backdrop .ds-card {
  box-shadow: var(--kg-settings-shadow-3, var(--kg-settings-shadow-card)) !important;
}

.kg-settings-root .kg-settings-meta-value,
.kg-settings-root .kg-settings-archive-kv > dd,
.kg-settings-root .kg-settings-archive-status-body,
.kg-settings-root .kg-settings-table__row > .ds-table-advanced__cell--body {
  color: var(--kg-settings-text-2, var(--kg-settings-text)) !important;
}

.kg-settings-root .kg-settings-muted,
.kg-settings-root .kg-settings-pack-description,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-note,
.kg-settings-root .kg-settings-modal-backdrop .kg-settings-structured-empty {
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle)) !important;
}

.kg-settings-root .kg-settings-ok {
  color: var(--kg-settings-success, var(--kg-settings-pill-positive-text)) !important;
}

.kg-settings-root .kg-settings-tabs .ds-tabs__list {
  box-shadow: var(--kg-settings-shadow-1, var(--kg-settings-shadow-card)) !important;
}

.kg-settings-root .kg-settings-tabs .ds-tabs__tab {
  transition:
    background-color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    border-color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    box-shadow var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease) !important;
}

html:not([data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-ui-surfaces~="settings"]) .kg-settings-root .kg-settings-tabs .ds-tabs__tab.is-active,
html:not([data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-ui-surfaces~="settings"]) .kg-settings-root .kg-settings-tabs .ds-tabs__tab[aria-selected="true"] {
  background: var(--kg-settings-accent-soft, var(--kg-settings-surface-1)) !important;
  color: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
}

.kg-settings-root .kg-settings-tabs .ds-tabs__tab:focus-visible {
  box-shadow:
    inset 0 0 0 1px var(--kg-settings-border),
    0 0 0 3px var(--kg-settings-focus, color-mix(in srgb, var(--kg-settings-accent, var(--kg-settings-primary)) 24%, transparent)) !important;
}

.kg-settings-root .kg-settings-input,
.kg-settings-root .kg-settings-select,
.kg-settings-root textarea,
.kg-settings-root .ds-input__control,
.kg-settings-root .ds-select__control,
.kg-settings-root .ds-date-time-picker__input,
.kg-settings-root .ds-search__input {
  transition:
    border-color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    box-shadow var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    background-color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease) !important;
}

.kg-settings-root .kg-settings-input:focus,
.kg-settings-root textarea:focus,
.kg-settings-root .ds-input__control:focus-within,
.kg-settings-root .ds-select__control:focus-within,
.kg-settings-root .ds-date-time-picker__input:focus,
.kg-settings-root .ds-search__input:focus {
  border-color: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
  box-shadow:
    var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)),
    0 0 0 3px var(--kg-settings-focus, color-mix(in srgb, var(--kg-settings-accent, var(--kg-settings-primary)) 24%, transparent)) !important;
}

.kg-settings-root .ds-button {
  transition:
    background-color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    border-color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    box-shadow var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    opacity var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease) !important;
  box-shadow: var(--kg-settings-shadow-1, var(--kg-settings-shadow-control-idle)) !important;
}

.kg-settings-root .ds-button:hover {
  box-shadow: var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)) !important;
}

.kg-settings-root .ds-button:active {
  box-shadow: var(--kg-settings-shadow-3, var(--kg-settings-shadow-control-active)) !important;
}

.kg-settings-root .ds-button:focus-visible {
  box-shadow:
    var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)),
    0 0 0 3px var(--kg-settings-focus, color-mix(in srgb, var(--kg-settings-accent, var(--kg-settings-primary)) 24%, transparent)) !important;
}

.kg-settings-root .ds-button--intent-primary {
  background: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
  border-color: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
}

.kg-settings-root .ds-button--intent-primary:hover {
  background: var(--kg-settings-primary-hover) !important;
  border-color: var(--kg-settings-primary-hover) !important;
}

.kg-settings-root .ds-button--intent-primary:active {
  background: var(--kg-settings-primary-active) !important;
  border-color: var(--kg-settings-primary-active) !important;
}

.kg-settings-root .ds-button--intent-secondary:hover {
  background: var(--kg-settings-accent-soft, var(--kg-settings-surface-3)) !important;
  border-color: var(--kg-settings-accent, var(--kg-settings-primary-hover)) !important;
}

.kg-settings-root .kg-settings-users-count-pill .value {
  color: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
}

.kg-settings-root .kg-settings-archive-status-row.is-warning {
  border-color: var(--kg-settings-warning, var(--kg-settings-pill-warning-border)) !important;
}

.kg-settings-root .kg-settings-archive-status-row.is-warning .kg-settings-archive-status-label {
  color: var(--kg-settings-warning, var(--kg-settings-pill-warning-text)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-panel[data-ds-component="ds/panel"],
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-card[data-ds-component="ds/card"],
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-sub-panel[data-ds-component="ds/sub-panel"] {
  background:
    linear-gradient(180deg, var(--kg-settings-highlight, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 70%, transparent)), transparent 18px),
    var(--kg-settings-surface, var(--kg-settings-surface-1)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-users-panel,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-users-table-host .kg-settings-table-panel,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-audit-panel,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-usage-panel,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-archive-panel,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-sessions-summary,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-box,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-pack-card,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-meta-item,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-archive-status-row,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-users-count-pill,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-archive-empty {
  background:
    linear-gradient(180deg, var(--kg-settings-highlight, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 70%, transparent)), transparent 18px),
    var(--kg-settings-surface-2) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-table__cell--header {
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 60%, transparent), transparent 14px),
    var(--kg-settings-table-header-bg) !important;
  color: var(--kg-settings-text-2, var(--kg-settings-table-header-text)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button {
  box-shadow: var(--kg-settings-shadow-1, var(--kg-settings-shadow-control-idle)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button:hover {
  filter: saturate(1.02) brightness(1.01);
  box-shadow: var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button:active {
  filter: none;
  box-shadow:
    inset 0 2px 5px color-mix(in srgb, var(--kg-settings-shadow-base, var(--kg-settings-text, CanvasText)) 8%, transparent),
    var(--kg-settings-shadow-1, var(--kg-settings-shadow-control-active)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-shell-panel > .ds-panel__body,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-shell-body,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-panel-body {
  background: transparent !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-users-panel,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-users-table-host .kg-settings-table-panel,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-audit-panel,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-usage-panel,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-archive-panel,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-box,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-pack-card,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-archive-empty {
  background:
    linear-gradient(180deg, var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 82%, transparent)), transparent 20px),
    var(--kg-settings-surface-1, var(--kg-settings-surface)) !important;
  box-shadow: var(--kg-settings-shadow-1, var(--kg-settings-shadow-card)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-users-count-pill,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-sessions-summary,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-meta-item,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-archive-status-row {
  background:
    linear-gradient(180deg, var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 82%, transparent)), transparent 18px),
    var(--kg-settings-surface-2, var(--kg-settings-surface-1)) !important;
  box-shadow: var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-table__cell--header {
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 66%, transparent), transparent 14px),
    linear-gradient(180deg, var(--kg-settings-surface-3), var(--kg-settings-accent-soft, var(--kg-settings-surface-3))) !important;
  border-bottom-color: color-mix(in srgb, var(--kg-settings-border, currentColor) 48%, transparent) !important;
  color: var(--kg-settings-text-2, var(--kg-settings-table-header-text)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-table__row:hover > .ds-table-advanced__cell--body {
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 34%, transparent), transparent 14px),
    var(--kg-settings-accent-soft, var(--kg-settings-surface-3)) !important;
  box-shadow:
    inset 0 1px 0 color-mix(in srgb, var(--kg-settings-contrast, Canvas) 55%, transparent),
    inset 0 -1px 0 color-mix(in srgb, var(--kg-settings-accent, var(--kg-settings-primary)) 4%, transparent) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-tabs .ds-tabs__list {
  background:
    linear-gradient(180deg, var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 82%, transparent)), transparent 18px),
    var(--kg-settings-surface-2, var(--kg-settings-surface-1)) !important;
  box-shadow: var(--kg-settings-shadow-1, var(--kg-settings-shadow-card)) !important;
}

html:not([data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-ui-surfaces~="settings"]) .kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-tabs .ds-tabs__tab.is-active,
html:not([data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-ui-surfaces~="settings"]) .kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-tabs .ds-tabs__tab[aria-selected="true"] {
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 58%, transparent), transparent 16px),
    var(--kg-settings-accent-soft, var(--kg-settings-surface-3)) !important;
  box-shadow:
    inset 0 1px 0 color-mix(in srgb, var(--kg-settings-contrast, Canvas) 62%, transparent),
    var(--kg-settings-shadow-1, var(--kg-settings-shadow-control-idle)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-search[data-ds-component="ds/search"],
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-date-time-picker[data-ds-component="ds/date-time-picker"],
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-select__control,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-input__control {
  background:
    linear-gradient(180deg, var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 82%, transparent)), transparent 16px),
    var(--kg-settings-control-surface) !important;
  border-color: color-mix(in srgb, var(--kg-settings-border, currentColor) 72%, transparent) !important;
  box-shadow:
    inset 0 1px 0 var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 82%, transparent)),
    var(--kg-settings-shadow-1, var(--kg-settings-shadow-control-idle)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-search[data-ds-component="ds/search"]:hover,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-date-time-picker[data-ds-component="ds/date-time-picker"]:hover,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-select__control:hover,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-input__control:hover {
  box-shadow:
    inset 0 1px 0 var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 82%, transparent)),
    var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-search[data-ds-component="ds/search"]:focus-within,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-date-time-picker[data-ds-component="ds/date-time-picker"]:focus-within,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-select__control:focus-within,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-input__control:focus-within {
  border-color: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
  box-shadow:
    inset 0 1px 0 var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-contrast, Canvas) 82%, transparent)),
    var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)),
    0 0 0 3px var(--kg-settings-focus, color-mix(in srgb, var(--kg-settings-accent, var(--kg-settings-primary)) 18%, transparent)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-search__icon,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-date-time-picker__icon,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-select__icon {
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle)) !important;
  opacity: 0.9;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-search[data-ds-component="ds/search"]:hover .ds-search__icon,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-search[data-ds-component="ds/search"]:focus-within .ds-search__icon,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-date-time-picker[data-ds-component="ds/date-time-picker"]:hover .ds-date-time-picker__icon,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-date-time-picker[data-ds-component="ds/date-time-picker"]:focus-within .ds-date-time-picker__icon,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-select__control:hover .ds-select__icon,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-select__control:focus-within .ds-select__icon {
  color: var(--kg-settings-text-2, var(--kg-settings-text)) !important;
  opacity: 1;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-date-time-picker[data-ds-component="ds/date-time-picker"],
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-date-time-picker__input,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-select__native {
  color-scheme: light;
  accent-color: var(--kg-settings-accent, var(--kg-settings-primary));
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-date-time-picker__input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.8;
  filter: saturate(0.8) brightness(0.94);
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-date-time-picker__input:hover::-webkit-calendar-picker-indicator,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-date-time-picker__input:focus::-webkit-calendar-picker-indicator {
  opacity: 1;
  filter: saturate(1) brightness(0.88);
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-select__native option,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-select__native optgroup,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] select option,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] select optgroup {
  background: var(--kg-settings-pretty-overlay-surface, var(--ds-surface-raised, Canvas));
  color: var(--kg-settings-text-2, var(--kg-settings-text));
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button {
  background-image: linear-gradient(180deg, var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-text, currentColor) 12%, transparent)), transparent 18px) !important;
  transition:
    box-shadow var(--kg-settings-dur-fast, 140ms) var(--kg-settings-ease-standard, ease),
    background-color var(--kg-settings-dur-fast, 140ms) var(--kg-settings-ease-standard, ease),
    border-color var(--kg-settings-dur-fast, 140ms) var(--kg-settings-ease-standard, ease),
    opacity var(--kg-settings-dur-fast, 140ms) var(--kg-settings-ease-standard, ease) !important;
  box-shadow:
    inset 0 1px 0 var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-text, currentColor) 12%, transparent)),
    var(--kg-settings-shadow-1, var(--kg-settings-shadow-control-idle)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button:hover {
  box-shadow:
    inset 0 1px 0 var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-text, currentColor) 12%, transparent)),
    var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button:active {
  box-shadow:
    inset 0 2px 5px var(--kg-settings-pretty-press-shadow, color-mix(in srgb, var(--kg-settings-text, currentColor) 8%, transparent)),
    inset 0 1px 0 var(--kg-settings-pretty-press-highlight, color-mix(in srgb, var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-text, currentColor) 12%, transparent)) 50%, transparent)),
    var(--kg-settings-shadow-1, var(--kg-settings-shadow-control-active)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button--intent-primary {
  background-image:
    linear-gradient(180deg, var(--kg-settings-pretty-button-sheen, color-mix(in srgb, var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-text, currentColor) 12%, transparent)) 32%, transparent)), transparent 18px),
    linear-gradient(180deg, var(--kg-settings-accent, var(--kg-settings-primary)), var(--kg-settings-primary-hover)) !important;
  border-color: var(--kg-settings-pretty-primary-border, color-mix(in srgb, var(--kg-settings-primary, currentColor) 16%, transparent)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button--intent-primary:hover {
  box-shadow:
    inset 0 1px 0 var(--kg-settings-pretty-primary-hover-highlight, color-mix(in srgb, var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-text, currentColor) 12%, transparent)) 40%, transparent)),
    var(--kg-settings-glow-soft, 0 0 0 transparent),
    var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)) !important;
  background-color: var(--kg-settings-primary-hover) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button--intent-primary:active {
  box-shadow:
    inset 0 2px 6px var(--kg-settings-pretty-primary-press-shadow, color-mix(in srgb, var(--kg-settings-text, currentColor) 14%, transparent)),
    inset 0 1px 0 var(--kg-settings-pretty-primary-press-highlight, color-mix(in srgb, var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-text, currentColor) 12%, transparent)) 22%, transparent)),
    var(--kg-settings-shadow-1, var(--kg-settings-shadow-control-active)) !important;
  background-color: var(--kg-settings-primary-active) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button--intent-secondary {
  background-image:
    linear-gradient(180deg, var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-text, currentColor) 12%, transparent)), transparent 18px),
    linear-gradient(180deg, var(--kg-settings-pretty-secondary-top, var(--kg-settings-pretty-overlay-surface, var(--ds-surface-base, Canvas))), var(--kg-settings-pretty-secondary-bottom, var(--kg-settings-pretty-overlay-surface-muted, var(--ds-surface-raised, Canvas)))) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button--intent-secondary:hover {
  background-image:
    linear-gradient(180deg, var(--kg-settings-pretty-secondary-hover-sheen, color-mix(in srgb, var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-text, currentColor) 12%, transparent)) 100%, transparent)), transparent 18px),
    linear-gradient(180deg, var(--kg-settings-accent-soft, var(--kg-settings-pretty-overlay-surface-muted, var(--ds-surface-subtle, var(--ds-surface-raised, Canvas)))), var(--kg-settings-pretty-secondary-top, var(--kg-settings-pretty-overlay-surface, var(--ds-surface-base, Canvas)))) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button--intent-danger {
  background-image:
    linear-gradient(180deg, var(--kg-settings-pretty-button-sheen, color-mix(in srgb, var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-text, currentColor) 12%, transparent)) 32%, transparent)), transparent 18px),
    linear-gradient(180deg, var(--kg-settings-danger), var(--kg-settings-danger-hover)) !important;
  border-color: var(--kg-settings-pretty-danger-border, color-mix(in srgb, var(--kg-settings-danger, currentColor) 18%, transparent)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button:focus-visible {
  box-shadow:
    inset 0 1px 0 var(--kg-settings-inner-highlight, color-mix(in srgb, var(--kg-settings-text, currentColor) 12%, transparent)),
    var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)),
    0 0 0 3px var(--kg-settings-focus, var(--ds-focus-ring, currentColor)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button:disabled,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button[disabled],
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button[aria-disabled="true"] {
  box-shadow: none !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-notice .ds-alert,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-toast,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-behavior-panel,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-alert,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-toast,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-behavior-panel {
  background-color: var(--kg-settings-overlay-surface, var(--kg-settings-pretty-overlay-surface, var(--ds-surface-raised, Canvas))) !important;
  background-image: none !important;
  border-color: var(--kg-settings-overlay-border, var(--kg-settings-border)) !important;
  box-shadow: var(--kg-settings-overlay-shadow, var(--kg-settings-shadow-3)) !important;
  color: var(--kg-settings-overlay-text, var(--kg-settings-text)) !important;
  opacity: 1 !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-notice .ds-alert__label,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-notice .ds-alert__body,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .kg-settings-notice .ds-alert__action,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-toast__label,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-toast__body,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-toast__action,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-behavior-option__label,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-alert__label,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-alert__body,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-alert__action,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-toast__label,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-toast__body,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-toast__action,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-behavior-option__label {
  color: var(--kg-settings-overlay-text, var(--kg-settings-text)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-behavior-option__meta,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-behavior-empty,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-behavior-option__meta,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-behavior-empty {
  color: var(--kg-settings-overlay-text-2, var(--kg-settings-text-subtle)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-behavior-option:hover,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-behavior-option--active,
.kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-behavior-option--selected,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-behavior-option:hover,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-behavior-option--active,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-behavior-option--selected {
  background: var(--kg-settings-accent-soft, var(--kg-settings-pretty-overlay-surface-muted, var(--ds-surface-subtle, var(--ds-surface-raised, Canvas)))) !important;
}

${SETTINGS_PRETTY_OVERLAY_PRESET_STYLE_TEXT}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-frame-host > .ds-modal,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .modal-card,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .modal-shell,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-panel,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-card,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-sub-panel {
  background-color: var(--kg-settings-pretty-overlay-surface) !important;
  background-image: none !important;
  border-color: var(--kg-settings-pretty-overlay-border) !important;
  box-shadow: var(--kg-settings-pretty-overlay-shadow) !important;
  color: var(--kg-settings-pretty-overlay-text) !important;
  filter: none !important;
  opacity: 1 !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-frame-host > .ds-modal .ds-modal__body,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-frame-host > .ds-modal .ds-modal__footer,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-alert__body,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-alert__footer,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-toast__body,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-toast__footer {
  background: transparent !important;
  color: inherit !important;
  filter: none !important;
  opacity: 1 !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-frame-host > .ds-modal .ds-modal__title,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-frame-host > .ds-modal .ds-modal__body,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-frame-host > .ds-modal .ds-modal__footer,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .modal-title,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-value,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-table td,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-pill {
  color: var(--kg-settings-pretty-overlay-text) !important;
  opacity: 1 !important;
  filter: none !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-frame-host > .ds-modal .ds-modal__description,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .modal-message,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-key,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-note,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-empty,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-table th,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-summary {
  color: var(--kg-settings-pretty-overlay-text-subtle) !important;
  opacity: 1 !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-scroll,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-details,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-table-wrap {
  background-color: var(--kg-settings-pretty-overlay-surface-muted) !important;
  background-image: none !important;
  border-color: var(--kg-settings-pretty-overlay-structured-border, color-mix(in srgb, var(--kg-settings-pretty-overlay-border) 86%, transparent)) !important;
  box-shadow: inset 0 1px 0 var(--kg-settings-pretty-overlay-structured-highlight, color-mix(in srgb, var(--kg-settings-pretty-overlay-text) 8%, transparent)) !important;
  color: var(--kg-settings-pretty-overlay-text) !important;
  filter: none !important;
  opacity: 1 !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-row,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-table th,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-table td,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-block {
  border-color: var(--kg-settings-pretty-overlay-structured-divider, color-mix(in srgb, var(--kg-settings-pretty-overlay-border) 86%, transparent)) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .kg-settings-structured-pill {
  background: var(--kg-settings-pretty-overlay-structured-pill-bg, color-mix(in srgb, var(--kg-settings-pretty-overlay-primary) 8%, var(--kg-settings-pretty-overlay-surface) 92%)) !important;
  border-color: var(--kg-settings-pretty-overlay-structured-pill-border, color-mix(in srgb, var(--kg-settings-pretty-overlay-primary) 12%, transparent)) !important;
  color: var(--kg-settings-pretty-overlay-text) !important;
  opacity: 1 !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-button,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop input:not([type="checkbox"]):not([type="radio"]),
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop select,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop textarea {
  background: var(--kg-settings-pretty-overlay-control) !important;
  background-image: none !important;
  border-color: var(--kg-settings-pretty-overlay-border-strong) !important;
  color: var(--kg-settings-pretty-overlay-text) !important;
  transition:
    box-shadow 140ms cubic-bezier(0.2, 0.8, 0.2, 1),
    background-color 140ms cubic-bezier(0.2, 0.8, 0.2, 1),
    border-color 140ms cubic-bezier(0.2, 0.8, 0.2, 1),
    opacity 140ms cubic-bezier(0.2, 0.8, 0.2, 1) !important;
  box-shadow:
    inset 0 1px 0 var(--kg-settings-pretty-overlay-highlight),
    var(--kg-settings-pretty-overlay-shadow-rest) !important;
  filter: none !important;
  opacity: 1 !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-button:hover {
  box-shadow:
    inset 0 1px 0 var(--kg-settings-pretty-overlay-highlight),
    var(--kg-settings-pretty-overlay-shadow-hover) !important;
  filter: none !important;
  opacity: 1 !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-button:active {
  box-shadow:
    inset 0 2px 5px var(--kg-settings-pretty-overlay-press-shadow, color-mix(in srgb, var(--kg-settings-pretty-overlay-text) 8%, transparent)),
    inset 0 1px 0 var(--kg-settings-pretty-overlay-press-highlight, color-mix(in srgb, var(--kg-settings-pretty-overlay-highlight) 48%, transparent)),
    var(--kg-settings-pretty-overlay-shadow-press) !important;
  filter: none !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-button--intent-primary {
  background-color: var(--kg-settings-pretty-overlay-primary) !important;
  background-image:
    linear-gradient(180deg, var(--kg-settings-pretty-overlay-button-sheen, color-mix(in srgb, var(--kg-settings-pretty-overlay-highlight) 25%, transparent)), transparent 18px) !important;
  border-color: var(--kg-settings-pretty-overlay-primary-border, color-mix(in srgb, var(--kg-settings-pretty-overlay-primary) 16%, transparent)) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-button--intent-primary:hover {
  box-shadow:
    inset 0 1px 0 var(--kg-settings-pretty-overlay-hover-highlight, color-mix(in srgb, var(--kg-settings-pretty-overlay-highlight) 36%, transparent)),
    var(--kg-settings-pretty-overlay-shadow-hover) !important;
  background-color: var(--kg-settings-pretty-overlay-primary-hover) !important;
  opacity: 1 !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-button--intent-primary:active {
  box-shadow:
    inset 0 2px 6px var(--kg-settings-pretty-overlay-primary-press-shadow, color-mix(in srgb, var(--kg-settings-pretty-overlay-text) 14%, transparent)),
    inset 0 1px 0 var(--kg-settings-pretty-overlay-primary-press-highlight, color-mix(in srgb, var(--kg-settings-pretty-overlay-highlight) 20%, transparent)),
    var(--kg-settings-pretty-overlay-shadow-press) !important;
  background-color: var(--kg-settings-pretty-overlay-primary-active) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-button--intent-secondary {
  background-color: var(--kg-settings-pretty-overlay-secondary-bg, var(--kg-settings-pretty-overlay-surface)) !important;
  background-image: none !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-button--intent-danger {
  background-color: var(--kg-settings-pretty-overlay-danger) !important;
  background-image:
    linear-gradient(180deg, var(--kg-settings-pretty-overlay-button-sheen, color-mix(in srgb, var(--kg-settings-pretty-overlay-highlight) 25%, transparent)), transparent 18px) !important;
  border-color: var(--kg-settings-pretty-overlay-danger-border, color-mix(in srgb, var(--kg-settings-pretty-overlay-danger) 18%, transparent)) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-button__label {
  color: var(--kg-settings-pretty-overlay-text) !important;
  opacity: 1 !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-button--intent-primary .ds-button__label {
  color: var(--kg-settings-pretty-overlay-primary-text) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-button--intent-danger .ds-button__label {
  color: var(--kg-settings-pretty-overlay-danger-text) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop input:not([type="checkbox"]):not([type="radio"]):focus,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop select:focus,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop textarea:focus,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop .ds-button:focus-visible {
  box-shadow:
    inset 0 1px 0 var(--kg-settings-pretty-overlay-highlight),
    var(--kg-settings-pretty-overlay-shadow-hover),
    0 0 0 3px var(--kg-settings-pretty-overlay-focus) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop input::placeholder,
body:has(.kg-settings-root[data-kg-settings-theme-mode="pretty"]) .kg-settings-modal-backdrop textarea::placeholder {
  color: color-mix(in srgb, var(--kg-settings-pretty-overlay-text-subtle) 78%, transparent) !important;
  opacity: 1 !important;
}

${SETTINGS_DARK_OVERLAY_PRESET_STYLE_TEXT}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-search[data-ds-component="ds/search"],
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker[data-ds-component="ds/date-time-picker"],
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-select__control,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-input__control {
  background:
    linear-gradient(
      180deg,
      color-mix(in srgb, var(--kg-settings-control-surface, Canvas) 96%, var(--kg-settings-shadow-base, CanvasText)),
      color-mix(in srgb, var(--kg-settings-control-surface, Canvas) 98%, var(--kg-settings-shadow-base, CanvasText))
    ) !important;
  border-color: color-mix(in srgb, var(--kg-settings-border, currentColor) 64%, transparent) !important;
  box-shadow:
    inset 0 1px 0 color-mix(in srgb, var(--kg-settings-contrast, Canvas) 4%, transparent),
    0 1px 2px color-mix(in srgb, var(--kg-settings-shadow-base, var(--kg-settings-text, CanvasText)) 30%, transparent),
    var(--kg-settings-shadow-control-idle) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-search[data-ds-component="ds/search"]:hover,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker[data-ds-component="ds/date-time-picker"]:hover,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-select__control:hover,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-input__control:hover {
  border-color: color-mix(in srgb, var(--kg-settings-border, currentColor) 76%, transparent) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-search[data-ds-component="ds/search"]:focus-within,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker[data-ds-component="ds/date-time-picker"]:focus-within,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-select__control:focus-within,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-input__control:focus-within {
  border-color: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
  box-shadow:
    inset 0 1px 0 color-mix(in srgb, var(--kg-settings-contrast, Canvas) 5%, transparent),
    var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)),
    0 0 0 3px var(--kg-settings-focus, color-mix(in srgb, var(--kg-settings-accent, var(--kg-settings-primary)) 32%, transparent)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-search__icon,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__icon,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-select__icon {
  color: var(--kg-settings-muted-tone, var(--kg-settings-text-subtle)) !important;
  opacity: 0.9;
  transition:
    color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    opacity var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    filter var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-search[data-ds-component="ds/search"]:hover .ds-search__icon,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-search[data-ds-component="ds/search"]:focus-within .ds-search__icon,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker[data-ds-component="ds/date-time-picker"]:hover .ds-date-time-picker__icon,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker[data-ds-component="ds/date-time-picker"]:focus-within .ds-date-time-picker__icon,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-select__control:hover .ds-select__icon,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-select__control:focus-within .ds-select__icon {
  color: var(--kg-settings-text-2, var(--kg-settings-text)) !important;
  opacity: 1;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-search__input,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-select__native,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-input__value,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .kg-settings-input,
.kg-settings-root[data-kg-settings-theme-mode="dark"] textarea {
  color: var(--kg-settings-control-text) !important;
  caret-color: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-search__input::placeholder,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::placeholder,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-input__value::placeholder,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .kg-settings-input::placeholder,
.kg-settings-root[data-kg-settings-theme-mode="dark"] textarea::placeholder {
  color: color-mix(in srgb, var(--kg-settings-control-placeholder) 82%, var(--kg-settings-contrast, Canvas) 18%) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker[data-ds-component="ds/date-time-picker"],
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-select__native {
  color-scheme: dark;
  accent-color: var(--kg-settings-accent, var(--kg-settings-primary));
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input {
  padding-right: 6px !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.9;
  filter: invert(0.96) saturate(0.3) brightness(1.06);
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input:hover::-webkit-calendar-picker-indicator,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input:focus::-webkit-calendar-picker-indicator {
  opacity: 1;
  filter: invert(1) saturate(0.45) brightness(1.12);
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::-webkit-datetime-edit,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::-webkit-datetime-edit-fields-wrapper,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::-webkit-datetime-edit-year-field,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::-webkit-datetime-edit-month-field,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::-webkit-datetime-edit-day-field,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::-webkit-datetime-edit-hour-field,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::-webkit-datetime-edit-minute-field,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::-webkit-datetime-edit-ampm-field {
  color: var(--kg-settings-control-text) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::-webkit-datetime-edit-text {
  color: var(--kg-settings-control-placeholder) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::-webkit-inner-spin-button,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__input::-webkit-clear-button {
  opacity: 0.78;
  filter: invert(0.92);
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-select__native option,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-select__native optgroup,
.kg-settings-root[data-kg-settings-theme-mode="dark"] select option,
.kg-settings-root[data-kg-settings-theme-mode="dark"] select optgroup {
  background: var(--kg-settings-control-surface, Canvas);
  color: var(--kg-settings-control-text);
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .kg-settings-notice .ds-alert,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-toast,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-alert,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-toast {
  background: var(--kg-settings-overlay-surface) !important;
  border-color: var(--kg-settings-overlay-border) !important;
  box-shadow: var(--kg-settings-overlay-alert-shadow, var(--ds-shadow-overlay, none)) !important;
  color: var(--kg-settings-overlay-text) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .kg-settings-notice .ds-alert__label,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .kg-settings-notice .ds-alert__body,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .kg-settings-notice .ds-alert__action,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-toast__label,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-toast__body,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-toast__action,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-alert__label,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-alert__body,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-alert__action,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-toast__label,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-toast__body,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-toast__action {
  color: var(--kg-settings-overlay-text) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .kg-settings-notice .ds-alert--intent-success,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-toast--intent-success,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-alert--intent-success,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-toast--intent-success {
  border-color: var(--kg-settings-overlay-success-border, color-mix(in srgb, var(--ds-intent-success-border, currentColor) 24%, transparent)) !important;
  box-shadow: inset 0 1px 0 var(--kg-settings-overlay-success-highlight, color-mix(in srgb, var(--ds-intent-success-border, currentColor) 8%, transparent)), var(--kg-settings-overlay-alert-shadow, var(--ds-shadow-overlay, none)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .kg-settings-notice .ds-alert--intent-warning,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-toast--intent-warning,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-alert--intent-warning,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-toast--intent-warning {
  border-color: var(--kg-settings-overlay-warning-border, color-mix(in srgb, var(--ds-intent-warning-border, currentColor) 24%, transparent)) !important;
  box-shadow: inset 0 1px 0 var(--kg-settings-overlay-warning-highlight, color-mix(in srgb, var(--ds-intent-warning-border, currentColor) 8%, transparent)), var(--kg-settings-overlay-alert-shadow, var(--ds-shadow-overlay, none)) !important;
}

.kg-settings-root[data-kg-settings-theme-mode="dark"] .kg-settings-notice .ds-alert--intent-error,
.kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-toast--intent-error,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-alert--intent-error,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-toast--intent-error {
  border-color: var(--kg-settings-overlay-danger-border, color-mix(in srgb, var(--ds-intent-danger-border, currentColor) 24%, transparent)) !important;
  box-shadow: inset 0 1px 0 var(--kg-settings-overlay-danger-highlight, color-mix(in srgb, var(--ds-intent-danger-border, currentColor) 8%, transparent)), var(--kg-settings-overlay-alert-shadow, var(--ds-shadow-overlay, none)) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop {
  background: var(--kg-settings-overlay-scrim) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-frame-host > .ds-modal,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .modal-card,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .modal-shell,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-panel,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-card,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-sub-panel {
  background: var(--kg-settings-overlay-surface) !important;
  border-color: var(--kg-settings-overlay-border) !important;
  box-shadow: var(--kg-settings-overlay-shadow) !important;
  color: var(--kg-settings-overlay-text) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-frame-host > .ds-modal .ds-modal__title,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-frame-host > .ds-modal .ds-modal__body,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-frame-host > .ds-modal .ds-modal__footer,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .modal-title,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-value,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-table td,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-pill {
  color: var(--kg-settings-overlay-text) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-frame-host > .ds-modal .ds-modal__description,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .modal-message,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-key,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-note,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-empty,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-table th,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-summary {
  color: var(--kg-settings-overlay-text-2) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-scroll,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-details,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-table-wrap {
  background: var(--kg-settings-overlay-structured-surface, color-mix(in srgb, var(--kg-settings-overlay-surface) 86%, transparent)) !important;
  border-color: var(--kg-settings-overlay-structured-border, color-mix(in srgb, var(--kg-settings-overlay-border) 78%, transparent)) !important;
  box-shadow: inset 0 1px 0 var(--kg-settings-overlay-structured-highlight, color-mix(in srgb, var(--kg-settings-overlay-text) 3%, transparent)) !important;
  color: var(--kg-settings-overlay-text) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-row,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-table th,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-table td,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-block {
  border-color: var(--kg-settings-overlay-structured-divider, color-mix(in srgb, var(--kg-settings-overlay-border) 72%, transparent)) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .kg-settings-structured-pill {
  background: var(--kg-settings-overlay-structured-pill-bg, color-mix(in srgb, var(--kg-settings-overlay-surface) 82%, transparent)) !important;
  border-color: var(--kg-settings-overlay-structured-pill-border, var(--kg-settings-overlay-border)) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-button,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop input:not([type="checkbox"]):not([type="radio"]),
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop select,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop textarea {
  transition:
    border-color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    box-shadow var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    background-color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    color var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease),
    opacity var(--kg-settings-dur-fast, 120ms) var(--kg-settings-ease-standard, ease) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop input:not([type="checkbox"]):not([type="radio"]),
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop select,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop textarea {
  background: var(--kg-settings-overlay-control-surface, linear-gradient(180deg, color-mix(in srgb, var(--kg-settings-overlay-surface) 96%, transparent), color-mix(in srgb, var(--kg-settings-overlay-surface) 98%, var(--kg-settings-overlay-border)))) !important;
  border-color: var(--kg-settings-overlay-control-border, color-mix(in srgb, var(--kg-settings-overlay-border) 84%, transparent)) !important;
  box-shadow:
    inset 0 1px 0 var(--kg-settings-overlay-control-highlight, color-mix(in srgb, var(--kg-settings-overlay-text) 4%, transparent)),
    0 1px 2px var(--kg-settings-overlay-control-shadow-soft, color-mix(in srgb, var(--kg-settings-overlay-scrim) 36%, transparent)),
    var(--kg-settings-shadow-control-idle) !important;
  color: var(--kg-settings-overlay-text) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop input:not([type="checkbox"]):not([type="radio"]):focus,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop select:focus,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop textarea:focus {
  border-color: var(--kg-settings-accent, var(--kg-settings-primary)) !important;
  box-shadow:
    inset 0 1px 0 var(--kg-settings-overlay-control-focus-highlight, color-mix(in srgb, var(--kg-settings-overlay-text) 5%, transparent)),
    var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)),
    0 0 0 3px var(--kg-settings-overlay-focus) !important;
  outline: none;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop input::placeholder,
body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop textarea::placeholder {
  color: color-mix(in srgb, var(--kg-settings-overlay-text-2) 72%, transparent) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-button {
  box-shadow: var(--kg-settings-shadow-1, var(--kg-settings-shadow-control-idle)) !important;
}

body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-button:focus-visible {
  box-shadow: var(--kg-settings-shadow-2, var(--kg-settings-shadow-control-hover)), 0 0 0 3px var(--kg-settings-overlay-focus) !important;
}

@media (max-width: 1280px) {
  .kg-settings-appearance-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .kg-settings-appearance-side {
    order: 2;
  }

  .kg-settings-pack-grid--appearance,
  .kg-settings-root .kg-settings-panel[data-panel="appearance"] .kg-settings-pack-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .kg-settings-appearance-grid,
  .kg-settings-appearance-actions,
  .kg-settings-appearance-meta-grid,
  .kg-settings-pack-grid--appearance,
  .kg-settings-root .kg-settings-panel[data-panel="appearance"] .kg-settings-pack-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (prefers-reduced-motion: reduce) {
  .kg-settings-root .ds-button,
  .kg-settings-root .kg-settings-tabs .ds-tabs__tab,
  .kg-settings-root .kg-settings-users-panel,
  .kg-settings-root .kg-settings-pack-card,
  .kg-settings-root .kg-settings-archive-status-row,
  .kg-settings-root .kg-settings-users-count-pill,
  .kg-settings-root .kg-settings-input,
  .kg-settings-root .kg-settings-select,
  .kg-settings-root textarea,
  .kg-settings-root .ds-input__control,
  .kg-settings-root .ds-select__control,
  .kg-settings-root .ds-date-time-picker__input,
  .kg-settings-root .ds-search__input,
  .kg-settings-root .kg-settings-table__row > .ds-table-advanced__cell--body,
  .kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-search__icon,
  .kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-date-time-picker__icon,
  .kg-settings-root[data-kg-settings-theme-mode="dark"] .ds-select__icon,
  body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop .ds-button,
  body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop input:not([type="checkbox"]):not([type="radio"]),
  body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop select,
  body:has(.kg-settings-root[data-kg-settings-theme-mode="dark"]) .kg-settings-modal-backdrop textarea {
    transition: none !important;
  }

  .kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button:hover,
  .kg-settings-root[data-kg-settings-theme-mode="pretty"] .ds-button:active {
    filter: none !important;
  }
}
`].join('\n');
  const legacyPriorityPattern = /\s*!important\b/giu;
  style.textContent = finalUi
    ? `${settingsStyleText.replace(legacyPriorityPattern, '')}\n${TUTOR_DESK_SAAS_FINAL_UI_SETTINGS_STYLES}${referenceV2 ? `\n${TUTOR_DESK_SETTINGS_REFERENCE_V2_STYLES}` : ''}`
    : settingsStyleText;
  style.dataset.kgSettingsPresentation = presentationId;
  if (shouldAppendStyle) document.head.appendChild(style);
}

function escapeHtml(value) {
  return String(value || '').replace(/[<>&"']/g, (m) => ({
    '<': '&lt;',
    '>': '&gt;',
    '&': '&amp;',
    '"': '&quot;',
    "'": '&#39;'
  }[m]));
}

function renderSettingsFinalUiActionAttributes(attrs = {}, label = '', {
  uiRole = 'action',
  includeActionGeometry = true
} = {}) {
  if (!isSettingsSaasFinalUiActive()) return '';
  const normalized = getSettingsFinalUiActionAttributes({ attrs, label });
  normalized['data-kg-ui-role'] = String(uiRole || 'action');
  if (!includeActionGeometry) {
    delete normalized['data-td-final-action-role'];
    delete normalized['data-td-final-action-size'];
    delete normalized['data-td-final-destructive-confirmation'];
  }
  return Object.entries(normalized)
    .filter(([key, value]) => key !== 'data-action' && value != null && value !== false)
    .map(([key, value]) => value === true
      ? ` ${escapeHtml(key)}`
      : ` ${escapeHtml(key)}="${escapeHtml(value)}"`)
    .join('');
}

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

const STRUCTURED_INSPECTOR_STYLE_ID = 'kgSettingsStructuredInspectorStyles';
const STRUCTURED_INSPECTOR_STYLE_TEXT = SETTINGS_STRUCTURED_INSPECTOR_STYLE_TEXT;

function ensureStructuredInspectorStyles() {
  if (typeof document !== 'undefined') {
    const existing = document.getElementById(STRUCTURED_INSPECTOR_STYLE_ID);
    if (existing) return '';
    try {
      const styleEl = document.createElement('style');
      styleEl.id = STRUCTURED_INSPECTOR_STYLE_ID;
      styleEl.textContent = STRUCTURED_INSPECTOR_STYLE_TEXT;
      const target = document.head || document.body || document.documentElement;
      if (target) {
        target.appendChild(styleEl);
        return '';
      }
    } catch {}
  }
  return `<style id="${STRUCTURED_INSPECTOR_STYLE_ID}">${STRUCTURED_INSPECTOR_STYLE_TEXT}</style>`;
}

function renderStructured(value, opts = {}) {
  const maxDepth = Number.isFinite(opts.maxDepth) ? opts.maxDepth : 6;
  const maxItems = Number.isFinite(opts.maxItems) ? opts.maxItems : 200;
  const seen = new WeakSet();
  let itemCount = 0;

  function note(message) {
    return renderDsMessage({
      component: 'ds/settings-structured-note',
      className: 'kg-settings-structured-note',
      text: message
    });
  }

  function takeItem() {
    itemCount += 1;
    if (itemCount > maxItems) {
      return false;
    }
    return true;
  }

  function renderPrimitive(current) {
    if (current === null) {
      return '<span class="kg-settings-structured-empty">—</span>';
    }
    if (typeof current === 'boolean') {
      return `<span class="kg-settings-structured-pill">${current ? 'true' : 'false'}</span>`;
    }
    if (typeof current === 'number' || typeof current === 'bigint') {
      return `<span class="kg-settings-structured-pill">${escapeHtml(String(current))}</span>`;
    }
    if (typeof current === 'string') {
      return current
        ? `<span class="kg-settings-structured-pill">${escapeHtml(current)}</span>`
        : '<span class="kg-settings-structured-empty">""</span>';
    }
    if (typeof current === 'undefined') {
      return '<span class="kg-settings-structured-empty">—</span>';
    }
    return `<span class="kg-settings-structured-note">${escapeHtml(`(${typeof current})`)}</span>`;
  }

  function renderArrayAsTable(rows, depth) {
    const keyFrequency = new Map();
    for (const row of rows) {
      Object.keys(row).forEach((key) => {
        keyFrequency.set(key, (keyFrequency.get(key) || 0) + 1);
      });
    }
    const keys = Array.from(keyFrequency.entries())
      .filter(([, count]) => count >= 2)
      .sort((a, b) => {
        if (b[1] !== a[1]) {
          return b[1] - a[1];
        }
        return a[0].localeCompare(b[0]);
      })
      .map(([key]) => key);
    if (!keys.length) {
      return '';
    }
    const header = keys.map((key) => renderDsTableCell({
      className: 'kg-settings-structured-table__header',
      role: 'columnheader',
      html: escapeHtml(key)
    })).join('');
    let taken = 0;
    const body = rows.slice(0, Math.max(0, maxItems - itemCount)).map((row) => {
      if (!takeItem()) {
        return '';
      }
      taken += 1;
      const cells = keys.map((key) => renderDsTableCell({
        className: 'kg-settings-structured-table__cell',
        role: 'cell',
        html: renderValue(row[key], depth + 1, key)
      })).join('');
      return renderDsTableRowMarkup({
        className: 'kg-settings-structured-table__row',
        cellsHtml: cells
      });
    }).join('');
    const hiddenCount = Math.max(0, rows.length - taken);
    const overflow = hiddenCount ? note(`+${hiddenCount} more...`) : '';
    return renderDsStack({
      component: 'ds/settings-structured-table-wrap',
      className: 'kg-settings-structured-table-wrap',
      html: [
        renderDsStack({
          component: 'ds/settings-structured-table',
          className: 'kg-settings-structured-table',
          html: [
            renderDsStack({
              component: 'ds/settings-structured-table-head',
              className: 'kg-settings-structured-table__head',
              html: header
            }),
            renderDsStack({
              component: 'ds/settings-structured-table-body',
              className: 'kg-settings-structured-table__body',
              html: body
            })
          ].join('')
        }),
        overflow
      ].join('')
    });
  }

  function renderArray(current, depth, label) {
    if (depth >= maxDepth) {
      return note(`Max depth reached${label ? ` for ${label}` : ''}.`);
    }
    if (!current.length) {
      return renderDsMessage({
        component: 'ds/settings-structured-empty',
        className: 'kg-settings-structured-empty',
        text: 'No items.'
      });
    }
    const strings = current.filter((item) => typeof item === 'string');
    if (strings.length === current.length) {
      let taken = 0;
      const rows = current.slice(0, Math.max(0, maxItems - itemCount)).map((item) => {
        if (!takeItem()) {
          return '';
        }
        taken += 1;
        return renderDsTableRowMarkup({
          className: 'kg-settings-structured-table__row',
          cellsHtml: renderDsTableCell({
            className: 'kg-settings-structured-table__cell',
            role: 'cell',
            html: renderPrimitive(item)
          })
        });
      }).join('');
      const hiddenCount = Math.max(0, current.length - taken);
      const overflow = hiddenCount ? note(`+${hiddenCount} more...`) : '';
      return renderDsStack({
        component: 'ds/settings-structured-table-wrap',
        className: 'kg-settings-structured-table-wrap',
        html: [
          renderDsStack({
            component: 'ds/settings-structured-table',
            className: 'kg-settings-structured-table',
            html: [
              renderDsStack({
                component: 'ds/settings-structured-table-head',
                className: 'kg-settings-structured-table__head',
                html: renderDsTableCell({
                  className: 'kg-settings-structured-table__header',
                  role: 'columnheader',
                  html: 'Value'
                })
              }),
              renderDsStack({
                component: 'ds/settings-structured-table-body',
                className: 'kg-settings-structured-table__body',
                html: rows
              })
            ].join('')
          }),
          overflow
        ].join('')
      });
    }
    const objects = current.filter((item) => isPlainObject(item));
    if (objects.length === current.length && current.length > 1) {
      const table = renderArrayAsTable(objects, depth);
      if (table) {
        return table;
      }
    }
    const items = [];
    for (let index = 0; index < current.length; index += 1) {
      if (!takeItem()) {
        items.push(`[#li>${note(`Showing first ${maxItems} items.`)}</li>`);
        break;
      }
      items.push(renderDsInlineGroup({
        component: 'ds/settings-structured-list-item',
        className: 'kg-settings-structured-list-item',
        html: [
          `<span class="kg-settings-structured-pill">#${index + 1}</span>`,
          renderDsStack({
            component: 'ds/settings-structured-value',
            className: 'kg-settings-structured-value',
            html: renderValue(current[index], depth + 1, String(index))
          })
        ].join('')
      }));
    }
    return renderDsStack({
      tag: 'ol',
      component: 'ds/settings-structured-list',
      className: 'kg-settings-structured-list',
      html: items.join('')
    });
  }

  function renderObject(current, depth) {
    if (depth >= maxDepth) {
      return note('Max depth reached.');
    }
    const keys = Object.keys(current);
    if (!keys.length) {
      return renderDsMessage({
        component: 'ds/settings-structured-empty',
        className: 'kg-settings-structured-empty',
        text: 'No fields.'
      });
    }
    const rows = [];
    for (const key of keys) {
      if (!takeItem()) {
        rows.push(note(`Showing first ${maxItems} items.`));
        break;
      }
      const next = current[key];
      const nested = Array.isArray(next) || isPlainObject(next);
      const valueHtml = nested
        ? [
          '<details class="kg-settings-structured-details" open>',
          `<summary class="kg-settings-structured-summary">${escapeHtml(Array.isArray(next) ? `${key} (${next.length})` : key)}</summary>`,
          renderDsStack({
            component: 'ds/settings-structured-block',
            className: 'kg-settings-structured-block',
            html: renderValue(next, depth + 1, key)
          }),
          '</details>'
        ].join('')
        : renderPrimitive(next);
      rows.push(renderDsInlineGroup({
        component: 'ds/settings-structured-row',
        className: `kg-settings-structured-row${nested ? ' kg-settings-structured-row--nested' : ''}`,
        html: [
          renderDsMessage({
            component: 'ds/settings-structured-key',
            className: 'kg-settings-structured-key',
            text: key
          }),
          renderDsStack({
            component: 'ds/settings-structured-value',
            className: 'kg-settings-structured-value',
            html: valueHtml
          })
        ].join('')
      }));
    }
    return rows.join('');
  }

  function renderValue(current, depth, label) {
    if (current && typeof current === 'object') {
      if (seen.has(current)) {
        return note(`Circular reference${label ? ` in ${label}` : ''}.`);
      }
      seen.add(current);
      if (Array.isArray(current)) {
        return renderArray(current, depth, label);
      }
      if (isPlainObject(current)) {
        return renderObject(current, depth);
      }
      return renderPrimitive(String(current));
    }
    return renderPrimitive(current);
  }

  return `${ensureStructuredInspectorStyles()}${renderDsStack({
    component: 'ds/settings-structured-root',
    className: 'kg-settings-structured-root',
    html: renderValue(value, 0, opts.label || '')
  })}`;
}

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function hasAccessKey(access, key, fallback = true) {
  const wanted = String(key || '').trim();
  if (!wanted) return Boolean(fallback);
  const list = Array.isArray(access && access.list) ? access.list : [];
  if (!list.length) return Boolean(fallback);
  if (list.includes('*') || list.includes(wanted)) return true;
  const parts = wanted.split('.');
  while (parts.length > 1) {
    parts.pop();
    if (list.includes(`${parts.join('.')}.*`)) return true;
  }
  return false;
}

async function fetchJson(url, { signal } = {}) {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
    signal
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = payload && payload.error ? payload.error : `HTTP ${response.status}`;
    throw new Error(msg);
  }
  return payload;
}

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value || '');
  return d.toLocaleString();
}

function renderEmpty(message = 'No data.') {
  return renderSettingsNotice({
    kind: 'info',
    className: 'kg-settings-notice-compact',
    message
  });
}

  function renderTable({ id = '', headers = [], bodyHtml = '', minWidth = '', className = '' }) {
    return renderSettingsTable({
      id,
      headers,
      bodyHtml,
      minWidth,
      className
    });
  }

function renderSettingsShellLayout({ activePanel = '', bodyHtml = '', subtitle = 'Settings and appearance controls.' } = {}) {
  const finalUi = isSettingsSaasFinalUiActive();
  const referenceV2 = isSettingsReferenceV2Active()
    && TUTOR_DESK_SETTINGS_REFERENCE_V2_PANEL_SET.has(activePanel);
  return renderDsPluginShellFrame({
    className: `kg-settings-root kg-settings-shell-panel${finalUi ? ' kg-settings-shell-panel--saas-final-ui' : ''}${referenceV2 ? ' kg-settings-shell-panel--reference-v2' : ''}`,
    title: referenceV2 ? '' : 'Settings',
    subtitle: referenceV2 ? '' : subtitle,
    responsiveMode: 'adaptive',
    presentationMode: finalUi ? 'class-contract' : '',
    attrs: {
      ...(activePanel ? { 'data-active-panel': activePanel } : {}),
      ...(finalUi ? {
        'data-kg-saas-final-ui-settings': 'true',
        'data-kg-ui-role': 'settings-page',
        'data-kg-ui-state': 'default'
      } : {}),
      ...(referenceV2 ? {
        'data-kg-settings-reference-v2': 'true',
        'data-runtime-plugin-root': 'platform.settings'
      } : {})
    },
    contentHtml: renderDsStack({
      component: 'ds/settings-shell-body',
      className: 'kg-settings-shell-body',
      html: bodyHtml
    })
  });
}

const ACCESS_CAPABILITY_CATALOG = [
  { group: 'Tabs', keys: ['tabs.contacts', 'tabs.emails', 'tabs.companies', 'tabs.tasks', 'tabs.tasksboard', 'tabs.calendar', 'tabs.kpi', 'tabs.marketing', 'tabs.settings'] },
  { group: 'Modules', keys: [
    'module.contacts.read', 'module.contacts.write', 'module.contacts.delete',
    'module.emails.read', 'module.emails.write', 'module.emails.delete',
    'module.companies.read', 'module.companies.write', 'module.companies.delete',
    'module.tasks.read', 'module.tasks.write', 'module.tasks.delete',
    'module.tasksboard.read', 'module.tasksboard.write', 'module.tasksboard.delete',
    'module.calendar.read', 'module.calendar.write', 'module.calendar.delete',
    'module.kpi.read', 'module.kpi.write', 'module.kpi.delete',
    'module.marketing.read', 'module.marketing.write', 'module.marketing.delete',
    'module.settings.read', 'module.settings.write', 'module.settings.delete'
  ] },
  { group: 'Features', keys: ['feature.tasks.ai', 'feature.tasks.sendemail', 'feature.settings.access', 'feature.settings.admin'] }
];

function normalizeCapabilityKey(key) {
  return String(key || '').trim();
}

function deriveDynamicTabCapabilityKey(manifest = {}) {
  const normalizedManifest = manifest && typeof manifest === 'object' ? manifest : {};
  const tabConfig = normalizedManifest.tab && typeof normalizedManifest.tab === 'object'
    ? normalizedManifest.tab
    : {};
  const explicitTabKey = normalizeCapabilityKey(
    tabConfig.capability || tabConfig.capabilityKey || tabConfig.accessKey
  );
  if (explicitTabKey) return explicitTabKey;
  const manifestId = normalizeCapabilityKey(normalizedManifest.id);
  if (!manifestId) return '';
  const tabToken = manifestId.includes('.') ? manifestId.split('.').pop() : manifestId;
  return tabToken ? `tabs.${tabToken}` : '';
}

function buildDynamicAccessCapabilityCatalog(pluginManifests = []) {
  const tabs = new Set();
  const functions = new Set();
  const features = new Set();
  const manifests = Array.isArray(pluginManifests) ? pluginManifests : [];

  manifests.forEach((manifest) => {
    if (!manifest || typeof manifest !== 'object') return;
    const navConfig = manifest.nav && typeof manifest.nav === 'object' ? manifest.nav : {};
    if (!navConfig.hidden) {
      const tabCapabilityKey = deriveDynamicTabCapabilityKey(manifest);
      if (tabCapabilityKey) tabs.add(tabCapabilityKey);
    }

    const requiredCapabilities = Array.isArray(manifest.requiredCapabilities)
      ? manifest.requiredCapabilities
      : [];
    const publishedCapabilities = Array.isArray(manifest.publishedCapabilities)
      ? manifest.publishedCapabilities
      : [];
    const declaredCapabilities = publishedCapabilities.length ? publishedCapabilities : requiredCapabilities;
    declaredCapabilities.forEach((rawKey) => {
      const key = normalizeCapabilityKey(rawKey);
      if (!key) return;
      if (key.startsWith('feature.')) {
        features.add(key);
        return;
      }
      if (key.startsWith('tabs.')) {
        tabs.add(key);
        return;
      }
      functions.add(key);
      const moduleMatch = key.match(/^module\.([^.]+)\.read$/);
      if (!publishedCapabilities.length && moduleMatch && moduleMatch[1]) {
        functions.add(`module.${moduleMatch[1]}.write`);
        functions.add(`module.${moduleMatch[1]}.delete`);
      }
    });

    const featureFlags = Array.isArray(manifest.featureFlags) ? manifest.featureFlags : [];
    featureFlags.forEach((rawKey) => {
      const key = normalizeCapabilityKey(rawKey);
      if (key) features.add(key);
    });
  });

  return [
    tabs.size ? { group: 'Tabs', keys: [...tabs].sort((a, b) => a.localeCompare(b)) } : null,
    functions.size ? { group: 'Modules', keys: [...functions].sort((a, b) => a.localeCompare(b)) } : null,
    features.size ? { group: 'Features', keys: [...features].sort((a, b) => a.localeCompare(b)) } : null
  ].filter(Boolean);
}

function normalizeSettingsPanelDescriptor(value) {
  if (value && typeof value === 'object') {
    const id = String(value.id || '').trim();
    return {
      ...value,
      id,
      label: String(value.label || SETTINGS_PANEL_LABELS[id] || id).trim() || id
    };
  }
  const id = String(value || '').trim();
  return { id, label: SETTINGS_PANEL_LABELS[id] || id };
}

function buildSettingsPanelTab(panel) {
  const descriptor = normalizeSettingsPanelDescriptor(panel);
  const panelId = descriptor.id;
  return {
    id: panelId,
    label: descriptor.label,
    attrs: {
      'data-action': 'main-tab',
      'data-tab': panelId
    }
  };
}

function renderSettingsReferenceV2NavButton(panel, activePanel = '') {
  const descriptor = normalizeSettingsPanelDescriptor(panel);
  const active = descriptor.id === activePanel;
  const icon = TUTOR_DESK_SETTINGS_REFERENCE_V2_ICONS[descriptor.id] || 'settings';
  return `
    <button
      type="button"
      id="settings-reference-v2-tab-${escapeHtml(descriptor.id)}"
      class="kg-settings-reference-v2-nav__item${active ? ' is-active active' : ' is-inactive'}"
      data-action="main-tab"
      data-kg-action-classification="identifier"
      data-tab="${escapeHtml(descriptor.id)}"
      data-settings-reference-v2-icon="${escapeHtml(icon)}"
      role="tab"
      aria-selected="${active ? 'true' : 'false'}"
      aria-controls="settings-reference-v2-panel-${escapeHtml(descriptor.id)}"
      tabindex="${active ? '0' : '-1'}"
    >
      <span class="kg-settings-reference-v2-nav__icon" aria-hidden="true"></span>
      <span class="kg-settings-reference-v2-nav__label">${escapeHtml(descriptor.label === 'Tutor Desk preferences' ? 'Appearance' : descriptor.label)}</span>
    </button>
  `;
}

function renderSettingsReferenceV2Navigation(visiblePanels = [], activePanel = '', subtitle = '') {
  const panelById = new Map(visiblePanels.map((panel) => {
    const descriptor = normalizeSettingsPanelDescriptor(panel);
    return [descriptor.id, descriptor];
  }));
  const groups = TUTOR_DESK_SETTINGS_REFERENCE_V2_GROUPS.map((group) => {
    const panels = group.panelIds.map((panelId) => panelById.get(panelId)).filter(Boolean);
    if (!panels.length) return '';
    return `
      <section class="kg-settings-reference-v2-nav__group" aria-labelledby="settings-reference-v2-group-${escapeHtml(group.id)}">
        <h3 id="settings-reference-v2-group-${escapeHtml(group.id)}">${escapeHtml(group.label)}</h3>
        <div class="kg-settings-reference-v2-nav__items" role="presentation">
          ${panels.map((panel) => renderSettingsReferenceV2NavButton(panel, activePanel)).join('')}
        </div>
      </section>
    `;
  }).join('');
  const adminPanels = visiblePanels
    .map((panel) => normalizeSettingsPanelDescriptor(panel))
    .filter((panel) => !TUTOR_DESK_SETTINGS_REFERENCE_V2_PANEL_SET.has(panel.id));
  const adminGroup = adminPanels.length ? `
    <section class="kg-settings-reference-v2-nav__group kg-settings-reference-v2-nav__group--admin" aria-labelledby="settings-reference-v2-group-admin">
      <h3 id="settings-reference-v2-group-admin">Platform administration</h3>
      <div class="kg-settings-reference-v2-nav__items" role="presentation">
        ${adminPanels.map((panel) => renderSettingsReferenceV2NavButton(panel, activePanel)).join('')}
      </div>
    </section>
  ` : '';
  return `
    <button type="button" class="kg-settings-reference-v2-nav-toggle" data-action="settings-reference-v2-nav-toggle" data-kg-action-classification="identifier" aria-controls="settings-reference-v2-navigation" aria-expanded="false">
      <span aria-hidden="true"></span>
      <strong>Settings menu</strong>
    </button>
    <div class="kg-settings-reference-v2-nav-backdrop" data-action="settings-reference-v2-nav-close" data-kg-action-classification="identifier" hidden></div>
    <nav id="settings-reference-v2-navigation" class="kg-settings-reference-v2-nav" aria-label="Settings sections" data-settings-reference-v2-nav data-open="false">
      <header class="kg-settings-reference-v2-nav__header">
        <div>
          <h2>Settings</h2>
          <p>${escapeHtml(subtitle)}</p>
        </div>
        <button type="button" class="kg-settings-reference-v2-nav__close" data-action="settings-reference-v2-nav-close" data-kg-action-classification="identifier" aria-label="Close Settings menu"><span aria-hidden="true"></span></button>
      </header>
      <div class="kg-settings-reference-v2-nav__scroll" role="tablist" aria-orientation="vertical">
        ${groups}${adminGroup}
      </div>
    </nav>
  `;
}

function renderSettingsPanelStage(panel, activePanel = '') {
  const descriptor = normalizeSettingsPanelDescriptor(panel);
  const panelId = descriptor.id;
  const isCorePanel = Object.prototype.hasOwnProperty.call(SETTINGS_PANEL_LABELS, panelId);
  const bodyId = isCorePanel
    ? `settings${panelId.charAt(0).toUpperCase()}${panelId.slice(1)}`
    : '';
  const active = panelId === activePanel;
  return `
    [#div id="settings-reference-v2-panel-${escapeHtml(panelId)}" class="kg-settings-panel" data-panel="${escapeHtml(panelId)}" data-active="${active ? 'true' : 'false'}" role="tabpanel" aria-labelledby="settings-reference-v2-tab-${escapeHtml(panelId)}" tabindex="0"${active ? '' : ' hidden'}>
      [#div${bodyId ? ` id="${escapeHtml(bodyId)}"` : ''} class="kg-settings-panel-body${isCorePanel ? '' : ' kg-settings-contribution-host'}"${isCorePanel ? '' : ` data-settings-panel-host="${escapeHtml(panelId)}" data-settings-contribution-owned="true"`}>${renderEmpty('Loading...')}</div>
    </div>
  `;
}

async function init(lifecycleContext = {}) {
  const {
    mountEl,
    sharedState,
    ui,
    events,
    plugins
  } = lifecycleContext;
  ensureSettingsStyles();
  const role = String(sharedState && sharedState.userRole || 'user').toLowerCase();
  const authMode = String(sharedState && sharedState.authMode || 'single').toLowerCase();
  if (!mountEl) return {};

  function resolveAccessState(accessPayload = sharedState && sharedState.access ? sharedState.access : null) {
    const normalizedAccess = accessPayload && typeof accessPayload === 'object' ? accessPayload : null;
    const hasSettingsFeatureAccess = hasAccessKey(normalizedAccess, 'feature.settings.access', true);
    const canRead = hasSettingsFeatureAccess && hasAccessKey(normalizedAccess, 'module.settings.read', true);
    const canWrite = hasAccessKey(normalizedAccess, 'module.settings.write', true);
    const canDelete = hasAccessKey(normalizedAccess, 'module.settings.delete', true);
    const canAdmin = hasAccessKey(normalizedAccess, 'feature.settings.admin', true);
    return {
      access: normalizedAccess,
      canRead,
      canWrite,
      canDelete,
      canAdmin,
      viewPolicy: resolveSettingsViewPolicy({
        role,
        authMode,
        canRead,
        canAdmin,
        access: normalizedAccess,
        settingsPlugin: sharedState && sharedState.settingsPlugin
      })
    };
  }

  const initialAccessState = resolveAccessState();

  if (!initialAccessState.canRead) {
    setElementHtml(mountEl, renderSettingsShellLayout({
      subtitle: 'Settings access is required.',
      bodyHtml: renderSettingsNotice({
        kind: 'error',
        message: 'You do not have read access to the Settings module.'
      })
    }));
    return {};
  }

  const themePreferenceBridge = initialAccessState.viewPolicy.bridgeCoreThemePreference === true
    && sharedState?.themePreference?.authority === 'settings-v2-personal'
    && typeof sharedState.themePreference.read === 'function'
    && typeof sharedState.themePreference.save === 'function'
    ? sharedState.themePreference
    : null;
  const initialBridgedTheme = themePreferenceBridge
    ? themePreferenceBridge.read()
    : null;

  const persistedSettingsThemeMode = getPersistedSettingsThemeMode();
  const platformThemeAvailable = hasPersistedPlatformThemeMode();
  const persistedPlatformThemeMode = getPersistedPlatformThemeMode();
  const allowRuntimeEligibleAppearanceScope = initialAccessState.viewPolicy.appearanceScopePolicy === 'runtime-eligible';
  const canUseFullAppAppearanceScope = allowRuntimeEligibleAppearanceScope && canApplyThemeScope(FULL_APP_SCOPE_ID);
  const initialScopeId = canUseFullAppAppearanceScope
    ? (platformThemeAvailable
      ? String(persistedPlatformThemeMode.scopeId || FULL_APP_SCOPE_ID)
      : FULL_APP_SCOPE_ID)
    : SETTINGS_ONLY_SCOPE_ID;
  const initialThemeModeId = themePreferenceBridge
    ? String(initialBridgedTheme?.themeModeId || getDefaultThemeModeId())
    : (canUseFullAppAppearanceScope && platformThemeAvailable
        ? String(persistedPlatformThemeMode.themeId || getDefaultThemeModeId())
        : String(persistedSettingsThemeMode.themeId || getDefaultThemeModeId()));

  const state = {
    mounted: true,
    mountEl,
    sharedState,
    viewPolicy: initialAccessState.viewPolicy,
    permissions: {
      canRead: initialAccessState.canRead,
      canWrite: initialAccessState.canWrite,
      canDelete: initialAccessState.canDelete,
      canAdmin: initialAccessState.canAdmin
    },
    sessionInfo: null,
    archivePayload: null,
    sessions: [],
    activePanel: initialAccessState.viewPolicy.initialPanel,
    appearance: {
      selectedScopeId: initialScopeId,
      persistedScopeId: canUseFullAppAppearanceScope && platformThemeAvailable
        ? String(persistedPlatformThemeMode.scopeId || FULL_APP_SCOPE_ID)
        : SETTINGS_ONLY_SCOPE_ID,
      selectedThemeModeId: initialThemeModeId,
      persistedThemeModeId: initialThemeModeId,
      appliedThemeModeId: initialThemeModeId,
      previewThemeModeId: null,
      platformThemeState: persistedPlatformThemeMode,
      status: { kind: 'muted', text: 'Preview and apply theme modes for the selected scope.' },
      dsFlow: null
    },
    inlineSelectDsFlow: null,
    usersStatusTimer: 0,
    usageStatusTimer: 0,
    cleanup: [],
    deleteUser: {
      email: '',
      confirm: '',
      kind: 'muted',
      text: '',
      preview: null
    },
    filters: {
      adminsOnly: false,
      mfaOffOnly: false,
      query: ''
    },
    inviteInFlight: false,
    inviteCooldownUntil: 0,
    mfaEnrollment: null,
    perUserCooldownUntil: new Map(),
    inFlightActions: new Set(),
    users: [],
    usersByEmail: new Set(),
    events: {
      tab: 'audit',
      audit: { items: [], nextCursor: null, loading: false },
      auth: { items: [], nextCursor: null, loading: false },
      moreFilters: false,
      search: '',
      server: {
        audit: { module: '', action: '', actorEmail: '', from: '', to: '' },
        auth: { email: '', eventType: '', from: '', to: '' }
      },
      export: {
        from: '',
        to: '',
        format: 'ndjson'
      },
      modal: { open: false, title: '', payload: null }
    },
    usage: {
      tab: 'summary',
      summaryPayload: null,
      status: { kind: 'muted', text: '' },
      explorer: {
        items: [],
        nextCursor: null,
        moreFilters: false,
        search: '',
        server: { email: '', module: '', action: '', from: '', to: '' },
        export: { from: '', to: '', format: 'ndjson' },
        modal: { open: false, title: '', payload: null }
      }
    },
    contribution: {
      runtime: null,
      providerPluginId: '',
      pending: new Map()
    },
    controller: new AbortController()
  };

  const settingsPendingMutationActions = new Set();
  const settingsPendingControlState = new WeakMap();
  let settingsActivityDepth = 0;
  let hasLoadedSettingsContent = false;
  let initialDestinationSkeletonVisible = false;
  let settingsReferenceV2NavReturnFocus = null;

  function shouldRenderInitialDestinationSkeleton() {
    return isSettingsSaasFinalUiActive()
      && state.sharedState?.asyncFeedback?.enabled === true;
  }

  function renderInitialDestinationSkeleton() {
    if (!shouldRenderInitialDestinationSkeleton()) return false;
    setElementHtml(state.mountEl, renderTutorDeskDestinationSkeleton({
      surface: 'settings',
      title: 'Loading Settings',
      detail: 'Preparing account preferences and access controls.',
      testId: 'settings-destination-skeleton',
      location: 'initial-data'
    }));
    initialDestinationSkeletonVisible = true;
    return true;
  }

  function ensureSettingsChromeAfterInitialLoad() {
    if (!initialDestinationSkeletonVisible) return getPanelElements();
    initialDestinationSkeletonVisible = false;
    renderSettingsChrome();
    return getPanelElements();
  }

  function setSettingsReferenceV2NavigationOpen(open = false, returnFocus = null) {
    const nav = state.mountEl.querySelector('[data-settings-reference-v2-nav]');
    const backdrop = state.mountEl.querySelector('.kg-settings-reference-v2-nav-backdrop');
    const toggle = state.mountEl.querySelector('[data-action="settings-reference-v2-nav-toggle"]');
    if (!nav) return;
    const nextOpen = open === true;
    if (nextOpen && returnFocus instanceof HTMLElement) settingsReferenceV2NavReturnFocus = returnFocus;
    nav.setAttribute('data-open', nextOpen ? 'true' : 'false');
    backdrop?.toggleAttribute('hidden', !nextOpen);
    toggle?.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
    if (nextOpen) {
      const activeButton = nav.querySelector('button[data-action="main-tab"][aria-selected="true"]')
        || nav.querySelector('button[data-action="main-tab"]');
      activeButton?.focus?.({ preventScroll: true });
    } else if (settingsReferenceV2NavReturnFocus?.isConnected) {
      settingsReferenceV2NavReturnFocus.focus?.({ preventScroll: true });
      settingsReferenceV2NavReturnFocus = null;
    }
  }

  function closeSettingsReferenceV2Navigation() {
    const nav = state.mountEl.querySelector('[data-settings-reference-v2-nav]');
    if (nav?.getAttribute('data-open') === 'true') setSettingsReferenceV2NavigationOpen(false);
  }

  function readSettingsControlLabel(control = null) {
    if (!(control instanceof HTMLElement)) return '';
    return String(
      control.getAttribute('aria-label')
      || control.getAttribute('title')
      || control.querySelector?.('.ds-button__label')?.textContent
      || control.textContent
      || ''
    ).replace(/\s+/gu, ' ').trim();
  }

  function settingsActivityIsMutation(actionId = '') {
    const normalized = String(actionId || '').trim();
    return /(?:submit|save|create|update|delete|remove|archive|unarchive|approve|decline|cancel|complete|confirm|record|invite|accept|switch|reset|reply|replay|reproject|resolve|reopen|apply|retry|logout|passkey|elevation|incident|policy|capacity)/iu.test(normalized)
      || /^settings-(?:disable-user|enable-user|make-admin|make-user|revoke-sessions|send-setup|mfa-(?:enroll|verify))$/u.test(normalized);
  }

  function settingsActivityIsRoute(actionId = '') {
    return /(?:navigate|tab\||route|open-day-focus|receipt-operations)/iu.test(String(actionId || ''));
  }

  function resolveSettingsActivityDescriptor(actionEl = null, actionOverride = '') {
    const action = String(actionOverride || actionEl?.getAttribute?.('data-action') || '').trim();
    if (!action) return null;
    const attrs = {
      'data-action': action,
      'data-kg-semantic-action-id': String(actionEl?.getAttribute?.('data-kg-semantic-action-id') || '').trim(),
      'data-testid': String(actionEl?.getAttribute?.('data-testid') || '').trim(),
      role: String(actionEl?.getAttribute?.('role') || '').trim()
    };
    const finalAction = resolveSettingsFinalUiAction({
      label: readSettingsControlLabel(actionEl),
      attrs,
      className: String(actionEl?.getAttribute?.('class') || '').trim()
    }, actionEl?.ownerDocument || state.mountEl?.ownerDocument);
    const activityAction = String(finalAction?.actionId || '').trim();
    const lifecycleRequired = Boolean(String(finalAction?.busyLabel || '').trim())
      || /(?:logout|pager-|range-|filter|page-tab|view-option|users-filter|main-tab|usage-tab)/iu.test(activityAction);
    if (!activityAction || String(finalAction?.classification || '') === 'unclassified' || !lifecycleRequired) return null;
    const opClass = settingsActivityIsMutation(activityAction)
      ? 'user-mutation'
      : settingsActivityIsRoute(activityAction)
        ? 'route-transition'
        : 'foreground-read';
    const label = String(finalAction.canonicalLabel || finalAction.label || readSettingsControlLabel(actionEl) || 'Settings').trim();
    return {
      activityAction,
      opClass,
      label,
      busyLabel: String(finalAction.busyLabel || '').trim(),
      control: actionEl
    };
  }

  function restoreSettingsAttribute(control, name, value) {
    if (value == null) control.removeAttribute(name);
    else control.setAttribute(name, value);
  }

  function beginSettingsControlPending(descriptor = null) {
    if (state.sharedState?.asyncFeedback?.enabled !== true || !(descriptor?.control instanceof HTMLElement)) {
      return () => {};
    }
    const control = descriptor.control.closest('button, [role="button"], input[type="button"], input[type="submit"]')
      || descriptor.control;
    let snapshot = settingsPendingControlState.get(control);
    if (!snapshot) {
      snapshot = {
        count: 0,
        disabled: 'disabled' in control ? Boolean(control.disabled) : null,
        ariaBusy: control.getAttribute('aria-busy'),
        ariaDisabled: control.getAttribute('aria-disabled'),
        uiState: control.getAttribute('data-kg-ui-state'),
        actionBusy: control.getAttribute('data-tutor-desk-action-busy'),
        activityControl: control.getAttribute('data-kg-async-feedback-control')
      };
      settingsPendingControlState.set(control, snapshot);
    }
    snapshot.count += 1;
    control.setAttribute('data-kg-async-feedback-control', descriptor.activityAction);
    if (control.matches('button, input[type="button"], input[type="submit"]')) {
      setSettingsButtonRuntimeState(control, {
        busy: true,
        disabled: true,
        legacyLabel: descriptor.busyLabel || `${descriptor.label}…`
      });
    } else {
      control.setAttribute('aria-busy', 'true');
      control.setAttribute('aria-disabled', 'true');
      control.setAttribute('data-kg-ui-state', 'busy');
      control.setAttribute('data-tutor-desk-action-busy', 'true');
    }
    return () => {
      const current = settingsPendingControlState.get(control);
      if (!current) return;
      current.count = Math.max(0, current.count - 1);
      if (current.count > 0) return;
      settingsPendingControlState.delete(control);
      if (!control.isConnected) return;
      if (control.matches('button, input[type="button"], input[type="submit"]')) {
        setSettingsButtonRuntimeState(control, {
          busy: false,
          disabled: current.disabled === true
        });
      }
      restoreSettingsAttribute(control, 'aria-busy', current.ariaBusy);
      restoreSettingsAttribute(control, 'aria-disabled', current.ariaDisabled);
      restoreSettingsAttribute(control, 'data-kg-ui-state', current.uiState);
      restoreSettingsAttribute(control, 'data-tutor-desk-action-busy', current.actionBusy);
      restoreSettingsAttribute(control, 'data-kg-async-feedback-control', current.activityControl);
      if ('disabled' in control && current.disabled != null) control.disabled = current.disabled;
    };
  }

  async function runSettingsActivity(actionEl, operation, { action = '' } = {}) {
    const descriptor = resolveSettingsActivityDescriptor(actionEl, action);
    if (!descriptor || !ui?.activity || typeof ui.activity.begin !== 'function') {
      if (descriptor && state.sharedState?.asyncFeedback?.enabled === true) {
        setUsersStatus('error', 'Settings feedback is unavailable. Please refresh and try again.');
        return false;
      }
      return operation();
    }
    if (
      descriptor.opClass === 'user-mutation'
      && settingsPendingMutationActions.has(descriptor.activityAction)
    ) return false;
    if (descriptor.opClass === 'user-mutation') {
      settingsPendingMutationActions.add(descriptor.activityAction);
    }
    let handle = null;
    try {
      handle = ui.activity.begin({
        action: descriptor.activityAction,
        opClass: descriptor.opClass,
        summary: descriptor.busyLabel || `${descriptor.label} in progress`,
        detail: 'Settings will stay available while this finishes.'
      });
    } catch (error) {
      if (state.sharedState?.asyncFeedback?.enabled === true) {
        setUsersStatus('error', 'This Settings action is not safely catalogued. Please refresh and try again.');
        if (descriptor.opClass === 'user-mutation') settingsPendingMutationActions.delete(descriptor.activityAction);
        return false;
      }
    }
    const restorePending = beginSettingsControlPending(descriptor);
    settingsActivityDepth += 1;
    try {
      const result = await operation();
      if (result === false) handle?.cancel({ reason: 'user-cancelled' });
      else handle?.succeed({ summary: `${descriptor.label} ready` });
      return result;
    } catch (error) {
      if (String(error?.name || '') === 'AbortError') {
        handle?.cancel({ reason: 'presentation-aborted' });
      } else {
        handle?.fail({ summary: `${descriptor.label} failed`, detail: 'Please try again.' });
      }
      return false;
    } finally {
      settingsActivityDepth = Math.max(0, settingsActivityDepth - 1);
      restorePending();
      if (descriptor.opClass === 'user-mutation') settingsPendingMutationActions.delete(descriptor.activityAction);
    }
  }

  let suppressNextAccessChangedEvent = false;

  const ACCESS_RECOVERY_KEYS = new Set([
    'tabs.settings',
    'module.settings.read',
    'module.settings.write',
    'module.settings.delete',
    'feature.settings.access',
    'feature.settings.admin'
  ]);

  function getSettingsShellSubtitle() {
    if (String(state.viewPolicy?.providerPluginId || '').trim()) {
      return state.viewPolicy && state.viewPolicy.canSeeAdminPanels
        ? 'Administration, application settings, and appearance preferences.'
        : 'Application settings and appearance preferences.';
    }
    return state.viewPolicy && state.viewPolicy.canSeeAdminPanels
      ? 'Admin controls and appearance preferences.'
      : 'Appearance preferences for the current user.';
  }

  function renderFinalUiSettingsIdentity() {
    if (!isSettingsSaasFinalUiActive()) return '';
    const buildId = String(
      state.sharedState?.buildId
      || state.sharedState?.presentation?.buildId
      || ''
    ).trim();
    if (isSettingsReferenceV2Active()) {
      return `
        <span
          class="kg-settings-final-app-identity kg-settings-final-app-identity--visually-hidden"
          data-testid="settings-final-ui-app-identity"
          ${buildId ? 'data-platform-build-info-owner="true"' : ''}
        >${escapeHtml(buildId ? `TToria SaaS staging build ${buildId}` : 'TToria SaaS staging')}</span>
      `;
    }
    return `
      <article
        class="kg-settings-final-app-identity"
        data-testid="settings-final-ui-app-identity"
        ${buildId ? 'data-platform-build-info-owner="true"' : ''}
      >
        <span class="kg-settings-final-app-identity__logo" aria-hidden="true">
          <img src="/assets/ttoria-saas-staging-wordmark-final-ui-v1.png" alt="" />
        </span>
        <span class="kg-settings-final-app-identity__copy">
          <strong>TToria</strong>
          <span>${escapeHtml(buildId ? `Build #${buildId}` : 'SaaS staging')}</span>
        </span>
      </article>
    `;
  }

  function getPanelElements() {
    return {
      usersEl: state.mountEl.querySelector('#settingsUsers'),
      sessionsEl: state.mountEl.querySelector('#settingsSessions'),
      auditEl: state.mountEl.querySelector('#settingsAudit'),
      usageEl: state.mountEl.querySelector('#settingsUsage'),
      appearanceEl: state.mountEl.querySelector('#settingsAppearance'),
      archiveEl: state.mountEl.querySelector('#settingsArchive')
    };
  }

  function getVisiblePanelDescriptors() {
    const appPanelById = state.viewPolicy?.appPanelById instanceof Map
      ? state.viewPolicy.appPanelById
      : new Map();
    return (Array.isArray(state.viewPolicy?.visiblePanels) ? state.viewPolicy.visiblePanels : [])
      .map((panelId) => appPanelById.get(panelId) || normalizeSettingsPanelDescriptor(panelId));
  }

  function findContributionHost(attributeName, contributionId) {
    const wanted = String(contributionId || '').trim();
    if (!wanted) return null;
    const nodes = state.mountEl.querySelectorAll(`[${attributeName}]`);
    return [...nodes].find((node) => String(node.getAttribute(attributeName) || '').trim() === wanted) || null;
  }

  function syncActivePanelPresentation() {
    const root = state.mountEl.querySelector('.kg-settings-root');
    if (root) root.setAttribute('data-active-panel', state.activePanel);
    state.mountEl.querySelectorAll('.kg-settings-panel[data-panel]').forEach((panelEl) => {
      const panelId = String(panelEl.getAttribute('data-panel') || '').trim();
      const active = panelId === state.activePanel;
      panelEl.setAttribute('data-active', active ? 'true' : 'false');
      panelEl.toggleAttribute('hidden', !active);
    });
    state.mountEl.querySelectorAll('button[data-action="main-tab"]').forEach((button) => {
      const panelId = String(button.getAttribute('data-tab') || '').trim();
      const active = panelId === state.activePanel;
      button.classList.toggle('active', active);
      button.classList.toggle('is-active', active);
      button.classList.toggle('is-inactive', !active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      if (button.closest('[data-settings-reference-v2-nav]')) {
        button.setAttribute('tabindex', active ? '0' : '-1');
      }
    });
  }

  function getViewPolicySignature(viewPolicy = state.viewPolicy) {
    const normalizedViewPolicy = viewPolicy && typeof viewPolicy === 'object' ? viewPolicy : {};
    const visiblePanels = Array.isArray(normalizedViewPolicy.visiblePanels) ? normalizedViewPolicy.visiblePanels : [];
    return [
      visiblePanels.join('|'),
      normalizedViewPolicy.initialPanel || '',
      normalizedViewPolicy.appearanceScopePolicy || '',
      normalizedViewPolicy.canSeeAdminPanels === true ? '1' : '0',
      normalizedViewPolicy.requiresAdminData === true ? '1' : '0',
      String(normalizedViewPolicy.providerPluginId || ''),
      (Array.isArray(normalizedViewPolicy.appearanceSections)
        ? normalizedViewPolicy.appearanceSections.map((section) => section.id).join('|')
        : ''),
      normalizedViewPolicy.bridgeCoreThemePreference === true ? 'bridge-core-theme' : 'local-core-theme'
    ].join('::');
  }

  function syncSettingsRootThemeConsumer() {
    const settingsRootEl = state.mountEl.querySelector('.kg-settings-root');
    if (!settingsRootEl) return;
    const resolvedThemeState = syncAppearanceThemeState();
    unregisterSettingsThemeConsumer();
    if (isSettingsSaasFinalUiActive()) {
      clearLegacySettingsThemePresentation(settingsRootEl);
      return;
    }
    applySettingsThemeMode(settingsRootEl, resolvedThemeState.effectiveThemeModeId, { mode: 'apply' });
    registerSettingsThemeConsumer(settingsRootEl, {
      appliedThemeModeId: resolvedThemeState.appliedThemeModeId,
      priority: 100
    });
  }

  function clearLegacySettingsThemePresentation(targetEl) {
    if (!targetEl) return;
    const style = targetEl.style;
    if (style) {
      for (let index = style.length - 1; index >= 0; index -= 1) {
        const name = style.item(index);
        if (String(name || '').startsWith('--kg-settings-')) style.removeProperty(name);
      }
      if (!String(targetEl.getAttribute('style') || '').trim()) targetEl.removeAttribute('style');
    }
    targetEl.removeAttribute('data-kg-settings-theme-mode');
    targetEl.removeAttribute('data-kg-settings-theme-preview');
    targetEl.classList?.remove('kg-settings-themed-modal');
  }

  function renderSettingsChrome() {
    state.contribution.pending.clear();
    if (state.contribution.runtime) {
      const activeProviderPluginId = String(state.viewPolicy?.providerPluginId || '').trim();
      const disposeProvider = activeProviderPluginId !== state.contribution.providerPluginId;
      void state.contribution.runtime.unmountAll({ disposeProvider }).catch(() => {});
      if (disposeProvider) {
        state.contribution.runtime = null;
        state.contribution.providerPluginId = '';
      }
    }
    if (!(state.permissions && state.permissions.canRead)) {
      setElementHtml(state.mountEl, renderSettingsShellLayout({
        subtitle: 'Settings access is required.',
        bodyHtml: renderSettingsNotice({
          kind: 'error',
          message: 'You do not have read access to the Settings module.'
        })
      }));
      syncSettingsRootThemeConsumer();
      return;
    }

    const visiblePanels = getVisiblePanelDescriptors();
    const referenceV2Panel = isSettingsReferenceV2Active()
      && TUTOR_DESK_SETTINGS_REFERENCE_V2_PANEL_SET.has(state.activePanel);
    setElementHtml(state.mountEl, renderSettingsShellLayout({
      activePanel: state.activePanel,
      subtitle: getSettingsShellSubtitle(),
      bodyHtml: referenceV2Panel ? `
        ${renderFinalUiSettingsIdentity()}
        [#div class="kg-settings-reference-v2-layout">
          ${renderSettingsReferenceV2Navigation(visiblePanels, state.activePanel, getSettingsShellSubtitle())}
          <main class="kg-settings-reference-v2-content" data-settings-reference-v2-content>
            [#div class="kg-settings-panels">
              ${visiblePanels.map((panel) => renderSettingsPanelStage(panel, state.activePanel)).join('')}
            </div>
          </main>
        </div>
      ` : `
        ${renderFinalUiSettingsIdentity()}
        [#div class="kgc-stage-filters kg-settings-nav">
          ${renderSettingsTabs({
            id: 'settingsMainTabs',
            size: 'md',
            tabVariant: 'bar',
            activeId: state.activePanel,
            className: 'kg-settings-main-tabs',
            tabs: visiblePanels.map((panel) => buildSettingsPanelTab(panel))
          })}
        </div>

        [#div class="kg-settings-panels">
          ${visiblePanels.map((panel) => renderSettingsPanelStage(panel, state.activePanel)).join('')}
        </div>
      `
    }));
    syncActivePanelPresentation();
    if (hasLoadedSettingsContent) restoreLoadedSettingsPanelContent();
    syncSettingsRootThemeConsumer();
  }

  function restoreLoadedSettingsPanelContent() {
    const {
      usersEl,
      sessionsEl,
      auditEl,
      usageEl,
      appearanceEl,
      archiveEl
    } = getPanelElements();
    if (state.viewPolicy.requiresAdminData) {
      if (usersEl) {
        setElementHtml(usersEl, renderInviteAndUsers(state.users));
        setUsersStatus('muted', '');
      }
      if (sessionsEl) setElementHtml(sessionsEl, renderSessionsPanel(state.sessions));
      if (auditEl) setElementHtml(auditEl, renderEventsExplorer());
      if (usageEl) setElementHtml(usageEl, renderUsagePanel(state.usage.summaryPayload));
      if (archiveEl) setElementHtml(archiveEl, renderArchiveStatus(state.archivePayload));
    }
    if (appearanceEl) {
      setElementHtml(appearanceEl, renderAppearancePanel());
      mountAppearanceDsFlow();
    }
    mountInlineSelectDsFlow();
  }

  async function ensureContributionRuntime() {
    const providerPluginId = String(state.viewPolicy?.providerPluginId || '').trim();
    if (!providerPluginId) return null;
    if (
      state.contribution.runtime
      && state.contribution.providerPluginId === providerPluginId
    ) {
      return state.contribution.runtime;
    }
    if (state.contribution.runtime) {
      await state.contribution.runtime.unmountAll({ disposeProvider: true }).catch(() => {});
    }
    state.contribution.providerPluginId = providerPluginId;
    state.contribution.runtime = createSettingsContributionRuntime({
      providerPluginId,
      pluginManifests: Array.isArray(sharedState?.pluginManifests) ? sharedState.pluginManifests : [],
      plugins,
      providerContext: {
        ...lifecycleContext,
        mountEl: null,
        root: null,
        settingsHostPluginId: 'platform.settings',
        confirmAction: openConfirmModal
      }
    });
    return state.contribution.runtime;
  }

  function renderContributionFailure(kind, descriptor, hostEl) {
    if (!hostEl) return;
    setElementHtml(hostEl, `${renderSettingsNotice({
      kind: 'error',
      className: 'kg-settings-notice-compact',
      message: `${descriptor.label} could not be loaded. Core Settings remains available.`
    })}${renderSettingsButton({
      label: 'Retry',
      busyLabel: 'Retrying…',
      intent: 'Secondary',
      attrs: {
        'data-action': 'platform-shell-load-retry-action',
        'data-contribution-kind': kind,
        'data-contribution-id': descriptor.id
      }
    })}`);
  }

  async function mountContributionSurface(kind, descriptor, { force = false } = {}) {
    if (!state.mounted || !descriptor) return;
    const normalizedKind = kind === 'appearance' ? 'appearance' : 'panel';
    const attributeName = normalizedKind === 'appearance'
      ? 'data-settings-appearance-section-host'
      : 'data-settings-panel-host';
    const hostEl = findContributionHost(attributeName, descriptor.id);
    if (!hostEl) return;
    const pendingKey = `${normalizedKind}:${descriptor.id}`;
    if (state.contribution.pending.has(pendingKey) && !force) {
      return state.contribution.pending.get(pendingKey);
    }

    const task = Promise.resolve().then(async () => {
      const runtime = await ensureContributionRuntime();
      if (!runtime) return;
      if (force) await runtime.unmountSurface(normalizedKind, descriptor).catch(() => {});
      if (!runtime.hasMountedSurface(normalizedKind, descriptor) || force) {
        setElementHtml(hostEl, renderSettingsNotice({
          kind: 'loading',
          className: 'kg-settings-notice-compact',
          message: `Loading ${descriptor.label}...`
        }));
      }
      await runtime.mountSurface({
        kind: normalizedKind,
        descriptor,
        mountEl: hostEl
      });
    }).catch((error) => {
      if (!state.mounted) return;
      console.error('[platform-settings] app contribution failed', {
        providerPluginId: state.contribution.providerPluginId,
        contributionId: descriptor.id,
        kind: normalizedKind,
        code: String(error?.code || 'SETTINGS_CONTRIBUTION_FAILED')
      });
      const currentHost = findContributionHost(attributeName, descriptor.id);
      renderContributionFailure(normalizedKind, descriptor, currentHost);
    }).finally(() => {
      if (state.contribution.pending.get(pendingKey) === task) {
        state.contribution.pending.delete(pendingKey);
      }
    });
    state.contribution.pending.set(pendingKey, task);
    return task;
  }

  async function mountActiveContributionPanel(options = {}) {
    const descriptor = state.viewPolicy?.appPanelById instanceof Map
      ? state.viewPolicy.appPanelById.get(state.activePanel)
      : null;
    if (!descriptor) return;
    await mountContributionSurface('panel', descriptor, options);
  }

  async function mountAppearanceContributions(options = {}) {
    const sections = Array.isArray(state.viewPolicy?.appearanceSections)
      ? state.viewPolicy.appearanceSections
      : [];
    await Promise.all(sections.map((descriptor) => (
      mountContributionSurface('appearance', descriptor, options)
    )));
  }

  function unmountAppearanceContributionSurfaces() {
    const runtime = state.contribution.runtime;
    if (!runtime) return;
    const sections = Array.isArray(state.viewPolicy?.appearanceSections)
      ? state.viewPolicy.appearanceSections
      : [];
    sections.forEach((descriptor) => {
      void runtime.unmountSurface('appearance', descriptor).catch(() => {});
    });
  }

  function reconcileAccessState(nextAccess = sharedState && sharedState.access ? sharedState.access : null) {
    const previousCanRead = Boolean(state.permissions && state.permissions.canRead);
    const previousViewPolicySignature = getViewPolicySignature();
    const resolvedAccessState = resolveAccessState(nextAccess);
    state.permissions.canRead = resolvedAccessState.canRead;
    state.permissions.canWrite = resolvedAccessState.canWrite;
    state.permissions.canDelete = resolvedAccessState.canDelete;
    state.permissions.canAdmin = resolvedAccessState.canAdmin;
    state.viewPolicy = resolvedAccessState.viewPolicy;
    if (sharedState && typeof sharedState === 'object') {
      sharedState.access = resolvedAccessState.access || { version: 1, list: [] };
    }
    if (!(state.permissions && state.permissions.canRead)) {
      state.activePanel = '';
    } else if (!state.viewPolicy.visiblePanelSet.has(state.activePanel)) {
      state.activePanel = state.viewPolicy.initialPanel;
    }
    const viewChanged = previousViewPolicySignature !== getViewPolicySignature();
    const readChanged = previousCanRead !== Boolean(state.permissions && state.permissions.canRead);
    if (viewChanged || readChanged) {
      state.archivePayload = null;
      state.usage.summaryPayload = null;
      destroyInlineSelectDsFlow();
      destroyAppearanceDsFlow();
      renderSettingsChrome();
    }
    return {
      canRead: resolvedAccessState.canRead,
      viewChanged,
      readChanged,
      accessChanged: true
    };
  }

  function isSameAccessPayload(left, right) {
    const leftJson = JSON.stringify(left && typeof left === 'object' ? left : { version: 1, list: [] });
    const rightJson = JSON.stringify(right && typeof right === 'object' ? right : { version: 1, list: [] });
    return leftJson === rightJson;
  }

  function syncShellAccess(accessPayload, options = {}) {
    const normalizedAccess = accessPayload && typeof accessPayload === 'object' ? accessPayload : { version: 1, list: [] };
    const notifyShell = options && options.notifyShell === true;
    if (notifyShell && ui && typeof ui.setAccess === 'function') {
      suppressNextAccessChangedEvent = true;
      try {
        ui.setAccess(normalizedAccess);
        return;
      } catch {
        suppressNextAccessChangedEvent = false;
        if (sharedState && typeof sharedState === 'object') {
          sharedState.access = normalizedAccess;
        }
      }
      return;
    }
    if (sharedState && typeof sharedState === 'object') {
      sharedState.access = normalizedAccess;
    }
  }

  function isCurrentUserEmail(email) {
    const currentEmail = normalizeEmail(sharedState && sharedState.userEmail ? sharedState.userEmail : '');
    const targetEmail = normalizeEmail(email);
    return Boolean(currentEmail && targetEmail && currentEmail === targetEmail);
  }

  function getActiveTabId() {
    const activeTabButton = document.querySelector('#platformNav [data-platform-tab][data-active="true"]')
      || document.querySelector('#platformNav [data-platform-tab]');
    const tabId = activeTabButton ? String(activeTabButton.getAttribute('data-platform-tab') || '').trim() : '';
    return tabId || 'platform.settings';
  }

  function syncCurrentUserAccessInShell(user, accessPayload) {
    const currentEmail = normalizeEmail(sharedState && sharedState.userEmail ? sharedState.userEmail : '');
    const targetEmail = normalizeEmail(user && user.email ? user.email : '');
    if (!currentEmail || !targetEmail || currentEmail !== targetEmail) return false;
    if (!sharedState || typeof sharedState !== 'object') return false;

    const nextAccess = accessPayload && typeof accessPayload === 'object' ? accessPayload : { version: 1, list: [] };
    if (ui && typeof ui.setAccess === 'function') {
      try {
        ui.setAccess(nextAccess);
      } catch {
        sharedState.access = nextAccess;
      }
    } else {
      sharedState.access = nextAccess;
    }
    if (ui && typeof ui.renderNavButtons === 'function') {
      try { ui.renderNavButtons(getActiveTabId()); } catch {}
    }
    return true;
  }

  // Portal modal: keep Details dialogs fixed in the viewport and independent of panel scroll/layout.
  const modalPortal = document.createElement('div');
  modalPortal.id = 'kg-settings-modal-portal-v1';
  setElementHtml(modalPortal, `
    [#div class="kg-settings-modal-backdrop hidden" data-action="kg-modal-close"${renderSettingsFinalUiActionAttributes(
      { 'data-action': 'kg-modal-close' },
      'Close',
      { uiRole: 'modal-backdrop', includeActionGeometry: false }
    )}>
      [#div class="kg-settings-modal-frame-host"></div>
    </div>
  `);
  const modalBackdrop = modalPortal.querySelector('.kg-settings-modal-backdrop');
  const modalFrameHost = modalPortal.querySelector('.kg-settings-modal-frame-host');
  const portalModalController = createSettingsModalController({
    closeOnEscape: true
  });
  if (modalFrameHost) {
    portalModalController.mount(modalFrameHost);
  }
  document.body.appendChild(modalPortal);

  const modalState = { open: false, kind: 'json', title: '', payload: null, deleteCtx: null, accessCtx: null, messageCtx: null, confirmCtx: null, userActionsCtx: null };
  let pendingConfirmResolve = null;

  function renderPortalDeleteActionButton(enabled = false) {
    return `<span data-delete-button-host="1">${renderSettingsButton({
      label: 'Delete user',
      danger: true,
      type: 'button',
      disabled: !enabled,
      state: enabled ? 'Default' : 'Disabled',
      attrs: { 'data-action': 'kg-delete-exec' }
    })}</span>`;
  }

  function buildPortalModalConfig() {
    if (modalState.kind === 'access-edit' && modalState.accessCtx) {
      const ctx = modalState.accessCtx;
      const user = ctx.user || {};
      const access = ctx.access || {};
      const recoveryMode = Boolean(ctx && ctx.recoveryMode);
      const dynamicCapabilityCatalog = !recoveryMode
        ? buildDynamicAccessCapabilityCatalog(sharedState && Array.isArray(sharedState.pluginManifests) ? sharedState.pluginManifests : [])
        : [];
      const capabilityCatalog = recoveryMode
        ? [
            {
              group: 'Recovery',
              keys: ['tabs.settings', 'module.settings.read', 'module.settings.write', 'module.settings.delete', 'feature.settings.access', 'feature.settings.admin']
            }
          ]
        : (dynamicCapabilityCatalog.length ? dynamicCapabilityCatalog : ACCESS_CAPABILITY_CATALOG);
      const list = Array.isArray(access.list) ? access.list : [];
      const active = new Set(list);
      const rows = capabilityCatalog.map((group) => {
        const groupLabel = group.group === 'Modules'
          ? 'Functions'
          : group.group === 'Features'
            ? 'AI Features'
            : group.group;
        const items = group.keys.map((key) => {
          return `[#div class="kg-settings-modal-access-item">${renderSettingsCheckbox({
            label: key,
            checked: active.has(key),
            size: 'md',
            className: 'kg-settings-access-checkbox',
            inputAttrs: { 'data-access-key': key }
          })}</div>`;
        }).join('');
        return renderSettingsSubPanel({
          title: groupLabel,
          bodyHtml: `[#div class="kg-settings-modal-access-items">${items}</div>`,
          className: 'kg-settings-modal-access-group'
        });
      }).join('');

      return {
        size: 'md',
        title: modalState.title,
        bodyHtml: `
          [#div class="kg-settings-modal-access-shell">
            [#div class="modal-message">${recoveryMode ? `Recovery mode: restore your Settings permissions for <strong>${escapeHtml(String(user.email || 'user'))}</strong>.` : `Configure module/tab/feature access for <strong>${escapeHtml(String(user.email || 'user'))}</strong>.`}</div>
            [#div class="kg-settings-modal-access-layout">${rows}</div>
          </div>
        `,
        footerHtml: `[#div class="kg-settings-modal-access-footer">${[
          renderSettingsButton({
            label: 'Cancel',
            secondary: true,
            type: 'button',
            attrs: { 'data-action': 'kg-modal-close' }
          }),
          renderSettingsButton({
            label: 'Save access',
            type: 'button',
            attrs: {
              'data-action': 'kg-access-save',
              'data-user-id': String(user.id || '')
            }
          })
        ].join('')}</div>`
      };
    }

    if (modalState.kind === 'message' && modalState.messageCtx) {
      const messageCtx = modalState.messageCtx || {};
      return {
        size: 'md',
        title: modalState.title,
        bodyHtml: `
          [#div class="kg-settings-modal-details-shell">
            ${messageCtx.bodyHtml || renderSettingsNotice({
              kind: messageCtx.kind === 'error' ? 'error' : messageCtx.kind === 'ok' ? 'ok' : 'info',
              className: 'kg-settings-notice-compact',
              message: String(messageCtx.message || '')
            })}
          </div>
        `,
        footerHtml: [
          messageCtx.copyText ? renderSettingsButton({
            label: String(messageCtx.copyLabel || 'Copy'),
            secondary: true,
            type: 'button',
            attrs: { 'data-action': 'kg-modal-copy-value' }
          }) : '',
          renderSettingsButton({
            label: 'Close',
            secondary: true,
            type: 'button',
            attrs: { 'data-action': 'kg-modal-close' }
          })
        ].filter(Boolean).join('')
      };
    }

    if (modalState.kind === 'confirm' && modalState.confirmCtx) {
      const confirmCtx = modalState.confirmCtx || {};
      return {
        size: 'md',
        title: modalState.title,
        bodyHtml: `
          [#div class="kg-settings-modal-details-shell">
            [#div class="modal-message">${escapeHtml(String(confirmCtx.message || ''))}</div>
          </div>
        `,
        footerHtml: [
          renderSettingsButton({
            label: String(confirmCtx.cancelLabel || 'Cancel'),
            secondary: true,
            type: 'button',
            attrs: { 'data-action': 'kg-confirm-cancel' }
          }),
          renderSettingsButton({
            label: String(confirmCtx.confirmLabel || 'Confirm'),
            intent: confirmCtx.danger ? 'Danger' : 'Primary',
            type: 'button',
            attrs: {
              'data-action': 'kg-confirm-accept',
              ...(isSettingsSaasFinalUiActive() && confirmCtx.danger
                ? { 'data-kg-semantic-action-id': 'settings-confirm-danger' }
                : {})
            }
          })
        ].join('')
      };
    }

    if (modalState.kind === 'user-actions' && modalState.userActionsCtx) {
      const ctx = modalState.userActionsCtx || {};
      const user = ctx.user || {};
      const buttons = buildUserActionButtons(user, { compact: false });
      return {
        size: 'md',
        title: modalState.title,
        bodyHtml: `
          [#div class="kg-settings-modal-details-shell">
            [#div class="modal-message">Choose an action for ${escapeHtml(String(user.email || 'this user'))}.</div>
            [#div class="kg-settings-actions">${buttons.length ? buttons.join('') : '<span class="kg-settings-muted">Read-only</span>'}</div>
          </div>
        `,
        footerHtml: renderSettingsButton({
          label: 'Close',
          secondary: true,
          type: 'button',
          attrs: { 'data-action': 'kg-modal-close' }
        })
      };
    }

    if (modalState.kind === 'delete-user' && modalState.deleteCtx) {
      const ctx = modalState.deleteCtx;
      const preview = ctx.preview || {};
      const blocked = Boolean(preview && preview.blocked);
      const blockReason = String(preview && preview.blockReason || '');
      const tgt = preview && preview.target ? preview.target : null;
      const would = preview && preview.would ? preview.would : null;
      const del = would && would.delete ? would.delete : {};
      const anon = would && would.anonymize ? would.anonymize : {};

      const summary = blocked
        ? (blockReason ? `Blocked: ${blockReason}` : 'Blocked.')
        : 'Type DELETE_USER to confirm deletion. This deletes the account and anonymizes its logs in D1.';
      const previewPayload = { target: tgt, would: would ? { delete: del, anonymize: anon } : null };

      return {
        size: 'md',
        title: modalState.title,
        bodyHtml: `
          [#div class="kg-settings-modal-details-shell">
            [#div class="modal-message">${escapeHtml(summary)}</div>
            [#div class="kg-settings-modal-details-scroll">
              ${renderStructured(previewPayload, { maxDepth: 6, maxItems: 200 })}
            </div>
            ${blocked ? '' : `
              [#div${isSettingsSaasFinalUiActive() ? ' class="kg-settings-final-delete-confirm-field"' : ' style="margin-top:12px;"'}>
                ${renderSettingsInput({
                  label: 'Confirm',
                  helperText: '',
                  id: 'kgDeleteConfirmField',
                  inputId: 'kgDeleteConfirmInput',
                  size: 'sm',
                  value: '',
                  placeholder: 'Type DELETE_USER',
                  inputAttrs: {
                    autocomplete: 'off',
                    autocapitalize: 'off',
                    spellcheck: 'false'
                  }
                })}
              </div>
            `}
          </div>
        `,
        footerHtml: [
          renderSettingsButton({
            label: 'Cancel',
            secondary: true,
            type: 'button',
            attrs: { 'data-action': 'kg-modal-close' }
          }),
          blocked ? '' : renderPortalDeleteActionButton(false)
        ].join('')
      };
    }

    return {
      size: 'lg',
      title: modalState.title,
      bodyHtml: `
        [#div class="kg-settings-modal-details-shell">
          [#div class="modal-message">Details</div>
          [#div class="kg-settings-modal-details-scroll">
            ${renderStructured(modalState.payload, { maxDepth: 6, maxItems: 200 })}
          </div>
        </div>
      `,
      footerHtml: [
        renderSettingsButton({
          label: 'Copy JSON',
          secondary: true,
          type: 'button',
          attrs: { 'data-action': 'kg-modal-copy' }
        }),
        renderSettingsButton({
          label: 'Close',
          secondary: true,
          type: 'button',
          attrs: { 'data-action': 'kg-modal-close' }
        })
      ].join('')
    };
  }

  function safeStringifyStructured(value, opts = {}) {
    const maxDepth = Number.isFinite(opts.maxDepth) ? Math.max(0, Number(opts.maxDepth)) : 6;
    const maxItems = Number.isFinite(opts.maxItems) ? Math.max(0, Number(opts.maxItems)) : 200;
    const seen = new WeakSet();

    function isDateLike(input) {
      return Object.prototype.toString.call(input) === '[object Date]';
    }

    function visit(input, depth) {
      if (depth > maxDepth) return '(max depth reached)';
      if (input === null) return null;
      if (typeof input === 'undefined') return null;
      if (typeof input === 'string' || typeof input === 'number' || typeof input === 'boolean') return input;
      if (typeof input === 'bigint') return String(input);
      if (typeof input === 'function' || typeof input === 'symbol') return `(${typeof input})`;
      if (isDateLike(input)) {
        return Number.isNaN(input.getTime()) ? String(input) : input.toISOString();
      }
      if (!input || typeof input !== 'object') return String(input);
      if (seen.has(input)) return '(circular)';

      seen.add(input);
      let normalized;

      if (Array.isArray(input)) {
        normalized = input.slice(0, maxItems).map((item) => visit(item, depth + 1));
        if (input.length > maxItems) {
          normalized.push(`+${input.length - maxItems} more...`);
        }
      } else {
        normalized = {};
        const keys = Object.keys(input);
        keys.slice(0, maxItems).forEach((key) => {
          normalized[key] = visit(input[key], depth + 1);
        });
        if (keys.length > maxItems) {
          normalized.__truncated__ = `+${keys.length - maxItems} more...`;
        }
      }

      seen.delete(input);
      return normalized;
    }

    try {
      return JSON.stringify(visit(value, 0), null, 2);
    } catch {
      return JSON.stringify({ error: 'Unable to serialize.' }, null, 2);
    }
  }

  function renderPortalModal(options = {}) {
    const normalized = options && typeof options === 'object' ? options : {};
    const skipControllerClose = Boolean(normalized.skipControllerClose);
    const finalUi = isSettingsSaasFinalUiActive();
    if (!modalBackdrop || !modalFrameHost) return;
    const clearThemeTarget = (targetEl) => {
      if (!targetEl || !targetEl.style) return;
      const style = targetEl.style;
      for (let index = style.length - 1; index >= 0; index -= 1) {
        const name = style.item(index);
        if (String(name || '').startsWith('--kg-settings-')) {
          style.removeProperty(name);
        }
      }
      targetEl.removeAttribute('data-kg-settings-theme-mode');
      targetEl.removeAttribute('data-kg-settings-theme-preview');
      if (targetEl.classList) {
        targetEl.classList.remove('kg-settings-themed-modal');
      }
    };

    const readModalThemeState = () => {
      const settingsRootEl = state.mountEl && typeof state.mountEl.querySelector === 'function'
        ? state.mountEl.querySelector('.kg-settings-root')
        : null;
      if (!settingsRootEl || !settingsRootEl.style) return null;

      const sourceStyle = settingsRootEl.style;
      const vars = [];
      for (let index = 0; index < sourceStyle.length; index += 1) {
        const name = sourceStyle.item(index);
        if (!String(name || '').startsWith('--kg-settings-')) continue;
        vars.push([
          name,
          sourceStyle.getPropertyValue(name),
          sourceStyle.getPropertyPriority(name)
        ]);
      }

      return {
        vars,
        themeModeId: settingsRootEl.getAttribute('data-kg-settings-theme-mode')
          || (settingsRootEl.dataset && settingsRootEl.dataset.kgSettingsThemeMode)
          || '',
        previewFlag: settingsRootEl.getAttribute('data-kg-settings-theme-preview')
          || (settingsRootEl.dataset && settingsRootEl.dataset.kgSettingsThemePreview)
          || ''
      };
    };

    const applyThemeStateToTarget = (targetEl, themeState, options = {}) => {
      clearThemeTarget(targetEl);
      if (!targetEl || !themeState) return;
      const addModalClass = Boolean(options.addModalClass);
      themeState.vars.forEach(([name, value, priority]) => {
        targetEl.style.setProperty(name, value, priority);
      });
      if (themeState.themeModeId) {
        targetEl.setAttribute('data-kg-settings-theme-mode', String(themeState.themeModeId));
      }
      if (themeState.previewFlag) {
        targetEl.setAttribute('data-kg-settings-theme-preview', 'true');
      }
      if (addModalClass && targetEl.classList) {
        targetEl.classList.add('kg-settings-themed-modal');
      }
    };

    if (!modalState.open) {
      clearThemeTarget(modalBackdrop);
      clearThemeTarget(modalFrameHost.querySelector('.ds-modal[data-ds-component="ds/modal"]'));
      modalBackdrop.classList.add('hidden');
      modalFrameHost.removeAttribute('data-modal-kind');
      if (!skipControllerClose) {
        portalModalController.close('settings-close');
      }
      return;
    }

    const modalThemeState = finalUi ? null : readModalThemeState();
    if (finalUi) clearThemeTarget(modalBackdrop);
    else applyThemeStateToTarget(modalBackdrop, modalThemeState);
    modalBackdrop.classList.remove('hidden');
    modalFrameHost.setAttribute('data-modal-kind', modalState.kind || '');
    const config = normalizeLegacyMarkupConfig(buildPortalModalConfig());
    portalModalController.open({
      id: 'kgSettingsModalFrame',
      ...config
    }, modalFrameHost);
    const modalSurface = modalFrameHost.querySelector('.ds-modal[data-ds-component="ds/modal"]');
    if (finalUi) clearThemeTarget(modalSurface);
    else applyThemeStateToTarget(modalSurface, modalThemeState, { addModalClass: true });
  }

  function openPortalModal(title, payload) {
    modalState.open = true;
    modalState.kind = 'json';
    modalState.title = String(title || '');
    modalState.payload = payload || null;
    modalState.deleteCtx = null;
    modalState.accessCtx = null;
    modalState.messageCtx = null;
    modalState.confirmCtx = null;
    modalState.userActionsCtx = null;
    renderPortalModal();
  }

  function openDeleteUserModal(title, preview) {
    modalState.open = true;
    modalState.kind = 'delete-user';
    modalState.title = String(title || 'Delete user?');
    modalState.payload = null;
    modalState.deleteCtx = { preview: preview || null };
    modalState.accessCtx = null;
    modalState.messageCtx = null;
    modalState.confirmCtx = null;
    modalState.userActionsCtx = null;
    renderPortalModal();
  }

  function openAccessModal(title, user, access, options = {}) {
    modalState.open = true;
    modalState.kind = 'access-edit';
    modalState.title = String(title || 'Edit access');
    modalState.payload = null;
    modalState.deleteCtx = null;
    modalState.accessCtx = { user: user || null, access: access || null, recoveryMode: Boolean(options && options.recoveryMode) };
    modalState.messageCtx = null;
    modalState.confirmCtx = null;
    modalState.userActionsCtx = null;
    renderPortalModal();
  }

  function openMessageModal(title, options = {}) {
    const normalized = options && typeof options === 'object' ? options : {};
    modalState.open = true;
    modalState.kind = 'message';
    modalState.title = String(title || 'Settings');
    modalState.payload = null;
    modalState.deleteCtx = null;
    modalState.accessCtx = null;
    modalState.messageCtx = {
      kind: normalized.kind === 'error' ? 'error' : normalized.kind === 'ok' ? 'ok' : 'info',
      message: String(normalized.message || ''),
      bodyHtml: normalized.bodyHtml || '',
      copyText: normalized.copyText == null ? '' : String(normalized.copyText),
      copyLabel: normalized.copyLabel == null ? '' : String(normalized.copyLabel)
    };
    modalState.confirmCtx = null;
    modalState.userActionsCtx = null;
    renderPortalModal();
  }

  function settleConfirmModal(result) {
    const resolve = pendingConfirmResolve;
    pendingConfirmResolve = null;
    if (resolve) {
      try { resolve(Boolean(result)); } catch {}
    }
  }

  function openConfirmModal(options = {}) {
    const normalized = options && typeof options === 'object' ? options : {};
    if (pendingConfirmResolve) {
      settleConfirmModal(false);
    }
    modalState.open = true;
    modalState.kind = 'confirm';
    modalState.title = String(normalized.title || 'Confirm');
    modalState.payload = null;
    modalState.deleteCtx = null;
    modalState.accessCtx = null;
    modalState.messageCtx = null;
    modalState.confirmCtx = {
      message: String(normalized.message || ''),
      confirmLabel: String(normalized.confirmLabel || 'Confirm'),
      cancelLabel: String(normalized.cancelLabel || 'Cancel'),
      danger: Boolean(normalized.danger)
    };
    modalState.userActionsCtx = null;
    renderPortalModal();
    return new Promise((resolve) => {
      pendingConfirmResolve = resolve;
    });
  }

  function openUserActionsModal(user) {
    modalState.open = true;
    modalState.kind = 'user-actions';
    modalState.title = String(user && user.email ? user.email : 'User actions');
    modalState.payload = null;
    modalState.deleteCtx = null;
    modalState.accessCtx = null;
    modalState.messageCtx = null;
    modalState.confirmCtx = null;
    modalState.userActionsCtx = { user: user || null };
    renderPortalModal();
  }

  function closePortalModal(options = {}) {
    const normalized = options && typeof options === 'object' ? options : {};
    if (!Boolean(normalized.skipConfirmResolve) && modalState.kind === 'confirm') {
      settleConfirmModal(false);
    }
    modalState.open = false;
    modalState.kind = 'json';
    modalState.title = '';
    modalState.payload = null;
    modalState.deleteCtx = null;
    modalState.accessCtx = null;
    modalState.messageCtx = null;
    modalState.confirmCtx = null;
    modalState.userActionsCtx = null;
    renderPortalModal(normalized);
  }

  function handlePortalModalClose() {
    closePortalModal({ skipControllerClose: true });
  }

  if (modalFrameHost) {
    modalFrameHost.addEventListener('ds:modal:close', handlePortalModalClose);
  }
  state.cleanup.push(() => {
    try {
      if (modalFrameHost) {
        modalFrameHost.removeEventListener('ds:modal:close', handlePortalModalClose);
      }
    } catch {}
    try { portalModalController.destroy(); } catch {}
    try { modalPortal.remove(); } catch {}
  });

  const onModalPortalClick = async (event) => {
    const el = event && event.target && event.target.closest ? event.target.closest('[data-action]') : null;
    if (!el) return;
    const action = String(el.getAttribute('data-action') || '').trim();
    if (action === 'kg-modal-close') {
      // The backdrop carries data-action for click-outside-to-close, but we must not close
      // when clicking inside the modal card (event.target will be a descendant).
      if (el.classList && el.classList.contains('kg-settings-modal-backdrop') && event && event.target !== el) return;
      closePortalModal();
      return;
    }
    if (action === 'kg-confirm-cancel') {
      settleConfirmModal(false);
      closePortalModal({ skipConfirmResolve: true });
      return;
    }
    if (action === 'kg-confirm-accept') {
      settleConfirmModal(true);
      closePortalModal({ skipConfirmResolve: true });
      return;
    }
    if (action === 'kg-modal-copy') {
      try {
        await navigator.clipboard.writeText(safeStringifyStructured(modalState.payload, { maxDepth: 6, maxItems: 200 }));
        setUsersStatus('ok', 'Copied JSON.');
      } catch {
        setUsersStatus('error', 'Unable to copy.');
      }
      return;
    }
    if (action === 'kg-modal-copy-value') {
      const value = String(modalState.messageCtx && modalState.messageCtx.copyText || '').trim();
      if (!value) {
        openMessageModal('Settings Error', { kind: 'error', message: 'Nothing to copy.' });
        return;
      }
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        openMessageModal('Settings Error', { kind: 'error', message: 'Unable to copy.' });
      }
      return;
    }
    if (modalState.kind === 'user-actions' && action && !action.startsWith('kg-')) {
      await handleActionClick(el, event);
      return;
    }
    if (action === 'kg-delete-exec') {
      if (!(state.permissions && state.permissions.canDelete)) {
        setUsersStatus('error', 'Delete access is required.');
        return;
      }
      // Delete-by-id modal only. Guard hard in case of miswire.
      const ctx = modalState && modalState.kind === 'delete-user' ? modalState.deleteCtx : null;
      const preview = ctx && ctx.preview ? ctx.preview : null;
      const tgt = preview && preview.target ? preview.target : null;
      const targetUserId = tgt && tgt.id ? String(tgt.id) : '';
      const inputEl = modalPortal.querySelector('#kgDeleteConfirmInput');
      const confirmText = String(inputEl && inputEl.value || '').trim();
      if (!targetUserId || confirmText !== 'DELETE_USER') return;

      const btn = el;
      if (state.sharedState?.asyncFeedback?.enabled !== true) try {
        setSettingsButtonRuntimeState(btn, {
          busy: true,
          disabled: true,
          legacyLabel: 'Deleting...'
        });
      } catch {}
      try {
        await postJson(`/api/admin/users/${encodeURIComponent(targetUserId)}/delete`, { dryRun: false, confirm: 'DELETE_USER' });
        closePortalModal();
        // Refresh current view (users list will update).
        await loadAndRender();
        setUsersStatus('ok', 'User deleted.');
      } catch (e) {
        const msg = String(e && e.message || e || 'Delete failed');
        setUsersStatus('error', msg);
        try {
          setSettingsButtonRuntimeState(btn, {
            busy: false,
            disabled: false,
            legacyLabel: 'Delete user'
          });
        } catch {}
        throw e;
      }
    }
    if (action === 'kg-access-save') {
      const accessCtx = modalState && modalState.kind === 'access-edit' ? modalState.accessCtx : null;
      const targetUser = accessCtx && accessCtx.user ? accessCtx.user : null;
      const isRecoveryMode = Boolean(accessCtx && accessCtx.recoveryMode);
      const isSelf = isCurrentUserEmail(targetUser && targetUser.email ? targetUser.email : '');
      if (!(state.permissions && state.permissions.canWrite) && !(isRecoveryMode && isSelf)) {
        setUsersStatus('error', 'Write access is required.');
        return;
      }
      const userId = String(el.getAttribute('data-user-id') || '').trim();
      if (!userId) return;
      const grants = {};
      modalPortal.querySelectorAll('input[data-access-key]').forEach((input) => {
        const key = String(input.getAttribute('data-access-key') || '').trim();
        if (!key) return;
        if (isRecoveryMode && !ACCESS_RECOVERY_KEYS.has(key)) return;
        grants[key] = Boolean(input.checked);
      });
      const saveBtn = el;
      if (state.sharedState?.asyncFeedback?.enabled !== true) try {
        setSettingsButtonRuntimeState(saveBtn, {
          busy: true,
          disabled: true,
          legacyLabel: 'Saving...'
        });
      } catch {}
      try {
        const payload = await patchJson(`/api/admin/users/${encodeURIComponent(userId)}/access`, { grants });
        syncCurrentUserAccessInShell(payload && payload.user ? payload.user : null, payload && payload.access ? payload.access : null);
        closePortalModal();
        await loadAndRender();
        setUsersStatus('ok', 'Access permissions updated.');
      } catch (e) {
        const msg = String(e && e.message || e || 'Save failed');
        setUsersStatus('error', msg);
        try {
          setSettingsButtonRuntimeState(saveBtn, {
            busy: false,
            disabled: false,
            legacyLabel: 'Save access'
          });
        } catch {}
        throw e;
      }
    }
  };

  modalPortal.addEventListener('click', async (event) => {
    const actionEl = event?.target?.closest?.('[data-action]') || null;
    if (!actionEl) return;
    await runSettingsActivity(actionEl, () => onModalPortalClick(event));
  });

  modalPortal.addEventListener('input', (event) => {
    const t = event && event.target ? event.target : null;
    if (!t || t.id !== 'kgDeleteConfirmInput') return;
    const host = modalPortal.querySelector('[data-delete-button-host="1"]');
    if (!host) return;
    const enabled = String(t.value || '').trim() === 'DELETE_USER';
    setOuterHtml(host, renderPortalDeleteActionButton(enabled));
  });

  function getCsrfHeader() {
    const csrf = String(state.sharedState && state.sharedState.csrfToken || '').trim();
    return csrf ? { 'x-csrf-token': csrf } : {};
  }

  function setUsersStatus(kind, text) {
    const el = state.mountEl.querySelector('#usersStatus');
    if (state.usersStatusTimer) {
      try { clearTimeout(state.usersStatusTimer); } catch {}
      state.usersStatusTimer = 0;
    }
    const normalized = kind === 'error' ? 'error' : kind === 'ok' ? 'ok' : 'muted';
    const message = String(text || '');
    const suppressLegacyProgress = normalized === 'muted'
      && message.trim()
      && state.sharedState?.asyncFeedback?.enabled === true
      && settingsActivityDepth > 0;
    if (el) {
      el.className = 'kg-settings-muted';
      el.textContent = normalized === 'muted' && !suppressLegacyProgress ? message : '';
    }
    if ((normalized === 'ok' || normalized === 'error') && message.trim()) {
      openMessageModal(normalized === 'error' ? 'Settings Error' : 'Settings Update', {
        kind: normalized,
        message
      });
      state.usersStatusTimer = setTimeout(() => {
        if (!state.mounted) return;
        const el2 = state.mountEl.querySelector('#usersStatus');
        if (el2) {
          el2.className = 'kg-settings-muted';
          el2.textContent = '';
        }
        state.usersStatusTimer = 0;
      }, 200);
    }
  }

  function setMfaStatus(kind, text) {
    const el = state.mountEl.querySelector('#mfaStatus');
    const normalized = kind === 'error' ? 'error' : kind === 'ok' ? 'ok' : 'muted';
    const message = String(text || '');
    const suppressLegacyProgress = normalized === 'muted'
      && message.trim()
      && state.sharedState?.asyncFeedback?.enabled === true
      && settingsActivityDepth > 0;
    if (el) {
      el.className = 'kg-settings-muted';
      el.textContent = normalized === 'muted' && !suppressLegacyProgress ? message : '';
    }
    if ((normalized === 'ok' || normalized === 'error') && message.trim()) {
      openMessageModal(normalized === 'error' ? 'Admin MFA Error' : 'Admin MFA', {
        kind: normalized,
        message
      });
    }
  }

  function setUsageStatus(kind, text) {
    const normalized = kind === 'error' ? 'error' : kind === 'ok' ? 'ok' : 'muted';
    const message = String(text || '');
    const suppressLegacyProgress = normalized === 'muted'
      && message.trim()
      && state.sharedState?.asyncFeedback?.enabled === true
      && settingsActivityDepth > 0;
    state.usage.status.kind = normalized;
    state.usage.status.text = suppressLegacyProgress ? '' : message;
    const el = state.mountEl.querySelector('#usageStatus');
    if (state.usageStatusTimer) {
      try { clearTimeout(state.usageStatusTimer); } catch {}
      state.usageStatusTimer = 0;
    }
    if (el) {
      el.className = 'kg-settings-muted';
      el.textContent = state.usage.status.kind === 'muted' && !suppressLegacyProgress
        ? state.usage.status.text
        : '';
    }
    if ((state.usage.status.kind === 'ok' || state.usage.status.kind === 'error') && state.usage.status.text.trim()) {
      openMessageModal(state.usage.status.kind === 'error' ? 'Usage Error' : 'Usage', {
        kind: state.usage.status.kind,
        message: state.usage.status.text
      });
      state.usageStatusTimer = setTimeout(() => {
        if (!state.mounted) return;
        state.usage.status = { kind: 'muted', text: '' };
        const el2 = state.mountEl.querySelector('#usageStatus');
        if (el2) {
          el2.className = 'kg-settings-muted';
          el2.textContent = '';
        }
        state.usageStatusTimer = 0;
      }, 200);
    }
  }

  function setAppearanceStatus(kind, text) {
    const normalizedKind = kind === 'error' ? 'error' : kind === 'ok' ? 'ok' : 'muted';
    state.appearance.status = { kind: normalizedKind, text: String(text || '') };
    const el = state.mountEl.querySelector('#appearanceStatus');
    if (el) {
      el.className = 'kg-settings-appearance-status kg-settings-muted';
      el.textContent = normalizedKind === 'muted' ? state.appearance.status.text : '';
    }
    if ((normalizedKind === 'ok' || normalizedKind === 'error') && state.appearance.status.text.trim()) {
      openMessageModal(normalizedKind === 'error' ? 'Appearance Error' : 'Appearance', {
        kind: normalizedKind,
        message: state.appearance.status.text
      });
    }
  }

  function getAppearanceThemeModes() {
    const isTutorDeskApp = String(sharedState?.appId || '').trim() === 'tutor-desk';
    const defaultThemePreview = getThemeDefinition('default', DEFAULT_THEME_PACK_ID)?.preview || {};
    const darkThemePreview = getThemeDefinition('dark', DEFAULT_THEME_PACK_ID)?.preview || {};
    const defaultPreviewSurface = String(defaultThemePreview.surface || '').trim()
      || 'var(--kg-settings-surface-1, var(--kg-settings-surface-2))';
    const darkPreviewSurface = String(darkThemePreview.surface || '').trim()
      || 'var(--kg-settings-surface-0, var(--kg-settings-surface-1))';
    const defaultPreviewAccent = String(defaultThemePreview.accent || '').trim()
      || 'var(--kg-settings-accent, var(--kg-settings-primary))';
    const darkPreviewAccent = String(darkThemePreview.accent || '').trim()
      || 'var(--kg-settings-primary, var(--kg-settings-accent))';
    const defaultPreviewSecondary = String(defaultThemePreview.secondary || '').trim()
      || 'var(--kg-settings-text, var(--kg-settings-text-subtle))';
    const darkPreviewSecondary = String(darkThemePreview.secondary || '').trim()
      || 'var(--kg-settings-text-subtle, var(--kg-settings-text))';
    const modes = listThemeModes().filter((themeMode) => (
      !isTutorDeskApp || ['default', 'dark'].includes(String(themeMode.id || '').trim())
    )).map((themeMode) => {
      const themeDef = getThemeDefinition(themeMode.id, DEFAULT_THEME_PACK_ID) || {};
      const normalizedThemeId = String(themeMode.id || '').trim();
      return {
        ...themeMode,
        label: isTutorDeskApp && normalizedThemeId === 'default' ? 'Light' : themeMode.label,
        description: isTutorDeskApp && normalizedThemeId === 'default'
          ? 'Tutor Desk light mode matching the app reference.'
          : themeMode.description,
        lifecycle: String(themeDef.lifecycle || 'active').trim().toLowerCase(),
        deprecatedSince: String(themeDef.deprecatedSince || '').trim(),
        replacementThemeRef: String(themeDef.replacementThemeRef || '').trim()
      };
    });
    if (isTutorDeskApp && themePreferenceBridge) {
      modes.push({
        id: 'system',
        label: 'Follow device',
        description: 'Automatically use Light or Dark to match this device.',
        version: 1,
        lifecycle: 'active',
        preview: {
          surface: `linear-gradient(135deg, ${defaultPreviewSurface} 0 50%, ${darkPreviewSurface} 50% 100%)`,
          accent: `linear-gradient(135deg, ${defaultPreviewAccent} 0 50%, ${darkPreviewAccent} 50% 100%)`,
          secondary: `linear-gradient(135deg, ${defaultPreviewSecondary} 0 50%, ${darkPreviewSecondary} 50% 100%)`
        }
      });
    }
    return modes;
  }

  function getAppearanceThemeLabel(themeId = '') {
    if (themePreferenceBridge && String(themeId || '').trim().toLowerCase() === 'system') {
      return 'Follow device';
    }
    if (
      String(sharedState?.appId || '').trim() === 'tutor-desk'
      && String(themeId || '').trim().toLowerCase() === getDefaultThemeModeId()
    ) {
      return 'Light';
    }
    return getThemeModeDefinition(themeId).label;
  }

  function normalizeAppearanceThemeModeId(themeId = '') {
    const requested = String(themeId || '').trim().toLowerCase();
    if (themePreferenceBridge && requested === 'system') return 'system';
    return getThemeModeDefinition(resolveThemeRef({
      packId: DEFAULT_THEME_PACK_ID,
      themeId: requested || getDefaultThemeModeId()
    }).themeRef.themeId).id;
  }

  function resolveEffectiveAppearanceThemeModeId(themeId = '') {
    const requested = normalizeAppearanceThemeModeId(themeId);
    if (requested !== 'system') return requested;
    try {
      return globalThis.matchMedia?.('(prefers-color-scheme: dark)')?.matches === true
        ? 'dark'
        : getDefaultThemeModeId();
    } catch {
      return getDefaultThemeModeId();
    }
  }

  function normalizeAppearanceScopeId(scopeId = '') {
    if (themePreferenceBridge) return FULL_APP_SCOPE_ID;
    const normalizedScopeId = String(scopeId || '').trim();
    if (normalizedScopeId === SETTINGS_ONLY_SCOPE_ID) return SETTINGS_ONLY_SCOPE_ID;
    if (state.viewPolicy.appearanceScopePolicy === 'runtime-eligible' && isPlatformThemeScopeId(normalizedScopeId)) {
      return normalizedScopeId;
    }
    return SETTINGS_ONLY_SCOPE_ID;
  }

  function getPreferredAppearanceScopeId(scopeId = '') {
    const normalizedScopeId = normalizeAppearanceScopeId(scopeId);
    if (normalizedScopeId !== SETTINGS_ONLY_SCOPE_ID) return normalizedScopeId;
    if (state.viewPolicy.appearanceScopePolicy !== 'runtime-eligible') {
      return SETTINGS_ONLY_SCOPE_ID;
    }
    return canApplyThemeScope(FULL_APP_SCOPE_ID) ? FULL_APP_SCOPE_ID : SETTINGS_ONLY_SCOPE_ID;
  }

  function getAppearanceScopeDefinition(scopeId = '') {
    return resolveThemeScope(scopeId) || {
      scopeId: SETTINGS_ONLY_SCOPE_ID,
      label: 'Settings only',
      description: 'Apply a theme mode only to the shared Settings plugin.',
      priority: 100
    };
  }

  function getAppearanceUiScopes() {
    if (themePreferenceBridge) return [getAppearanceScopeDefinition(FULL_APP_SCOPE_ID)];
    if (state.viewPolicy.appearanceScopePolicy !== 'runtime-eligible') {
      return [getAppearanceScopeDefinition(SETTINGS_ONLY_SCOPE_ID)];
    }
    return listThemeScopes();
  }

  function getPersistedAppearanceThemeStateForScope(scopeId, platformThemeState = state.appearance.platformThemeState) {
    const normalizedScopeId = normalizeAppearanceScopeId(scopeId);
    if (themePreferenceBridge) {
      const serverTheme = themePreferenceBridge.read();
      const requestedThemeModeId = serverTheme?.persisted === true && serverTheme.themeModeId
        ? normalizeAppearanceThemeModeId(serverTheme.themeModeId)
        : normalizeAppearanceThemeModeId(state.appearance?.persistedThemeModeId || getDefaultThemeModeId());
      return {
        selectedScopeId: FULL_APP_SCOPE_ID,
        persistedScopeId: FULL_APP_SCOPE_ID,
        persistedThemeModeId: requestedThemeModeId,
        requestedThemeModeId,
        clamped: false
      };
    }
    if (normalizedScopeId === SETTINGS_ONLY_SCOPE_ID) {
      const localThemeState = getPersistedSettingsThemeMode();
      return {
        selectedScopeId: SETTINGS_ONLY_SCOPE_ID,
        persistedScopeId: SETTINGS_ONLY_SCOPE_ID,
        persistedThemeModeId: String(localThemeState.themeId || getDefaultThemeModeId()),
        requestedThemeModeId: String(localThemeState.requestedThemeId || localThemeState.themeId || getDefaultThemeModeId()),
        clamped: Boolean(localThemeState.clamped)
      };
    }

    const normalizedPlatformThemeState = platformThemeState || getPersistedPlatformThemeMode();
    return {
      selectedScopeId: normalizedScopeId,
      persistedScopeId: String(normalizedPlatformThemeState.scopeId || 'full-app'),
      persistedThemeModeId: String(normalizedPlatformThemeState.themeId || getDefaultThemeModeId()),
      requestedThemeModeId: String(normalizedPlatformThemeState.requestedThemeId || normalizedPlatformThemeState.themeId || getDefaultThemeModeId()),
      clamped: Boolean(normalizedPlatformThemeState.clamped)
    };
  }

  function resolveAppearanceThemeState(nextFields = {}) {
    const previousState = state.appearance || {};
    const platformThemeState = nextFields.platformThemeState || previousState.platformThemeState || getPersistedPlatformThemeMode();
    const previousSelectedScopeId = normalizeAppearanceScopeId(previousState.selectedScopeId || SETTINGS_ONLY_SCOPE_ID);
    const selectedScopeId = normalizeAppearanceScopeId(
      Object.prototype.hasOwnProperty.call(nextFields, 'selectedScopeId')
        ? nextFields.selectedScopeId
        : previousSelectedScopeId
    );
    const scopeChanged = selectedScopeId !== previousSelectedScopeId;
    const persistedForScope = getPersistedAppearanceThemeStateForScope(selectedScopeId, platformThemeState);
    const requestedAppliedTheme = Object.prototype.hasOwnProperty.call(nextFields, 'appliedThemeModeId')
        ? nextFields.appliedThemeModeId
        : (scopeChanged ? persistedForScope.persistedThemeModeId : previousState.appliedThemeModeId);
    const resolvedAppliedTheme = resolveThemeRef({
      packId: DEFAULT_THEME_PACK_ID,
      themeId: requestedAppliedTheme === 'system' ? getDefaultThemeModeId() : requestedAppliedTheme
    });
    const appliedThemeModeId = normalizeAppearanceThemeModeId(requestedAppliedTheme);

    const requestedSelectedTheme = Object.prototype.hasOwnProperty.call(nextFields, 'selectedThemeModeId')
        ? nextFields.selectedThemeModeId
        : (scopeChanged ? persistedForScope.persistedThemeModeId : previousState.selectedThemeModeId);
    const resolvedSelectedTheme = resolveThemeRef({
      packId: DEFAULT_THEME_PACK_ID,
      themeId: requestedSelectedTheme === 'system' ? getDefaultThemeModeId() : requestedSelectedTheme
    });
    const selectedThemeModeId = normalizeAppearanceThemeModeId(requestedSelectedTheme);

    let previewThemeModeId = null;
    if (Object.prototype.hasOwnProperty.call(nextFields, 'previewThemeModeId')) {
      previewThemeModeId = nextFields.previewThemeModeId == null
        ? null
        : normalizeAppearanceThemeModeId(nextFields.previewThemeModeId);
    } else if (!scopeChanged && previousState.previewThemeModeId) {
      previewThemeModeId = normalizeAppearanceThemeModeId(previousState.previewThemeModeId);
    }

    const effectiveThemeModeId = resolveEffectiveAppearanceThemeModeId(previewThemeModeId
      || appliedThemeModeId
      || persistedForScope.persistedThemeModeId
      || getDefaultThemeModeId());

    return {
      selectedScopeId,
      persistedScopeId: persistedForScope.persistedScopeId,
      selectedThemeModeId,
      persistedThemeModeId: persistedForScope.persistedThemeModeId,
      appliedThemeModeId,
      previewThemeModeId,
      effectiveThemeModeId,
      effectiveScopeId: selectedScopeId,
      platformThemeState,
      clampedWarning: (resolvedSelectedTheme.clamped || persistedForScope.clamped)
        ? `The requested theme '${persistedForScope.requestedThemeModeId || selectedThemeModeId}' is unavailable and was clamped to the default DS1 theme.`
        : ''
    };
  }

  function renderAppearanceContributionHosts() {
    const sections = Array.isArray(state.viewPolicy?.appearanceSections)
      ? state.viewPolicy.appearanceSections
      : [];
    if (sections.length === 0) return '';
    return `[#div class="kg-settings-appearance-contributions">${sections.map((descriptor) => (
      renderSettingsSubPanel({
        label: descriptor.label,
        state: 'Expanded',
        density: 'compact',
        className: 'kg-settings-appearance-contribution',
        bodyHtml: `[#div class="kg-settings-contribution-host" data-settings-appearance-section-host="${escapeHtml(descriptor.id)}" data-settings-contribution-owned="true">${renderEmpty('Loading...')}</div>`
      })
    )).join('')}</div>`;
  }

  function renderAppearancePanel() {
    const finalUi = isSettingsSaasFinalUiActive();
    const referenceV2 = isSettingsReferenceV2Active();
    const themeModes = getAppearanceThemeModes();
    const resolvedAppearance = resolveAppearanceThemeState();
    const selectedThemeModeId = String(resolvedAppearance.selectedThemeModeId || getDefaultThemeModeId());
    const appliedThemeModeId = String(resolvedAppearance.appliedThemeModeId || getDefaultThemeModeId());
    const previewThemeModeId = resolvedAppearance.previewThemeModeId
      ? String(resolvedAppearance.previewThemeModeId)
      : '';
    const visibleThemeModes = themeModes.filter((themeMode) => isThemeLifecycleSelectable(themeMode));
    const clampedNotice = resolvedAppearance.clampedWarning
      ? renderSettingsNotice({
        kind: 'Info',
        title: 'Theme selection clamped',
        message: resolvedAppearance.clampedWarning
      })
      : '';

    const cards = visibleThemeModes.map((themeMode) => {
      const active = themeMode.id === selectedThemeModeId;
      const isApplied = themeMode.id === appliedThemeModeId;
      const isPreviewing = previewThemeModeId && themeMode.id === previewThemeModeId;
      const badges = [
        isApplied ? '<span class="kg-settings-badge mfa-on">Applied</span>' : '',
        isPreviewing ? '<span class="kg-settings-badge current-user">Preview</span>' : '',
        themeMode.lifecycle === 'deprecated' ? '<span class="kg-settings-badge usage">Deprecated</span>' : ''
      ].filter(Boolean).join('');
      const deprecationHint = themeMode.lifecycle === 'deprecated'
        ? `[#div class="kg-settings-pack-meta">Deprecated since ${escapeHtml(themeMode.deprecatedSince || 'now')}${themeMode.replacementThemeRef ? ` • Use ${escapeHtml(themeMode.replacementThemeRef)}` : ''}</div>`
        : '';
      if (finalUi) {
        const normalizedThemeModeId = String(themeMode.id || '').trim();
        const appliedBadge = isApplied
          ? '<span class="kg-settings-final-theme-badge">Applied</span>'
          : (isPreviewing ? '<span class="kg-settings-final-theme-badge">Preview</span>' : '');
        return `
          <article class="kg-settings-final-theme-card ${active ? 'active' : ''}" data-theme-mode="${escapeHtml(normalizedThemeModeId)}">
            <div
              class="kg-settings-final-theme-preview"
              data-theme-mode="${escapeHtml(normalizedThemeModeId)}"
              role="img"
              aria-label="${escapeHtml(themeMode.label)} theme preview"
            >
              <span class="kg-settings-final-theme-preview__rail" aria-hidden="true">
                <span class="kg-settings-final-theme-preview__wordmark">
                  <img src="/assets/ttoria-saas-staging-wordmark-final-ui-v1.png" alt="" />
                </span>
                <span class="kg-settings-final-theme-preview__rail-line kg-settings-final-theme-preview__rail-line--active"></span>
                <span class="kg-settings-final-theme-preview__rail-line"></span>
                <span class="kg-settings-final-theme-preview__rail-line"></span>
                <span class="kg-settings-final-theme-preview__rail-line"></span>
                <span class="kg-settings-final-theme-preview__rail-line"></span>
              </span>
              <span class="kg-settings-final-theme-preview__canvas" aria-hidden="true">
                <span class="kg-settings-final-theme-preview__bar"></span>
                <span class="kg-settings-final-theme-preview__cards">
                  <span class="kg-settings-final-theme-preview__card"></span>
                  <span class="kg-settings-final-theme-preview__card"></span>
                  <span class="kg-settings-final-theme-preview__card"></span>
                </span>
              </span>
            </div>
            <div class="kg-settings-final-theme-card__heading">
              <strong>${escapeHtml(themeMode.label)}</strong>
              ${appliedBadge}
            </div>
            ${renderSettingsButton({
              label: 'Choose',
              intent: 'Secondary',
              attrs: {
                'data-action': 'appearance-pick',
                'data-theme-id': normalizedThemeModeId,
                'data-kg-semantic-action-id': 'settings-theme-choose',
                'data-td-final-action-role': 'secondary'
              }
            })}
          </article>
        `;
      }
      const surface = themeMode.preview && themeMode.preview.surface
        ? String(themeMode.preview.surface)
        : 'var(--kg-settings-surface-3, var(--kg-settings-surface-2, Canvas))';
      const accent = themeMode.preview && themeMode.preview.accent
        ? String(themeMode.preview.accent)
        : 'var(--kg-settings-accent, var(--kg-settings-primary))';
      const secondary = themeMode.preview && themeMode.preview.secondary
        ? String(themeMode.preview.secondary)
        : 'var(--kg-settings-secondary, var(--kg-settings-accent-soft, var(--kg-settings-surface-2, Canvas)))';
      return `
        [#div class="kg-settings-pack-card ${active ? 'active' : ''}">
          [#div class="kg-settings-pack-title">
            <span>${escapeHtml(themeMode.label)}</span>
            ${badges}
          </div>
          [#div class="kg-settings-pack-description">${escapeHtml(themeMode.description || '')}</div>
          ${deprecationHint}
          [#div class="kg-settings-pack-swatches">
            <span style="background:${escapeHtml(surface)};"></span>
            <span style="background:${escapeHtml(accent)};"></span>
            <span style="background:${escapeHtml(secondary)};"></span>
          </div>
          [#div>
            ${renderSettingsButton({
              label: 'Choose',
              intent: 'Secondary',
              compact: true,
              attrs: {
                'data-action': 'appearance-pick',
                'data-theme-id': String(themeMode.id || '')
              }
            })}
          </div>
        </div>
      `;
    }).join('');

    const previewPanel = renderSettingsSubPanel({
      label: finalUi ? 'Theme' : 'Theme previews',
      state: 'Expanded',
      density: 'compact',
      className: 'kg-settings-appearance-panel kg-settings-appearance-panel--previews',
      bodyHtml: `
        ${clampedNotice}
        ${finalUi ? '<p class="kg-settings-final-theme-intro">Choose your preferred theme for the application.</p>' : ''}
        [#div class="kg-settings-pack-grid kg-settings-pack-grid--appearance">${cards}</div>
        [#div class="kg-settings-appearance-inline-controls">
          [#div class="kg-settings-appearance-actions">
            ${renderSettingsButton({
              label: 'Preview',
              intent: 'Secondary',
              attrs: {
                'data-action': 'appearance-preview',
                ...(finalUi ? {
                  'data-kg-semantic-action-id': 'settings-theme-preview',
                  'data-td-final-action-role': 'secondary',
                  'data-td-final-action-size': 'secondary'
                } : {})
              }
            })}
            ${renderSettingsButton({
              label: 'Apply',
              intent: 'Primary',
              attrs: {
                'data-action': 'appearance-apply',
                ...(finalUi ? {
                  'data-kg-semantic-action-id': 'settings-theme-apply',
                  'data-td-final-action-role': 'primary',
                  'data-td-final-action-size': 'main'
                } : {})
              }
            })}
            ${renderSettingsButton({
              label: 'Reset default',
              intent: 'Secondary',
              attrs: {
                'data-action': 'appearance-reset',
                ...(finalUi ? {
                  'data-kg-semantic-action-id': 'settings-theme-reset',
                  'data-td-final-action-role': 'secondary',
                  'data-td-final-action-size': 'secondary'
                } : {})
              }
            })}
          </div>
        </div>
      `
    });

    const pageHeader = referenceV2 ? `
      <header class="kg-settings-reference-v2-page-header">
        <h1>Appearance</h1>
        <p>Personalize how Tutor Desk looks and feels for your account.</p>
      </header>
    ` : '';
    return `${pageHeader}${previewPanel}${renderAppearanceContributionHosts()}`;
  }

  function setDeleteUserStatus(kind, text, preview = null) {
    state.deleteUser.kind = kind === 'error' ? 'error' : kind === 'ok' ? 'ok' : 'muted';
    state.deleteUser.text = String(text || '');
    state.deleteUser.preview = preview;
    const el = state.mountEl.querySelector('#deleteStatus');
    if (el) {
      el.className = 'kg-settings-muted';
      el.textContent = state.deleteUser.kind === 'muted' ? state.deleteUser.text : '';
    }

    const previewEl = state.mountEl.querySelector('#deletePreview');
    if (previewEl) {
      previewEl.textContent = '';
    }

    if (preview) {
      const title = preview && preview.target
        ? `Delete user ${String(preview.target.email || preview.target.id || '')}?`
        : 'Delete user preview';
      openDeleteUserModal(title, preview);
    } else if ((state.deleteUser.kind === 'ok' || state.deleteUser.kind === 'error') && state.deleteUser.text.trim()) {
      openMessageModal(state.deleteUser.kind === 'error' ? 'Danger Zone Error' : 'Danger Zone', {
        kind: state.deleteUser.kind,
        message: state.deleteUser.text
      });
    }

    const execBtn = state.mountEl.querySelector('button[data-action="delete-user-exec"]');
    if (execBtn) {
      const confirmText = String(state.mountEl.querySelector('#deleteConfirmText')?.value || '').trim();
      execBtn.disabled = confirmText !== 'DELETE_USER' || !state.deleteUser.preview || Boolean(state.deleteUser.preview && state.deleteUser.preview.blocked);
    }
  }

  function toLocalDateTimeValue(date) {
    const pad = (n) => String(n).padStart(2, '0');
    const d = date instanceof Date ? date : new Date(date);
    if (!Number.isFinite(d.getTime())) return '';
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  function initExportDefaults() {
    const now = new Date();
    const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    state.events.export.from = toLocalDateTimeValue(from);
    state.events.export.to = toLocalDateTimeValue(now);
    state.events.export.format = 'ndjson';
    state.usage.explorer.export.from = toLocalDateTimeValue(from);
    state.usage.explorer.export.to = toLocalDateTimeValue(now);
    state.usage.explorer.export.format = 'ndjson';
  }

  function localInputToIso(value) {
    const text = String(value || '').trim();
    if (!text) return '';
    const d = new Date(text);
    if (!Number.isFinite(d.getTime())) return '';
    return d.toISOString();
  }

  function buildAuditEventsUrl({ cursor = '', limit = 50 } = {}) {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (cursor) params.set('cursor', cursor);
    const f = state.events.server.audit;
    if (f.module) params.set('module', f.module);
    if (f.action) params.set('action', f.action);
    if (f.actorEmail) params.set('actorEmail', f.actorEmail);
    if (f.from) params.set('from', f.from);
    if (f.to) params.set('to', f.to);
    return `/api/admin/audit/events?${params.toString()}`;
  }

  function buildAuthEventsUrl({ cursor = '', limit = 50 } = {}) {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (cursor) params.set('cursor', cursor);
    const f = state.events.server.auth;
    if (f.email) params.set('email', f.email);
    if (f.eventType) params.set('eventType', f.eventType);
    if (f.from) params.set('from', f.from);
    if (f.to) params.set('to', f.to);
    return `/api/admin/auth/events?${params.toString()}`;
  }

  function buildUsageEventsUrl({ cursor = '', limit = 50 } = {}) {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (cursor) params.set('cursor', cursor);
    const f = state.usage.explorer.server;
    if (f.email) params.set('email', f.email);
    if (f.module) params.set('module', f.module);
    if (f.action) params.set('action', f.action);
    if (f.from) params.set('from', f.from);
    if (f.to) params.set('to', f.to);
    return `/api/admin/usage/events?${params.toString()}`;
  }

    function renderEventsList(kind, items) {
      const q = String(state.events.search || '').trim().toLowerCase();
      const filtered = q
        ? items.filter((it) => JSON.stringify(it || {}).toLowerCase().includes(q))
        : items;
      if (!filtered.length) return renderEmpty('No events.');

      if (kind === 'audit') {
        const rows = filtered.map((e) => {
          const eventId = String(e.id || '');
          const time = formatDateTime(e.created_at || '');
          const actor = String(e.actor_email || '');
          const action = `${String(e.module || '')} / ${String(e.action || '')}`;
          const res = `${String(e.resource_type || '')}${e.resource_id ? `:${String(e.resource_id)}` : ''}`;
          const req = String(e.request_id || '');
          return `
            [#tr>
              [#td>${escapeHtml(time)}</td>
              [#td>${escapeHtml(actor)}</td>
              [#td>${escapeHtml(action)}</td>
              [#td>${escapeHtml(res)}</td>
              [#td class="mono">${escapeHtml(req)}</td>
              [#td style="text-align:right;">
                ${renderSettingsButton({
                  label: 'Details',
                  intent: 'Secondary',
                  compact: true,
                  attrs: {
                    'data-action': 'event-details',
                    'data-kind': 'audit',
                    'data-id': eventId
                  }
                })}
              </td>
            </tr>
          `;
        }).join('');
        return renderTable({
          id: 'audit-events-table',
          headers: ['Time', 'Actor', 'Module / Action', 'Resource', 'Request', ''],
          bodyHtml: rows,
          minWidth: '1200px'
        });
      }

      const rows = filtered.map((e) => {
        const eventId = String(e.id || '');
        const time = formatDateTime(e.created_at || '');
        const email = String(e.email || '');
        const type = String(e.event_type || '');
        const ip = String(e.ip_address || '');
        const req = String(e.request_id || '');
        return `
          [#tr>
            [#td>${escapeHtml(time)}</td>
            [#td>${escapeHtml(email)}</td>
            [#td class="mono">${escapeHtml(type)}</td>
            [#td class="mono">${escapeHtml(ip)}</td>
            [#td class="mono">${escapeHtml(req)}</td>
            [#td style="text-align:right;">
              ${renderSettingsButton({
                label: 'Details',
                intent: 'Secondary',
                compact: true,
                attrs: {
                  'data-action': 'event-details',
                  'data-kind': 'auth',
                  'data-id': eventId
                }
              })}
            </td>
          </tr>
        `;
      }).join('');
      return renderTable({
        id: 'auth-events-table',
        headers: ['Time', 'Email', 'Event', 'IP', 'Request', ''],
        bodyHtml: rows,
        minWidth: '1200px'
      });
    }

  function renderEventsExplorer() {
    const tab = state.events.tab;
    const audit = state.events.audit;
    const auth = state.events.auth;
    const more = Boolean(state.events.moreFilters);
    const exportFrom = state.events.export.from || '';
    const exportTo = state.events.export.to || '';
    const exportFormat = String(state.events.export.format || 'ndjson');

    const serverAudit = state.events.server.audit;
    const serverAuth = state.events.server.auth;

    const activeItems = tab === 'audit' ? audit.items : auth.items;
    const nextCursor = tab === 'audit' ? audit.nextCursor : auth.nextCursor;
    const filterPanelHtml = more ? renderSettingsSubPanel({
      label: 'Server filters',
      density: 'compact',
      className: 'kg-settings-audit-panel kg-settings-audit-panel--filters',
      bodyHtml: `
        [#div class="kg-settings-audit-filter-grid">
          [#div class="kg-settings-audit-field">
            <label class="kg-settings-label" for="eventsFrom">From</label>
            ${renderSettingsDateTimeField({ id: 'eventsFrom', value: toLocalDateTimeValue(serverAudit.from || serverAuth.from || ''), placeholder: 'From', triggerClass: 'kg-settings-input mini', minWidth: 280 })}
          </div>
          [#div class="kg-settings-audit-field">
            <label class="kg-settings-label" for="eventsTo">To</label>
            ${renderSettingsDateTimeField({ id: 'eventsTo', value: toLocalDateTimeValue(serverAudit.to || serverAuth.to || ''), placeholder: 'To', triggerClass: 'kg-settings-input mini', minWidth: 280 })}
          </div>
          ${tab === 'audit' ? `
            [#div class="kg-settings-audit-field">
              <label class="kg-settings-label" for="eventsActorEmail">Actor email</label>
              ${renderSettingsInput({
                label: '',
                helperText: '',
                size: 'md',
                inputId: 'eventsActorEmail',
                value: serverAudit.actorEmail,
                placeholder: 'actor email',
                inputAttrs: { type: 'text' }
              })}
            </div>
            [#div class="kg-settings-audit-field">
              <label class="kg-settings-label" for="eventsModule">Module</label>
              ${renderSettingsInput({
                label: '',
                helperText: '',
                size: 'sm',
                inputId: 'eventsModule',
                value: serverAudit.module,
                placeholder: 'module',
                inputAttrs: { type: 'text' }
              })}
            </div>
            [#div class="kg-settings-audit-field">
              <label class="kg-settings-label" for="eventsAction">Action</label>
              ${renderSettingsInput({
                label: '',
                helperText: '',
                size: 'sm',
                inputId: 'eventsAction',
                value: serverAudit.action,
                placeholder: 'action',
                inputAttrs: { type: 'text' }
              })}
            </div>
            [#div class="kg-settings-audit-filter-actions">
              ${renderSettingsButton({
                label: 'Apply',
                intent: 'Primary',
                attrs: { 'data-action': 'events-apply-filters' }
              })}
              ${renderSettingsButton({
                label: 'Reset',
                intent: 'Secondary',
                attrs: { 'data-action': 'events-reset-filters' }
              })}
            </div>
          ` : `
            [#div class="kg-settings-audit-field">
              <label class="kg-settings-label" for="eventsEmail">Email</label>
              ${renderSettingsInput({
                label: '',
                helperText: '',
                size: 'md',
                inputId: 'eventsEmail',
                value: serverAuth.email,
                placeholder: 'email',
                inputAttrs: { type: 'text' }
              })}
            </div>
            [#div class="kg-settings-audit-field">
              <label class="kg-settings-label" for="eventsEventType">Event type</label>
              ${renderSettingsInput({
                label: '',
                helperText: '',
                size: 'sm',
                inputId: 'eventsEventType',
                value: serverAuth.eventType,
                placeholder: 'eventType',
                inputAttrs: { type: 'text' }
              })}
            </div>
            [#div class="kg-settings-audit-filter-actions">
              ${renderSettingsButton({
                label: 'Apply',
                intent: 'Primary',
                attrs: { 'data-action': 'events-apply-filters' }
              })}
              ${renderSettingsButton({
                label: 'Reset',
                intent: 'Secondary',
                attrs: { 'data-action': 'events-reset-filters' }
              })}
            </div>
          `}
        </div>
      `
    }) : '';

    const exportHtml = renderSettingsSubPanel({
      label: 'Export',
      density: 'compact',
      className: 'kg-settings-audit-panel kg-settings-audit-panel--export',
      bodyHtml: `
        [#div class="kg-settings-audit-export-grid">
          [#div class="kg-settings-audit-export-field">
            <label class="kg-settings-label" for="exportFrom">From</label>
            ${renderSettingsDateTimeField({ id: 'exportFrom', value: exportFrom, placeholder: 'From', triggerClass: 'kg-settings-input mini', minWidth: 280 })}
          </div>
          [#div class="kg-settings-audit-export-field">
            <label class="kg-settings-label" for="exportTo">To</label>
            ${renderSettingsDateTimeField({ id: 'exportTo', value: exportTo, placeholder: 'To', triggerClass: 'kg-settings-input mini', minWidth: 280 })}
          </div>
          [#div class="kg-settings-audit-export-field">
            <label class="kg-settings-label" for="exportFormat">Format</label>
            [#div id="exportFormatDsHost" class="kg-settings-inline-ds-host"></div>
          </div>
          [#div class="kg-settings-audit-export-field kg-settings-audit-export-field--action">
            ${renderSettingsButton({
              label: 'Download',
              intent: 'Secondary',
              attrs: { 'data-action': 'events-download' }
            })}
          </div>
        </div>
      `
    });

    return `
      [#div class="kg-settings-audit-layout">
        [#div class="kg-settings-audit-toolbar">
          [#div class="kg-settings-audit-search">
            ${renderSettingsInput({
              kind: 'search',
              label: '',
              helperText: '',
              size: 'md',
              inputId: 'eventsSearch',
              value: state.events.search,
              placeholder: 'Search loaded events...',
              inputAttrs: { autocomplete: 'off' }
            })}
          </div>
          ${renderSettingsTabs({
            id: 'eventsTabToggle',
            size: 'sm',
            className: 'kg-settings-audit-tabs',
            activeId: tab,
            tabs: [
              { id: 'audit', label: 'Audit', attrs: { 'data-action': 'events-tab', 'data-tab': 'audit' } },
              { id: 'auth', label: 'Auth', attrs: { 'data-action': 'events-tab', 'data-tab': 'auth' } }
            ]
          })}
          [#div class="kg-settings-audit-button-slot">
            ${renderSettingsButton({
              label: more ? 'Hide filters' : 'More filters',
              intent: 'Secondary',
              attrs: { 'data-action': 'events-toggle-filters' }
            })}
          </div>
          [#div class="kg-settings-audit-button-slot">
            ${nextCursor ? renderSettingsButton({
              label: 'Load more',
              intent: 'Secondary',
              attrs: { 'data-action': 'events-load-more' }
            }) : ''}
          </div>
        </div>
        [#div class="kg-settings-audit-control-rail${more ? '' : ' is-compact'}">
          ${more ? filterPanelHtml : ''}
          ${exportHtml}
        </div>
        [#div class="kg-settings-audit-table-region">
          [#div id="eventsList" class="kg-settings-audit-table-host">${renderEventsList(tab, activeItems)}</div>
        </div>
      </div>
    `;
  }

  function renderUsageEventsList(items) {
    const q = String(state.usage.explorer.search || '').trim().toLowerCase();
    const filtered = q
      ? items.filter((it) => JSON.stringify(it || {}).toLowerCase().includes(q))
      : items;
    if (!filtered.length) return renderEmpty('No usage events.');

    const rows = filtered.map((e) => {
      const eventId = String(e.id || '');
      const time = formatDateTime(e.created_at || '');
      const email = String(e.user_email || '');
      const action = `${String(e.module || '')} / ${String(e.action || '')}`;
      const req = String(e.request_id || '');
      return `
        [#tr>
          [#td>${escapeHtml(time)}</td>
          [#td>${escapeHtml(email)}</td>
          [#td>${escapeHtml(action)}</td>
          [#td class="mono">${escapeHtml(req)}</td>
          [#td style="text-align:right;">
            ${renderSettingsButton({
              label: 'Details',
              intent: 'Secondary',
              compact: true,
              attrs: {
                'data-action': 'usage-details',
                'data-id': eventId
              }
            })}
          </td>
        </tr>
      `;
    }).join('');

    return renderTable({
      id: 'usage-events-table',
      headers: ['Time', 'Email', 'Module / Action', 'Request', ''],
      bodyHtml: rows,
      minWidth: '1100px'
    });
  }

  function renderUsageExplorer() {
    const ex = state.usage.explorer;
    const more = Boolean(ex.moreFilters);
    const exportFrom = ex.export.from || '';
    const exportTo = ex.export.to || '';
    const f = ex.server;
    const filterPanelHtml = more ? renderSettingsSubPanel({
      label: 'Server filters',
      density: 'compact',
      className: 'kg-settings-usage-panel kg-settings-usage-panel--filters',
      bodyHtml: `
        [#div class="kg-settings-usage-filter-grid">
          [#div class="kg-settings-usage-field">
            <label class="kg-settings-label" for="usageEventsFrom">From</label>
            ${renderSettingsDateTimeField({ id: 'usageEventsFrom', value: toLocalDateTimeValue(f.from || ''), placeholder: 'From', triggerClass: 'kg-settings-input mini', minWidth: 320 })}
          </div>
          [#div class="kg-settings-usage-field">
            <label class="kg-settings-label" for="usageEventsTo">To</label>
            ${renderSettingsDateTimeField({ id: 'usageEventsTo', value: toLocalDateTimeValue(f.to || ''), placeholder: 'To', triggerClass: 'kg-settings-input mini', minWidth: 320 })}
          </div>
          [#div class="kg-settings-usage-field">
            <label class="kg-settings-label" for="usageEventsEmail">Email</label>
            ${renderSettingsInput({
              label: '',
              helperText: '',
              size: 'md',
              inputId: 'usageEventsEmail',
              value: f.email,
              placeholder: 'email',
              inputAttrs: { type: 'text' }
            })}
          </div>
          [#div class="kg-settings-usage-field">
            <label class="kg-settings-label" for="usageEventsModule">Module</label>
            ${renderSettingsInput({
              label: '',
              helperText: '',
              size: 'sm',
              inputId: 'usageEventsModule',
              value: f.module,
              placeholder: 'module',
              inputAttrs: { type: 'text' }
            })}
          </div>
          [#div class="kg-settings-usage-field">
            <label class="kg-settings-label" for="usageEventsAction">Action</label>
            ${renderSettingsInput({
              label: '',
              helperText: '',
              size: 'sm',
              inputId: 'usageEventsAction',
              value: f.action,
              placeholder: 'action',
              inputAttrs: { type: 'text' }
            })}
          </div>
          [#div class="kg-settings-usage-filter-actions">
            ${renderSettingsButton({
              label: 'Apply',
              intent: 'Primary',
              attrs: { 'data-action': 'usage-apply-filters' }
            })}
            ${renderSettingsButton({
              label: 'Reset',
              intent: 'Secondary',
              attrs: { 'data-action': 'usage-reset-filters' }
            })}
          </div>
        </div>
      `
    }) : '';

    const exportHtml = renderSettingsSubPanel({
      label: 'Export',
      density: 'compact',
      className: 'kg-settings-usage-panel kg-settings-usage-panel--export',
      bodyHtml: `
        [#div class="kg-settings-usage-export-grid">
          [#div class="kg-settings-usage-export-field">
            <label class="kg-settings-label" for="usageExportFrom">From</label>
            ${renderSettingsDateTimeField({ id: 'usageExportFrom', value: exportFrom, placeholder: 'From', triggerClass: 'kg-settings-input mini', minWidth: 280 })}
          </div>
          [#div class="kg-settings-usage-export-field">
            <label class="kg-settings-label" for="usageExportTo">To</label>
            ${renderSettingsDateTimeField({ id: 'usageExportTo', value: exportTo, placeholder: 'To', triggerClass: 'kg-settings-input mini', minWidth: 280 })}
          </div>
          [#div class="kg-settings-usage-export-field">
            <label class="kg-settings-label" for="usageExportFormat">Format</label>
            [#div id="usageExportFormatDsHost" class="kg-settings-inline-ds-host"></div>
          </div>
          [#div class="kg-settings-usage-export-field kg-settings-usage-export-field--action">
            ${renderSettingsButton({
              label: 'Download',
              intent: 'Secondary',
              attrs: { 'data-action': 'usage-download' }
            })}
          </div>
        </div>
      `
    });

    return `
      [#div class="kg-settings-usage-control-rail${more ? '' : ' is-compact'}">
        ${more ? filterPanelHtml : ''}
        ${exportHtml}
      </div>
      [#div class="kg-settings-usage-table-region">
        [#div id="usageEventsList" class="kg-settings-usage-table-host">${renderUsageEventsList(ex.items)}</div>
      </div>
    `;
  }

  async function loadEventsInitial(signal) {
    const [auditPayload, authPayload] = await Promise.all([
      fetchJson(buildAuditEventsUrl({ limit: 50 }), { signal }),
      fetchJson(buildAuthEventsUrl({ limit: 50 }), { signal })
    ]);
    state.events.audit.items = Array.isArray(auditPayload && auditPayload.events) ? auditPayload.events : [];
    state.events.audit.nextCursor = String(auditPayload && auditPayload.nextCursor || '') || null;
    state.events.auth.items = Array.isArray(authPayload && authPayload.events) ? authPayload.events : [];
    state.events.auth.nextCursor = String(authPayload && authPayload.nextCursor || '') || null;
  }

  async function loadMoreEvents() {
    const tab = state.events.tab;
    if (tab === 'audit') {
      const cursor = state.events.audit.nextCursor || '';
      if (!cursor) return;
      const payload = await fetchJson(buildAuditEventsUrl({ limit: 50, cursor }), {});
      const items = Array.isArray(payload && payload.events) ? payload.events : [];
      state.events.audit.items = state.events.audit.items.concat(items);
      state.events.audit.nextCursor = String(payload && payload.nextCursor || '') || null;
      return;
    }
    const cursor = state.events.auth.nextCursor || '';
    if (!cursor) return;
    const payload = await fetchJson(buildAuthEventsUrl({ limit: 50, cursor }), {});
    const items = Array.isArray(payload && payload.events) ? payload.events : [];
    state.events.auth.items = state.events.auth.items.concat(items);
    state.events.auth.nextCursor = String(payload && payload.nextCursor || '') || null;
  }

  async function loadUsageEventsInitial(signal) {
    const payload = await fetchJson(buildUsageEventsUrl({ limit: 50 }), { signal });
    state.usage.explorer.items = Array.isArray(payload && payload.events) ? payload.events : [];
    state.usage.explorer.nextCursor = String(payload && payload.nextCursor || '') || null;
  }

  async function loadMoreUsageEvents() {
    const cursor = state.usage.explorer.nextCursor || '';
    if (!cursor) return;
    const payload = await fetchJson(buildUsageEventsUrl({ limit: 50, cursor }), {});
    const items = Array.isArray(payload && payload.events) ? payload.events : [];
    state.usage.explorer.items = state.usage.explorer.items.concat(items);
    state.usage.explorer.nextCursor = String(payload && payload.nextCursor || '') || null;
  }

  function rerenderUsageOnly() {
    if (!state.mounted) return;
    const usageEl = state.mountEl.querySelector('#settingsUsage');
    if (!usageEl) return;
    destroyInlineSelectDsFlow();
    preserveFocusAndScroll(usageEl, () => {
      setElementHtml(usageEl, renderUsagePanel(state.usage.summaryPayload));
    });
    mountInlineSelectDsFlow();
  }

  function rerenderAppearanceOnly() {
    if (!state.mounted) return;
    const appearanceEl = state.mountEl.querySelector('#settingsAppearance');
    if (!appearanceEl) return;
    destroyAppearanceDsFlow();
    unmountAppearanceContributionSurfaces();
    preserveFocusAndScroll(appearanceEl, () => {
      setElementHtml(appearanceEl, renderAppearancePanel());
    });
    mountAppearanceDsFlow();
    void mountAppearanceContributions();
  }

  function syncAppearanceThemeState(nextFields = {}) {
    const resolved = resolveAppearanceThemeState(nextFields);
    state.appearance.selectedScopeId = resolved.effectiveScopeId;
    state.appearance.persistedScopeId = resolved.persistedScopeId;
    state.appearance.selectedThemeModeId = resolved.selectedThemeModeId;
    state.appearance.persistedThemeModeId = resolved.persistedThemeModeId;
    state.appearance.appliedThemeModeId = resolved.appliedThemeModeId;
    state.appearance.previewThemeModeId = resolved.previewThemeModeId;
    state.appearance.platformThemeState = resolved.platformThemeState;
    state.appearance.clampedWarning = resolved.clampedWarning || '';
    return resolved;
  }

  function destroyAppearanceDsFlow() {
    const dsFlow = state.appearance && state.appearance.dsFlow;
    if (!dsFlow) return;
    if (Array.isArray(dsFlow.cleanup)) {
      dsFlow.cleanup.forEach((fn) => {
        try { fn(); } catch {}
      });
    }
    ['scopeSelect', 'themeSelect'].forEach((key) => {
      const controller = dsFlow[key];
      if (controller && typeof controller.destroy === 'function') {
        try { controller.destroy(); } catch {}
      }
    });
    state.appearance.dsFlow = null;
  }

  function destroyInlineSelectDsFlow() {
    const dsFlow = state.inlineSelectDsFlow;
    if (!dsFlow || typeof dsFlow !== 'object') return;
    Object.values(dsFlow).forEach((controller) => {
      if (controller && typeof controller.destroy === 'function') {
        try { controller.destroy(); } catch {}
      }
    });
    state.inlineSelectDsFlow = null;
  }

  function mountInlineSelectDsFlow() {
    if (!state.mounted || !state.mountEl) return;
    const currentInviteRole = String(state.mountEl.querySelector('#inviteRole')?.value || 'user').trim().toLowerCase();
    const currentExportFormat = String(state.mountEl.querySelector('#exportFormat')?.value || state.events.export.format || 'ndjson').trim().toLowerCase();
    const currentUsageExportFormat = String(state.mountEl.querySelector('#usageExportFormat')?.value || state.usage.explorer.export.format || 'ndjson').trim().toLowerCase();
    destroyInlineSelectDsFlow();

    const configs = [
      {
        key: 'inviteRole',
        hostId: 'inviteRoleDsHost',
        selectId: 'inviteRole',
        size: 'md',
        value: currentInviteRole === 'admin' ? 'admin' : 'user',
        options: [
          { value: 'user', label: 'User' },
          { value: 'admin', label: 'Admin' }
        ]
      },
      {
        key: 'exportFormat',
        hostId: 'exportFormatDsHost',
        selectId: 'exportFormat',
        size: 'sm',
        value: currentExportFormat === 'csv' ? 'csv' : 'ndjson',
        options: [
          { value: 'ndjson', label: 'NDJSON' },
          { value: 'csv', label: 'CSV' }
        ]
      },
      {
        key: 'usageExportFormat',
        hostId: 'usageExportFormatDsHost',
        selectId: 'usageExportFormat',
        size: 'sm',
        value: currentUsageExportFormat === 'csv' ? 'csv' : 'ndjson',
        options: [
          { value: 'ndjson', label: 'NDJSON' },
          { value: 'csv', label: 'CSV' }
        ]
      }
    ];

    const dsFlow = {};

    configs.forEach((config) => {
      const host = state.mountEl.querySelector(`#${cssEscape(config.hostId)}`);
      if (!host) return;
      const controller = createSettingsSelectController({
        id: `${config.hostId}Control`,
        selectId: config.selectId,
        label: '',
        helperText: '',
        size: config.size,
        options: config.options,
        value: config.value
      });
      controller.mount(host);
      dsFlow[config.key] = controller;
    });

    state.inlineSelectDsFlow = dsFlow;
  }

  function mountAppearanceDsFlow() {
    if (!state.mounted) return;
    state.appearance.dsFlow = null;
  }

  function rerenderAuditOnly() {
    if (!state.mounted) return;
    const auditEl = state.mountEl.querySelector('#settingsAudit');
    if (!auditEl) return;
    destroyInlineSelectDsFlow();
    preserveFocusAndScroll(auditEl, () => {
      setElementHtml(auditEl, renderEventsExplorer());
    });
    mountInlineSelectDsFlow();
  }

  function cssEscape(value) {
    // CSS.escape is not available in some older runtimes; keep it simple for ids we control.
    if (typeof CSS !== 'undefined' && CSS && typeof CSS.escape === 'function') return CSS.escape(value);
    return String(value || '').replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function preserveFocusAndScroll(containerEl, updateFn) {
    if (!containerEl || typeof updateFn !== 'function') return;
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    const activeId = active && active.id ? String(active.id) : '';
    const shouldRestoreFocus = Boolean(activeId && active && containerEl.contains(active));
    let selStart = null;
    let selEnd = null;
    if (shouldRestoreFocus) {
      try {
        if (typeof active.selectionStart === 'number') selStart = active.selectionStart;
        if (typeof active.selectionEnd === 'number') selEnd = active.selectionEnd;
      } catch {}
    }

    const scrollTop = typeof containerEl.scrollTop === 'number' ? containerEl.scrollTop : 0;
    updateFn();
    try { containerEl.scrollTop = scrollTop; } catch {}

    if (!shouldRestoreFocus) return;
    const next = containerEl.querySelector(`#${cssEscape(activeId)}`);
    if (!next || typeof next.focus !== 'function') return;
    try { next.focus({ preventScroll: true }); } catch { try { next.focus(); } catch {} }
    if (selStart == null || selEnd == null) return;
    try {
      if (typeof next.setSelectionRange === 'function') next.setSelectionRange(selStart, selEnd);
    } catch {}
  }

  function computeFilteredUsers(users) {
    const q = String(state.filters.query || '').trim().toLowerCase();
    let filtered = Array.isArray(users) ? users : [];
    if (q) {
      filtered = filtered.filter((u) => String(u.email || '').toLowerCase().includes(q));
    }
    if (state.filters.adminsOnly) {
      filtered = filtered.filter((u) => String(u.role || '').toLowerCase() === 'admin');
    }
    if (state.filters.mfaOffOnly) {
      filtered = filtered.filter((u) => {
        const isAdmin = String(u.role || '').toLowerCase() === 'admin';
        const mfaEnabled = Number(u.mfa_enabled || 0) === 1;
        return isAdmin && !mfaEnabled;
      });
    }
    return filtered;
  }

  function buildUserActionButtons(user, options = {}) {
    const normalized = options && typeof options === 'object' ? options : {};
    const compact = normalized.compact !== false;
    const canWrite = Boolean(state.permissions && state.permissions.canWrite);
    const canDelete = Boolean(state.permissions && state.permissions.canDelete);
    const userId = String(user && user.id || '');
    const emailRaw = String(user && user.email || '');
    const disabled = String(user && user.status || '').toLowerCase() === 'disabled';
    const isAdmin = String(user && user.role || '').toLowerCase() === 'admin';
    const mfaEnabled = Number(user && user.mfa_enabled || 0) === 1;
    const isLegacy = !String(emailRaw || '').includes('@');
    const isSelf = isCurrentUserEmail(emailRaw);
    const actionButtons = [];

    if (canWrite) {
      actionButtons.push(renderSettingsButton({
        label: 'Access',
        intent: 'Primary',
        compact,
        attrs: {
          'data-action': 'edit-access',
          'data-user-id': userId,
          'data-email': emailRaw
        }
      }));
      actionButtons.push(renderSettingsButton({
        label: 'Send setup',
        intent: 'Primary',
        compact,
        attrs: {
          'data-action': 'send-setup',
          'data-user-id': userId,
          'data-email': emailRaw
        }
      }));
      actionButtons.push(renderSettingsButton({
        label: disabled ? 'Enable' : 'Disable',
        intent: 'Primary',
        compact,
        attrs: {
          'data-action': disabled ? 'enable' : 'disable',
          'data-user-id': userId,
          'data-email': emailRaw
        }
      }));
      actionButtons.push(renderSettingsButton({
        label: isAdmin ? 'Make user' : 'Make admin',
        intent: 'Primary',
        compact,
        attrs: {
          'data-action': isAdmin ? 'make-user' : 'make-admin',
          'data-user-id': userId,
          'data-email': emailRaw
        }
      }));
      if (isAdmin) {
        actionButtons.push(renderSettingsButton({
          label: 'Reset MFA',
          intent: 'Danger',
          compact,
          disabled: !mfaEnabled,
          attrs: {
            'data-action': 'mfa-reset',
            'data-user-id': userId,
            'data-email': emailRaw,
            title: mfaEnabled ? 'Reset MFA for this admin' : 'MFA is not enabled'
          }
        }));
      }
      actionButtons.push(renderSettingsButton({
        label: 'Revoke sessions',
        intent: 'Danger',
        compact,
        attrs: {
          'data-action': 'revoke-sessions',
          'data-user-id': userId,
          'data-email': emailRaw
        }
      }));
    }
    if (!canWrite && isSelf) {
      actionButtons.push(renderSettingsButton({
        label: 'Access recovery',
        intent: 'Primary',
        compact,
        attrs: {
          'data-action': 'edit-access',
          'data-user-id': userId,
          'data-email': emailRaw
        }
      }));
    }
    if (canDelete && isLegacy) {
      actionButtons.push(renderSettingsButton({
        label: 'Delete user',
        intent: 'Danger',
        compact,
        attrs: {
          'data-action': 'delete-user-by-id',
          'data-user-id': userId,
          'data-email': emailRaw
        }
      }));
    }
    return actionButtons;
  }

  function renderUsersList(users) {
    const filtered = computeFilteredUsers(users);
    if (!filtered.length) {
      return { html: renderEmpty('No users.'), count: 0 };
    }

    const rows = filtered.map((user) => {
      const userId = String(user.id || '');
      const emailRaw = String(user.email || '');
      const email = escapeHtml(emailRaw);
      const roleLabel = String(user.role || 'user').toUpperCase();
      const statusRaw = String(user.status || 'active');
      const statusLabel = escapeHtml(statusRaw);
      const disabled = statusRaw.toLowerCase() === 'disabled';
      const isAdmin = String(user.role || '').toLowerCase() === 'admin';
      const mfaEnabled = Number(user.mfa_enabled || 0) === 1;
      const isLegacy = !String(emailRaw || '').includes('@');
      const isCurrentUser = isCurrentUserEmail(emailRaw);

      const roleBadges = [
        `<span class="kg-settings-badge ${isAdmin ? 'admin' : ''}">${escapeHtml(isAdmin ? 'ADMIN' : roleLabel)}</span>`,
        isAdmin ? `<span class="kg-settings-badge ${mfaEnabled ? 'mfa-on' : 'mfa-off'}">${mfaEnabled ? 'MFA ON' : 'MFA OFF'}</span>` : '',
        isLegacy ? `<span class="kg-settings-badge mfa-off">LEGACY</span>` : ''
      ].filter(Boolean).join('');

      const mfaCell = isAdmin
        ? `<span class="kg-settings-badge ${mfaEnabled ? 'mfa-on' : 'mfa-off'}">${mfaEnabled ? 'Enabled' : 'Off'}</span>`
        : '<span class="kg-settings-muted">-</span>';
      const statusBadges = [
        `<span class="kg-settings-badge ${disabled ? 'mfa-off' : 'status-active'}">${disabled ? 'DISABLED' : statusLabel.toUpperCase()}</span>`,
        isCurrentUser ? '<span class="kg-settings-badge current-user">CURRENT USER</span>' : ''
      ].filter(Boolean).join('');

      const actionButtons = buildUserActionButtons(user);
      const actions = actionButtons.length
        ? renderSettingsButton({
            label: 'Actions',
            intent: 'Primary',
            compact: true,
            attrs: {
              'data-action': 'user-row-actions',
              'data-user-id': userId,
              'data-email': emailRaw
            }
          })
        : '<span class="kg-settings-muted">Read-only</span>';

      return `
        [#tr>
          [#td>
            [#div class="kg-settings-user-email">${email}</div>
          </td>
          [#td>[#div class="kg-settings-user-role">${roleBadges}</div></td>
          [#td>[#div class="kg-settings-user-role">${statusBadges}</div></td>
          [#td>${mfaCell}</td>
          [#td class="kg-settings-table__cell--actions">${actions}</td>
        </tr>
      `;
    }).join('');

    const listHtml = renderTable({
      id: 'users-table',
      headers: ['Email', 'Role', 'Status', 'MFA', 'Actions'],
      bodyHtml: rows,
      minWidth: '1200px',
      headerVariant: 'bandedDark',
      className: 'kg-settings-table--users'
    });

    return {
      html: listHtml,
      count: filtered.length
    };
  }

  function renderInviteAndUsers(users = []) {
    const canWrite = Boolean(state.permissions && state.permissions.canWrite);
    const canDelete = Boolean(state.permissions && state.permissions.canDelete);
    const items = Array.isArray(users) ? users : [];
    state.users = items;
    state.usersByEmail = new Set(items.map((u) => String(u.email || '').toLowerCase()));
    const { html: listHtml, count } = renderUsersList(items);
    const isAdminSession = String(state.sessionInfo && state.sessionInfo.role || '').toLowerCase() === 'admin';
    const userPanelsHtml = `
      ${isAdminSession ? renderMfaCard() : ''}
      ${canWrite ? renderSettingsSubPanel({
        title: 'Invite user',
        label: 'Invite user',
        state: 'Expanded',
        density: 'compact',
        className: 'kg-settings-users-panel kg-settings-users-panel--invite',
        bodyHtml: `
          <form id="inviteForm" class="kg-settings-users-stack">
            [#div class="kg-settings-users-form-grid">
              [#div class="kg-settings-users-field">
                ${renderSettingsInput({
                  label: '',
                  helperText: '',
                  size: 'md',
                  inputId: 'inviteEmail',
                  placeholder: 'Enter Email here',
                  inputAttrs: {
                    type: 'email',
                    name: 'inviteEmail',
                    autocomplete: 'email',
                    required: true
                  }
                })}
              </div>
              [#div class="kg-settings-users-action">
                ${renderSettingsButton({
                  id: 'inviteBtn',
                  label: 'Send Invite',
                  intent: 'Primary',
                  type: 'submit',
                  ...(isSettingsSaasFinalUiActive()
                    ? { attrs: { 'data-action': 'invite-submit' } }
                    : {})
                })}
              </div>
              [#div class="kg-settings-users-field kg-settings-users-field--span-2">
                [#div id="inviteRoleDsHost" class="kg-settings-inline-ds-host"></div>
              </div>
            </div>
          </form>
        `
      }) : renderSettingsSubPanel({
        title: 'Invite user',
        label: 'Invite user',
        state: 'Expanded',
        density: 'compact',
        className: 'kg-settings-users-panel kg-settings-users-panel--invite',
        bodyHtml: renderSettingsNotice({
          kind: 'warning',
          className: 'kg-settings-notice-compact',
          message: 'Read-only access. Inviting users requires module.settings.write.'
        })
      })}

      ${canDelete ? renderSettingsSubPanel({
        title: 'Danger zone',
        label: 'Danger zone',
        state: 'Expanded',
        density: 'compact',
        className: 'kg-settings-users-panel kg-settings-users-panel--danger',
        bodyHtml: `
          [#div class="kg-settings-users-stack">
            [#div class="kg-settings-users-form-grid">
            [#div class="kg-settings-users-field">
              ${renderSettingsInput({
                label: '',
                helperText: '',
                size: 'md',
                inputId: 'deleteUserEmail',
                value: state.deleteUser.email ? state.deleteUser.email : undefined,
                placeholder: 'Enter Email here',
                inputAttrs: { type: 'email' }
              })}
            </div>
            [#div class="kg-settings-users-action">
              ${renderSettingsButton({
                label: 'Preview',
                intent: 'Danger',
                attrs: { 'data-action': 'delete-user-preview' }
              })}
            </div>
              [#div class="kg-settings-users-field">
                ${renderSettingsInput({
                  label: '',
                  helperText: '',
                  size: 'md',
                  inputId: 'deleteConfirmText',
                  value: state.deleteUser.confirm ? state.deleteUser.confirm : undefined,
                  placeholder: 'Enter Email here',
                  inputAttrs: { type: 'text' }
                })}
              </div>
            [#div class="kg-settings-users-action">
              ${renderSettingsButton({
                label: 'Delete',
                intent: 'Danger',
                disabled: true,
                attrs: { 'data-action': 'delete-user-exec' }
              })}
            </div>
            </div>
          </div>
        `
      }) : renderSettingsSubPanel({
        title: 'Danger zone',
        label: 'Danger zone',
        state: 'Expanded',
        density: 'compact',
        className: 'kg-settings-users-panel kg-settings-users-panel--danger',
        bodyHtml: renderSettingsNotice({
          kind: 'warning',
          className: 'kg-settings-notice-compact',
          message: 'Delete actions are disabled (module.settings.delete is off).'
        })
      })}
    `;

    return `
      [#div class="kg-settings-users-layout">
        [#div class="kg-settings-users-toolbar">
          [#div class="kg-settings-users-toolbar-right">
            ${renderSettingsTabs({
              id: 'usersFilterTabs',
              size: 'sm',
              tabVariant: 'segmentedCompact',
              className: 'kg-settings-users-tabs',
              activeId: state.filters.mfaOffOnly ? 'mfaoff' : state.filters.adminsOnly ? 'admins' : 'all',
              tabs: [
                { id: 'all', label: 'All', attrs: { 'data-action': 'users-filter', 'data-filter': 'all' } },
                { id: 'admins', label: 'Admins', attrs: { 'data-action': 'users-filter', 'data-filter': 'admins' } },
                { id: 'mfaoff', label: 'Admins MFA OFF', attrs: { 'data-action': 'users-filter', 'data-filter': 'mfaoff' } }
              ]
            })}
            [#div class="kg-settings-users-count-slot">
              [#div class="kg-settings-users-count-pill"><span>Users</span><span class="value" id="usersCount">${count}</span></div>
            </div>
            [#div class="kg-settings-users-action-slot">
              ${renderSettingsButton({
                label: 'Refresh',
                intent: 'Secondary',
                attrs: { 'data-action': 'users-refresh' }
              })}
            </div>
          </div>
        </div>
        [#div class="kg-settings-users-search-row">
          [#div class="kg-settings-users-search">
            ${renderSettingsInput({
              kind: 'search',
              label: '',
              helperText: '',
              size: 'md',
              inputId: 'filterQuery',
              value: state.filters.query,
              placeholder: 'Search email...',
              inputAttrs: { autocomplete: 'off' }
            })}
          </div>
        </div>
        [#div id="usersStatus" class="kg-settings-muted"></div>
        [#div class="kg-settings-users-control-rail">${userPanelsHtml}</div>
        [#div class="kg-settings-users-table-region">
          [#div id="usersList" class="kg-settings-users-table-host">${listHtml}</div>
        </div>
      </div>
    `;
  }

  function renderMfaCard() {
    const canWrite = Boolean(state.permissions && state.permissions.canWrite);
    return renderSettingsSubPanel({
      label: 'Your admin MFA (TOTP)',
      state: 'Expanded',
      density: 'compact',
      className: 'kg-settings-users-panel kg-settings-users-panel--mfa',
      bodyHtml: `
        [#div class="kg-settings-users-stack">
          ${canWrite ? `
            [#div class="kg-settings-users-form-grid">
              [#div class="kg-settings-users-action kg-settings-users-field--span-2">
                ${renderSettingsButton({
                  label: 'Start enrollment',
                  intent: 'Primary',
                  attrs: { 'data-action': 'mfa-enroll' }
                })}
              </div>
              [#div class="kg-settings-users-field">
                ${renderSettingsInput({
                  label: '',
                  helperText: '',
                  size: 'sm',
                  inputId: 'mfaCode',
                  placeholder: 'MFA 6 digit code',
                  inputAttrs: {
                    type: 'text',
                    name: 'mfaCode',
                    inputmode: 'numeric',
                    pattern: '[0-9]{6}'
                  }
                })}
              </div>
              [#div class="kg-settings-users-action">
                ${renderSettingsButton({
                  label: 'Verify & Enable',
                  intent: 'Primary',
                  attrs: { 'data-action': 'mfa-verify' }
                })}
              </div>
            </div>
          ` : renderSettingsNotice({
            kind: 'warning',
            className: 'kg-settings-notice-compact',
            message: 'Read-only access. MFA actions require module.settings.write.'
          })}
        </div>
      `
    });
  }

    function renderArchiveStatus(payload) {
      const err = payload && payload.__error ? String(payload.__error || '') : '';
      if (err) {
        return renderSettingsNotice({
          kind: 'error',
          className: 'kg-settings-notice-compact',
          bodyHtml: `Failed to load archive status: ${escapeHtml(err)}`
        });
      }
      const p = payload || {};
      const latest = p.latest || {};
      const retention = p.retention || { auditDays: 365, authDays: 180, usageDays: 30 };

      const safeLatest = (row) => row && typeof row === 'object'
        ? {
            exported_at: String(row.exported_at || ''),
            row_count: Number(row.row_count || 0),
            object_key: String(row.object_key || ''),
            sha256: String(row.sha256 || ''),
            range_start: row.range_start == null ? '' : String(row.range_start),
            range_end: row.range_end == null ? '' : String(row.range_end)
          }
        : null;

      const auditLatest = safeLatest(latest.audit_events);
      const authLatest = safeLatest(latest.auth_events);
      const usageLatest = safeLatest(latest.module_usage_events);
      const exports = Array.isArray(p.exports) ? p.exports : [];
      const totals = (p && typeof p.totals === 'object' && p.totals) ? p.totals : null;
      const r2FreeTier = (p && typeof p.r2FreeTier === 'object' && p.r2FreeTier) ? p.r2FreeTier : null;
      const r2Usage = (p && typeof p.r2Usage === 'object' && p.r2Usage) ? p.r2Usage : null;
      const estimatedOps = (p && typeof p.estimatedOps === 'object' && p.estimatedOps) ? p.estimatedOps : null;

      const formatBytes = (n) => {
        const v = Number(n || 0);
        if (!Number.isFinite(v) || v <= 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        let idx = 0;
        let value = v;
        while (value >= 1024 && idx < units.length - 1) {
          value /= 1024;
          idx += 1;
        }
        const digits = idx === 0 ? 0 : (idx >= 3 ? 2 : 1);
        return `${value.toFixed(digits)} ${units[idx]}`;
      };

      const renderArchiveStatusRow = ({ label = '', tone = 'neutral', bodyHtml = '' } = {}) => {
        const toneClass = tone === 'critical' ? ' is-critical' : tone === 'warning' ? ' is-warning' : '';
        return `
          [#div class="kg-settings-archive-status-row${toneClass}">
            <span class="kg-settings-archive-status-label">${escapeHtml(label)}</span>
            [#div class="kg-settings-archive-status-body">${bodyHtml}</div>
          </div>
        `;
      };

      const nowMs = Date.now();
      const parseMs = (iso) => {
        const ms = Date.parse(String(iso || '').trim());
        return Number.isFinite(ms) ? ms : 0;
      };
      const newestMs = Math.max(
        parseMs(auditLatest && auditLatest.exported_at),
        parseMs(authLatest && authLatest.exported_at),
        parseMs(usageLatest && usageLatest.exported_at)
      );
      const ageHours = newestMs ? Math.round(((nowMs - newestMs) / (60 * 60 * 1000)) * 10) / 10 : 0;
      const stale = newestMs ? (nowMs - newestMs) > (36 * 60 * 60 * 1000) : true;
      const freshnessLine = newestMs
        ? renderArchiveStatusRow({
            label: 'Archive freshness',
            tone: stale ? 'critical' : 'neutral',
            bodyHtml: `Last archive run <span class="mono">~${escapeHtml(ageHours)}h</span> ago. Cron is scheduled daily at <span class="mono">09:00 UTC</span>.`
          })
        : renderArchiveStatusRow({
            label: 'Archive freshness',
            tone: 'critical',
            bodyHtml: 'No archive exports found yet. Trigger the scheduled worker once from Cloudflare, or wait for the next cron run.'
          });

      const avgPerDay = totals && Number.isFinite(Number(totals.avgCompressedBytesPerDay)) ? Number(totals.avgCompressedBytesPerDay) : 0;
      const projected30 = totals && Number.isFinite(Number(totals.projectedCompressedBytesPer30d)) ? Number(totals.projectedCompressedBytesPer30d) : 0;
      const freeStorage = r2FreeTier && Number.isFinite(Number(r2FreeTier.storageBytes)) ? Number(r2FreeTier.storageBytes) : (10 * 1024 * 1024 * 1024);
      const usedStorage = r2Usage && Number.isFinite(Number(r2Usage.totalBytes)) ? Number(r2Usage.totalBytes) : 0;
      const remainingStorage = Math.max(0, freeStorage - usedStorage);
      const monthsTo10gb = avgPerDay > 0 ? ((remainingStorage / avgPerDay) / 30) : 0;
      const willExceedIn6Months = avgPerDay > 0 && monthsTo10gb > 0 && monthsTo10gb <= 6;
      const hasSizing = avgPerDay > 0;
      const r2UsageAvailable = Boolean(r2Usage && r2Usage.available === true);
      const r2UsagePrefix = String(r2Usage && r2Usage.prefix || '').trim() || 'root';
      const r2UsageError = String(r2Usage && r2Usage.error || '').trim();
      const usagePercent = freeStorage > 0 ? Math.min(100, Math.max(0, (usedStorage / freeStorage) * 100)) : 0;
      const r2UsageMarkup = r2UsageAvailable
        ? `<span class="mono">${escapeHtml(formatBytes(usedStorage))}</span> used across <span class="mono">${escapeHtml(String(Number(r2Usage.totalObjects || 0)))}</span> object(s) under <span class="mono">${escapeHtml(r2UsagePrefix || 'root')}</span>${r2Usage && r2Usage.latestObjectAt ? `. Latest object ${escapeHtml(formatDateTime(r2Usage.latestObjectAt))}.` : '.'}`
        : 'Live R2 usage is currently unavailable.';
      const r2UsageLine = renderArchiveStatusRow({
        label: 'Live R2 usage',
        tone: r2UsageAvailable ? 'neutral' : 'warning',
        bodyHtml: r2UsageAvailable
          ? `${r2UsageMarkup} <span class="mono">(${escapeHtml(usagePercent.toFixed(2))}%</span> of free tier).`
          : `${r2UsageMarkup}${r2UsageError ? ` <span class="mono">${escapeHtml(r2UsageError)}</span>` : ''}`
      });
      const sizingLine = hasSizing
        ? renderArchiveStatusRow({
            label: 'Capacity outlook',
            tone: willExceedIn6Months ? 'warning' : 'neutral',
            bodyHtml: `Avg/day (compressed): <span class="mono">${escapeHtml(formatBytes(avgPerDay))}</span>. Projected 30d: <span class="mono">${escapeHtml(formatBytes(projected30))}</span>. Remaining free-tier headroom: <span class="mono">${escapeHtml(formatBytes(remainingStorage))}</span>. Est. time to 10GB: <span class="mono">${escapeHtml(monthsTo10gb.toFixed(1))}</span> months.`
          })
        : renderArchiveStatusRow({
            label: 'Capacity outlook',
            tone: 'warning',
            bodyHtml: r2UsageAvailable
              ? `Current archive footprint: <span class="mono">${escapeHtml(formatBytes(usedStorage))}</span>. Sizing projections will appear after the next archive run with byte metadata.`
              : 'Sizing will appear after the next archive run.'
          });

      const latestRows = [
        { type: 'audit_events', row: auditLatest },
        { type: 'auth_events', row: authLatest },
        { type: 'module_usage_events', row: usageLatest }
      ].map((it) => {
        const r = it.row;
        const exportedAt = r ? formatDateTime(r.exported_at) : '';
        const rowsCount = r ? String(r.row_count || 0) : '';
        const range = r
          ? `${formatDateTime(r.range_start || '')}${r.range_end ? ` -> ${formatDateTime(r.range_end || '')}` : ''}`
          : '';
        return `
          [#tr>
            [#td class="mono">${escapeHtml(it.type)}</td>
            [#td>${escapeHtml(exportedAt || 'No data')}</td>
            [#td class="mono">${escapeHtml(rowsCount || '')}</td>
            [#td>${escapeHtml(range || '')}</td>
          </tr>
        `;
      }).join('');

      const recentRows = exports.slice(0, 20).map((row) => {
        const type = String(row.type || 'unknown');
        const when = formatDateTime(String(row.exported_at || ''));
        const rows = Number(row.row_count || 0);
        const key = String(row.object_key || '');
        const sha = String(row.sha256 || '');
        const shaShort = sha ? `${sha.slice(0, 10)}…` : '';
        return `
          [#tr>
            [#td>${escapeHtml(when)}</td>
            [#td class="mono">${escapeHtml(type)}</td>
            [#td class="mono">${escapeHtml(String(rows))}</td>
            [#td class="mono" title="${escapeHtml(key)}">${escapeHtml(key)}</td>
            [#td class="mono">${escapeHtml(shaShort)}</td>
            [#td style="text-align:right;">
              ${renderSettingsButton({
                label: 'Copy key',
                intent: 'Secondary',
                compact: true,
                attrs: {
                  'data-action': 'archive-copy',
                  'data-value': key
                }
              })}
            </td>
          </tr>
        `;
      }).join('');

      const latestTable = renderTable({
        id: 'archive-latest-table',
        headers: ['Type', 'Exported at', 'Rows', 'Range'],
        bodyHtml: latestRows,
        className: 'kg-settings-table--archive-latest'
      });

      const recentTable = exports.length
        ? renderTable({
            id: 'archive-recent-table',
            headers: ['Time', 'Type', 'Rows', 'Object key', 'SHA', ''],
            bodyHtml: recentRows,
            className: 'kg-settings-table--archive-recent'
          })
        : `[#div class="kg-settings-archive-empty">No exports recorded yet.</div>`;

      const freeTierStorage = r2FreeTier
        ? escapeHtml(formatBytes(r2FreeTier.storageBytes))
        : '10 GB';
      const classAOps = r2FreeTier
        ? escapeHtml(r2FreeTier.classAOpsPerMonth)
        : '1,000,000';
      const classBOps = r2FreeTier
        ? escapeHtml(r2FreeTier.classBOpsPerMonth)
        : '10,000,000';
      const estimatedOpsMarkup = estimatedOps
        ? `<span class="mono">${escapeHtml(estimatedOps.putsPerDay)}</span> PUT/day (~<span class="mono">${escapeHtml(estimatedOps.putsPerMonth)}</span>/month), <span class="mono">${escapeHtml(estimatedOps.getsPerMonth)}</span> GET/month`
        : 'Will populate after the next archive run.';

      const overviewPanel = renderSettingsSubPanel({
        label: 'Archive overview',
        state: 'Expanded',
        density: 'compact',
        className: 'kg-settings-archive-panel',
        bodyHtml: `
          [#div class="kg-settings-archive-copy">
            [#div class="kg-settings-muted">Long-term archive (D1 -> R2). Export history still comes from <span class="mono">archive_exports</span> in D1, and live bucket usage is read from R2.</div>
          </div>
          <dl class="kg-settings-archive-kv">
            <dt>Retention</dt>
            <dd>Audit <span class="mono">${escapeHtml(retention.auditDays)}</span> days, Auth <span class="mono">${escapeHtml(retention.authDays)}</span> days, Usage <span class="mono">${escapeHtml(retention.usageDays)}</span> days in D1.</dd>
            <dt>R2 free tier</dt>
            <dd><span class="mono">${freeTierStorage}</span> storage, <span class="mono">${classAOps}</span> Class A ops/month, <span class="mono">${classBOps}</span> Class B ops/month.</dd>
            <dt>R2 usage</dt>
            <dd>${r2UsageAvailable ? r2UsageMarkup : `Unavailable.${r2UsageError ? ` <span class="mono">${escapeHtml(r2UsageError)}</span>` : ''}`}</dd>
            <dt>Estimated ops</dt>
            <dd>${estimatedOpsMarkup}</dd>
          </dl>
        `
      });

      const healthPanel = renderSettingsSubPanel({
        label: 'Health & sizing',
        state: 'Expanded',
        density: 'compact',
        className: 'kg-settings-archive-panel',
        bodyHtml: `
          [#div class="kg-settings-archive-status-stack">
            ${freshnessLine}
            ${r2UsageLine}
            ${sizingLine}
          </div>
        `
      });

      return `
        [#div class="kg-settings-archive-layout">
          [#div class="kg-settings-archive-summary">
            ${overviewPanel}
            ${healthPanel}
          </div>
          [#div class="kg-settings-archive-tables">
            ${renderSettingsSubPanel({
              label: 'Latest exports',
              state: 'Expanded',
              density: 'compact',
              className: 'kg-settings-archive-panel',
              bodyHtml: latestTable
            })}
            ${renderSettingsSubPanel({
              label: 'Recent export objects',
              state: 'Expanded',
              density: 'compact',
              className: 'kg-settings-archive-panel',
              bodyHtml: recentTable
            })}
          </div>
        </div>
      `;
    }

  async function loadAndRender({ activity = true } = {}) {
    if (activity && settingsActivityDepth === 0) {
      return runSettingsActivity(
        null,
        () => loadAndRender({ activity: false }),
        { action: 'settings-load' }
      );
    }
    if (!state.mounted) return;
    syncAppearanceThemeState();
    // Abort any previous in-flight loads, but keep the module mounted.
    try { state.controller.abort(); } catch {}
    state.controller = new AbortController();
    const { signal } = state.controller;

    let {
      usersEl,
      sessionsEl,
      auditEl,
      usageEl,
      appearanceEl,
      archiveEl
    } = getPanelElements();

    destroyInlineSelectDsFlow();
    destroyAppearanceDsFlow();
    unmountAppearanceContributionSurfaces();

    const loadingNotice = renderSettingsNotice({
      kind: 'loading',
      className: 'kg-settings-notice-compact',
      message: 'Loading...'
    });
    const showLoadingNotices = state.sharedState?.asyncFeedback?.enabled !== true || !hasLoadedSettingsContent;
    if (showLoadingNotices) {
      if (usersEl) setElementHtml(usersEl, loadingNotice);
      if (sessionsEl) setElementHtml(sessionsEl, loadingNotice);
      if (auditEl) setElementHtml(auditEl, loadingNotice);
      if (usageEl) setElementHtml(usageEl, loadingNotice);
      if (appearanceEl) setElementHtml(appearanceEl, loadingNotice);
      if (archiveEl) setElementHtml(archiveEl, loadingNotice);
    }

    try {
      const sessionInfo = await fetchJson('/auth/session', { signal });
      state.sessionInfo = sessionInfo || null;
      if (sessionInfo && sessionInfo.access && !isSameAccessPayload(sharedState && sharedState.access, sessionInfo.access)) {
        syncShellAccess(sessionInfo.access, { notifyShell: true });
      } else if (sessionInfo && sessionInfo.access) {
        syncShellAccess(sessionInfo.access, { notifyShell: false });
      }
      const reconciliation = reconcileAccessState(sessionInfo && sessionInfo.access ? sessionInfo.access : sharedState && sharedState.access);
      ({
        usersEl,
        sessionsEl,
        auditEl,
        usageEl,
        appearanceEl,
        archiveEl
      } = getPanelElements());
      if (!reconciliation.canRead) {
        ensureSettingsChromeAfterInitialLoad();
        return;
      }
      if (reconciliation.viewChanged || reconciliation.readChanged) {
        if (usersEl) setElementHtml(usersEl, loadingNotice);
        if (sessionsEl) setElementHtml(sessionsEl, loadingNotice);
        if (auditEl) setElementHtml(auditEl, loadingNotice);
        if (usageEl) setElementHtml(usageEl, loadingNotice);
        if (appearanceEl) setElementHtml(appearanceEl, loadingNotice);
        if (archiveEl) setElementHtml(archiveEl, loadingNotice);
      }

      if (state.viewPolicy.requiresAdminData) {
        const archivePromise = fetchJson('/api/admin/archive/exports?limit=30', { signal })
          .catch((e) => ({ __error: String(e && e.message || e || 'Failed') }));
        const [usersPayload, sessionsPayload, usagePayload, archivePayload] = await Promise.all([
          fetchJson('/api/admin/users', { signal }),
          fetchJson('/api/admin/sessions/active', { signal }),
          // Usage defaults to last 7d in the API, but pass explicit range so UI and API match.
          fetchJson('/api/admin/usage/summary?from=' + encodeURIComponent(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()) + '&to=' + encodeURIComponent(new Date().toISOString()), { signal }),
          archivePromise
        ]);
        state.sessions = Array.isArray(sessionsPayload?.sessions) ? sessionsPayload.sessions : [];
        state.archivePayload = archivePayload || null;
        initExportDefaults();
        await loadEventsInitial(signal);

        if (initialDestinationSkeletonVisible) {
          ({
            usersEl,
            sessionsEl,
            auditEl,
            usageEl,
            appearanceEl,
            archiveEl
          } = ensureSettingsChromeAfterInitialLoad());
        }

        if (usersEl) {
          setElementHtml(usersEl, renderInviteAndUsers(usersPayload.users));
          setUsersStatus('muted', '');
        }
        if (sessionsEl) {
          setElementHtml(sessionsEl, renderSessionsPanel(state.sessions));
        }
        if (auditEl) {
          setElementHtml(auditEl, renderEventsExplorer());
        }
        state.usage.summaryPayload = usagePayload || null;
        if (usageEl) setElementHtml(usageEl, renderUsagePanel(state.usage.summaryPayload));
        if (archiveEl) {
          setElementHtml(archiveEl, renderArchiveStatus(state.archivePayload));
        }
      } else {
        state.sessions = [];
        state.archivePayload = null;
        state.usage.summaryPayload = null;
        if (initialDestinationSkeletonVisible) {
          ({
            usersEl,
            sessionsEl,
            auditEl,
            usageEl,
            appearanceEl,
            archiveEl
          } = ensureSettingsChromeAfterInitialLoad());
        }
      }

      if (appearanceEl) {
        setElementHtml(appearanceEl, renderAppearancePanel());
        mountAppearanceDsFlow();
      }
      mountInlineSelectDsFlow();
      if (state.activePanel === 'appearance') {
        await mountAppearanceContributions();
      } else {
        await mountActiveContributionPanel();
      }

      // MFA card is rendered inside the Audit tab.
      setMfaStatus('muted', '');
      hasLoadedSettingsContent = true;
    } catch (error) {
      if (!state.mounted) return;
      if (initialDestinationSkeletonVisible) {
        ({
          usersEl,
          sessionsEl,
          auditEl,
          usageEl,
          appearanceEl,
          archiveEl
        } = ensureSettingsChromeAfterInitialLoad());
      }
      const message = String(
        error && error.message
        || (state.viewPolicy.requiresAdminData ? 'Failed to load admin data' : 'Failed to load appearance settings')
      );
      const errorNotice = renderSettingsNotice({
        kind: 'error',
        className: 'kg-settings-notice-compact',
        message
      });
      if (state.sharedState?.asyncFeedback?.enabled !== true || !hasLoadedSettingsContent) {
        if (usersEl) setElementHtml(usersEl, errorNotice);
        if (sessionsEl) setElementHtml(sessionsEl, errorNotice);
        if (auditEl) setElementHtml(auditEl, errorNotice);
        if (usageEl) setElementHtml(usageEl, errorNotice);
        if (appearanceEl) setElementHtml(appearanceEl, errorNotice);
        if (archiveEl) setElementHtml(archiveEl, errorNotice);
      }
      throw error;
    }
  }

  function renderSessionsPanel(sessions = []) {
    const items = Array.isArray(sessions) ? sessions : [];
    const summaryPanel = renderSettingsSubPanel({
      label: 'Session overview',
      density: 'compact',
      className: 'kg-settings-sessions-summary',
      bodyHtml: `
        [#div class="kg-settings-meta-grid">
          [#div class="kg-settings-meta-item">
            <span class="kg-settings-meta-label">Active sessions</span>
            [#div class="kg-settings-meta-value">${items.length}</div>
          </div>
          [#div class="kg-settings-meta-item">
            <span class="kg-settings-meta-label">Scope</span>
            [#div class="kg-settings-meta-value">Current signed-in sessions</div>
          </div>
          [#div class="kg-settings-meta-item">
            <span class="kg-settings-meta-label">Behavior</span>
            [#div class="kg-settings-meta-value">Review IP, activity, and expiry windows</div>
          </div>
        </div>
      `
    });
    if (!items.length) {
      return `
        [#div class="kg-settings-sessions-layout">
          [#div class="kg-settings-sessions-toolbar">
            [#div class="kg-settings-toolbar-ghost"></div>
            [#div class="kg-settings-toolbar-ghost"></div>
            [#div class="kg-settings-sessions-count-slot">
              [#div class="kg-settings-users-count-pill"><span>Active sessions</span><span class="value">0</span></div>
            </div>
            [#div class="kg-settings-toolbar-ghost"></div>
          </div>
          ${summaryPanel}
          [#div class="kg-settings-sessions-table-region">
            [#div class="kg-settings-sessions-table-host">${renderEmpty('No active sessions.')}</div>
          </div>
        </div>
      `;
    }

    const rows = items.map((s) => {
      const email = String(s.email || '');
      const ip = String(s.ip_address || '');
      const last = formatDateTime(s.last_seen_at || '');
      const started = formatDateTime(s.started_at || '');
      const expires = formatDateTime(s.expires_at || '');
      return `
        [#tr>
          [#td>${escapeHtml(email)}</td>
          [#td class="mono">${escapeHtml(ip)}</td>
          [#td>${escapeHtml(last)}</td>
          [#td>${escapeHtml(started)}</td>
          [#td>${escapeHtml(expires)}</td>
        </tr>
      `;
    }).join('');

    const tableHtml = renderTable({
      id: 'sessions-table',
      headers: ['Email', 'IP', 'Last activity', 'Started', 'Expires'],
      bodyHtml: rows,
      minWidth: '1100px',
      className: 'kg-settings-table--sessions'
    });

    return `
      [#div class="kg-settings-sessions-layout">
        [#div class="kg-settings-sessions-toolbar">
          [#div class="kg-settings-toolbar-ghost"></div>
          [#div class="kg-settings-toolbar-ghost"></div>
          [#div class="kg-settings-sessions-count-slot">
            [#div class="kg-settings-users-count-pill"><span>Active sessions</span><span class="value">${items.length}</span></div>
          </div>
          [#div class="kg-settings-toolbar-ghost"></div>
        </div>
        ${summaryPanel}
        [#div class="kg-settings-sessions-table-region">
          [#div class="kg-settings-sessions-table-host">${tableHtml}</div>
        </div>
      </div>
    `;
  }

  function renderUsagePanel(payload) {
    const enabled = Boolean(payload && payload.enabled);
    const tab = String(state.usage.tab || 'summary');
    const usageTabs = renderSettingsTabs({
      id: enabled ? 'usageTabToggle' : 'usageTabToggleDisabled',
      size: 'sm',
      className: 'kg-settings-usage-tabs',
      activeId: tab === 'events' ? 'events' : 'summary',
      tabs: [
        { id: 'summary', label: 'Summary', attrs: { 'data-action': 'usage-tab', 'data-tab': 'summary' } },
        { id: 'events', label: 'Events', attrs: { 'data-action': 'usage-tab', 'data-tab': 'events' } }
      ]
    });

    if (!enabled) {
      return `
        [#div class="kg-settings-usage-layout">
          [#div class="kg-settings-usage-toolbar">
            [#div class="kg-settings-usage-search"></div>
            ${usageTabs}
            [#div class="kg-settings-usage-button-slot"></div>
            [#div class="kg-settings-usage-button-slot"></div>
            [#div class="kg-settings-usage-button-slot"></div>
          </div>
          [#div id="usageStatus" class="kg-settings-muted">${escapeHtml(state.usage.status.text)}</div>
          ${renderSettingsNotice({
            kind: 'warning',
            className: 'kg-settings-notice-compact',
            bodyHtml: 'Usage logging is disabled. To enable: set <span class="mono">USAGE_ENABLED=true</span> in Cloudflare Pages (production + preview) and redeploy.'
          })}
        </div>
      `;
    }

    const fromIso = String(payload && payload.from || '').trim();
    const toIso = String(payload && payload.to || '').trim();
    const summary = (payload && Array.isArray(payload.summary)) ? payload.summary : [];

    const fromLocal = fromIso ? toLocalDateTimeValue(fromIso) : toLocalDateTimeValue(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
    const toLocal = toIso ? toLocalDateTimeValue(toIso) : toLocalDateTimeValue(new Date());
    const rows = summary.length
      ? renderTable({
          id: 'usage-summary-table',
          headers: ['Module / Action', 'Count'],
          bodyHtml: summary.map((item) => `
            [#tr>
              [#td>${escapeHtml(String(item.module || 'unknown'))} / ${escapeHtml(String(item.action || 'event'))}</td>
              [#td class="mono">${escapeHtml(String(item.count || 0))}</td>
            </tr>
          `).join(''),
          minWidth: '700px'
        })
      : renderEmpty('No usage events recorded yet.');

    const summaryHtml = `
      [#div class="kg-settings-usage-control-rail is-compact">
        ${renderSettingsSubPanel({
          label: 'Summary window',
          density: 'compact',
          className: 'kg-settings-usage-panel kg-settings-usage-panel--summary',
          bodyHtml: `
            [#div class="kg-settings-usage-summary-grid">
              [#div class="kg-settings-usage-field">
                <label class="kg-settings-label" for="usageFrom">From</label>
                ${renderSettingsDateTimeField({ id: 'usageFrom', value: fromLocal, placeholder: 'From', triggerClass: 'kg-settings-input mini', minWidth: 320 })}
              </div>
              [#div class="kg-settings-usage-field">
                <label class="kg-settings-label" for="usageTo">To</label>
                ${renderSettingsDateTimeField({ id: 'usageTo', value: toLocal, placeholder: 'To', triggerClass: 'kg-settings-input mini', minWidth: 320 })}
              </div>
              [#div class="kg-settings-usage-field">
                ${renderSettingsButton({
                  label: 'Refresh',
                  intent: 'Secondary',
                  attrs: { 'data-action': 'usage-refresh' }
                })}
              </div>
              [#div class="kg-settings-muted">Default window is last 7 days. This view is aggregated (module/action counts) and limited to top 200.</div>
            </div>
          `
        })}
      </div>
      [#div class="kg-settings-usage-table-region">
        [#div class="kg-settings-usage-table-host">${rows}</div>
      </div>
    `;

    const eventsToolbar = `
      [#div class="kg-settings-usage-toolbar">
        [#div class="kg-settings-usage-search">
          ${tab === 'events' ? renderSettingsInput({
            kind: 'search',
            label: '',
            helperText: '',
            size: 'md',
            inputId: 'usageEventsSearch',
            value: state.usage.explorer.search,
            placeholder: 'Search loaded usage events...',
            inputAttrs: { autocomplete: 'off' }
          }) : ''}
        </div>
        ${usageTabs}
        [#div class="kg-settings-usage-button-slot">
          ${tab === 'events' ? renderSettingsButton({
            label: state.usage.explorer.moreFilters ? 'Hide filters' : 'More filters',
            intent: 'Secondary',
            attrs: { 'data-action': 'usage-toggle-filters' }
          }) : renderSettingsButton({
            label: 'Refresh',
            intent: 'Secondary',
            attrs: { 'data-action': 'usage-refresh' }
          })}
        </div>
        [#div class="kg-settings-usage-button-slot">
          ${tab === 'events' ? renderSettingsButton({
            label: 'Refresh',
            intent: 'Secondary',
            attrs: { 'data-action': 'usage-refresh-events' }
          }) : ''}
        </div>
        [#div class="kg-settings-usage-button-slot">
          ${tab === 'events' && state.usage.explorer.nextCursor ? renderSettingsButton({
            label: 'Load more',
            intent: 'Secondary',
            attrs: { 'data-action': 'usage-load-more' }
          }) : ''}
        </div>
      </div>
    `;

    return `
      [#div class="kg-settings-usage-layout">
        ${eventsToolbar}
        [#div id="usageStatus" class="kg-settings-muted">${escapeHtml(state.usage.status.text)}</div>
        ${tab === 'events' ? renderUsageExplorer() : summaryHtml}
      </div>
    `;
  }

  async function postJson(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...getCsrfHeader() },
      credentials: 'same-origin',
      body: JSON.stringify(payload || {})
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data && data.error ? data.error : `HTTP ${response.status}`);
    }
    return data;
  }

  async function postJsonWithMeta(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...getCsrfHeader() },
      credentials: 'same-origin',
      body: JSON.stringify(payload || {})
    });
    const retryAfterRaw = String(response.headers.get('retry-after') || '').trim();
    const retryAfterSeconds = retryAfterRaw && /^[0-9]+$/.test(retryAfterRaw) ? Number(retryAfterRaw) : 0;
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, status: response.status, data, retryAfterSeconds };
  }

  async function patchJson(url, payload) {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', ...getCsrfHeader() },
      credentials: 'same-origin',
      body: JSON.stringify(payload || {})
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data && data.error ? data.error : `HTTP ${response.status}`);
    }
    return data;
  }

  // Event delegation: handle invite submit and per-user action clicks.
  const onSubmit = async (event) => {
    if (!state.mounted) return;
    const target = event.target;
    if (!target || target.id !== 'inviteForm') return;
    event.preventDefault();
    if (!(state.permissions && state.permissions.canWrite)) {
      setUsersStatus('error', 'Write access is required.');
      return;
    }

    const now = Date.now();
    if (state.inviteInFlight || now < state.inviteCooldownUntil) return;
    const email = String(state.mountEl.querySelector('#inviteEmail')?.value || '').trim().toLowerCase();
    const roleValue = String(state.mountEl.querySelector('#inviteRole')?.value || 'user').toLowerCase() === 'admin' ? 'admin' : 'user';
    const sendSetup = true;

    if (!email || !email.includes('@')) {
      setUsersStatus('error', 'Valid email is required.');
      return;
    }
    if (state.usersByEmail.has(email)) {
      setUsersStatus('error', 'User already exists.');
      return;
    }
    if (roleValue === 'admin') {
      const ok = await openConfirmModal({
        title: 'Invite as admin?',
        message: 'This grants full access to Settings.',
        confirmLabel: 'Invite admin'
      });
      if (!ok) return;
    }

    const inviteButton = state.mountEl.querySelector('#inviteBtn');
    await runSettingsActivity(inviteButton, async () => {
      state.inviteInFlight = true;
      if (state.sharedState?.asyncFeedback?.enabled !== true && isSettingsSaasFinalUiActive() && inviteButton) {
        setSettingsButtonRuntimeState(inviteButton, {
          busy: true,
          disabled: true,
          legacyLabel: 'Inviting user...'
        });
      }
      setUsersStatus('muted', 'Inviting user...');
      try {
        const invitePayload = await postJson('/api/admin/users/invite', { email, role: roleValue });
        state.inviteCooldownUntil = Date.now() + 5000;
        let finalKind = 'ok';
        let finalText = 'Invited.';
        if (sendSetup) {
          setUsersStatus('muted', 'Invited. Requesting setup email...');
          const invitedId = invitePayload && invitePayload.invited && invitePayload.invited.id ? String(invitePayload.invited.id) : '';
          if (!invitedId) {
            finalKind = 'error';
            finalText = 'Invited, but could not request setup email (missing user id).';
          } else {
            const setupResp = await postJsonWithMeta(`/api/admin/users/${encodeURIComponent(invitedId)}/send-setup-email`, {});
            if (setupResp.status === 429) {
              const wait = Number(setupResp.retryAfterSeconds || 60);
              finalKind = 'error';
              finalText = `Rate limited. Try again in ${wait}s.`;
            } else if (!setupResp.ok) {
              finalKind = 'error';
              finalText = String(setupResp.data && setupResp.data.error || `HTTP ${setupResp.status}`);
            } else if (setupResp.data && setupResp.data.webhookConfigured === false) {
              finalKind = 'error';
              finalText = 'Reset email webhook is not configured.';
            } else if (setupResp.data && setupResp.data.emailQueued === false) {
              const upstreamStatus = setupResp.data && setupResp.data.upstreamStatus ? ` (upstream ${setupResp.data.upstreamStatus})` : '';
              if (setupResp.data && setupResp.data.reason === 'forbidden') {
                finalKind = 'error';
                finalText = `Setup email blocked by upstream (403). Check Access token / n8n auth.${upstreamStatus}`;
              } else {
                finalKind = 'error';
                finalText = `Setup email request failed. Check n8n executions/email provider.${upstreamStatus}`;
              }
            } else {
              finalKind = 'ok';
              finalText = 'Invited. Setup email delivered to n8n. Check inbox/spam.';
            }
          }
        }
        await loadAndRender();
        setUsersStatus(finalKind, finalText);
      } catch (error) {
        setUsersStatus('error', String(error && error.message || 'Invite failed'));
        throw error;
      } finally {
        state.inviteInFlight = false;
        if (state.sharedState?.asyncFeedback?.enabled !== true && isSettingsSaasFinalUiActive() && inviteButton?.isConnected) {
          setSettingsButtonRuntimeState(inviteButton, {
            busy: false,
            disabled: false,
            legacyLabel: 'Send Invite'
          });
        }
      }
    });
  };
  state.mountEl.addEventListener('submit', onSubmit);
  state.cleanup.push(() => state.mountEl.removeEventListener('submit', onSubmit));

  async function handleActionClick(actionEl, event) {
      if (!state.mounted) return;
      if (!actionEl) return;
      const action = String(actionEl.getAttribute('data-action') || '').trim();
      const userId = String(actionEl.getAttribute('data-user-id') || '').trim();
      const email = String(actionEl.getAttribute('data-email') || '').trim();
      const isMfaAction = action.startsWith('mfa-');
      const isUsageAction = action.startsWith('usage-');
      const isMainTabAction = action === 'main-tab';
      const isGlobalAction = action === 'delete-user-preview'
        || action === 'delete-user-exec'
        || action === 'users-refresh'
        || action === 'users-filter'
        || action === 'appearance-pick'
        || action === 'appearance-preview'
        || action === 'appearance-apply'
        || action === 'appearance-reset'
        || action === 'platform-shell-load-retry-action'
        || action === 'archive-copy'
        || action === 'settings-reference-v2-nav-toggle'
        || action === 'settings-reference-v2-nav-close'
        || isMainTabAction
        || isUsageAction;
      const isEventsAction = action.startsWith('events-')
        || action === 'event-details'
        || action.startsWith('modal-');
    if (!action) return;
    if (!isMfaAction && !isGlobalAction && !isEventsAction && !userId) return;
    if (isMfaAction) {
      // We render MFA controls inside the Audit card; keep them from triggering user-action locks.
      event.preventDefault();
    }

    const writeActions = new Set([
      'edit-access',
      'send-setup',
      'disable',
      'enable',
      'make-admin',
      'make-user',
      'revoke-sessions',
      'mfa-enroll',
      'mfa-copy-otpauth',
      'mfa-verify',
      'mfa-reset'
    ]);
    const deleteActions = new Set([
      'delete-user-by-id',
      'delete-user-preview',
      'delete-user-exec'
    ]);
    if (deleteActions.has(action) && !(state.permissions && state.permissions.canDelete)) {
      setUsersStatus('error', 'Delete access is required.');
      return;
    }
    const isSelfAccessEdit = action === 'edit-access' && isCurrentUserEmail(email);
    if (writeActions.has(action) && !(state.permissions && state.permissions.canWrite) && !isSelfAccessEdit) {
      if (action.startsWith('mfa-')) setMfaStatus('error', 'Write access is required.');
      else setUsersStatus('error', 'Write access is required.');
      return;
    }

    const key = `${action}:${isMfaAction ? 'self' : (isGlobalAction || isEventsAction ? 'global' : userId)}`;
    const now = Date.now();
    const cooldownUntil = Number(state.perUserCooldownUntil.get(key) || 0);
    if (state.inFlightActions.has(key) || now < cooldownUntil) return;
    state.inFlightActions.add(key);

    try {
      if (action === 'user-row-actions') {
        const targetUser = (state.users || []).find((item) => String(item && item.id || '') === userId)
          || { id: userId, email };
        openUserActionsModal(targetUser);
        state.perUserCooldownUntil.set(key, Date.now() + 250);
        return;
      }
      if (action === 'archive-copy') {
        const value = String(actionEl.getAttribute('data-value') || '').trim();
        if (!value) {
          setUsersStatus('error', 'No object key to copy.');
          return;
        }
        try {
          await navigator.clipboard.writeText(value);
          setUsersStatus('ok', 'Copied object key.');
        } catch {
          setUsersStatus('error', 'Unable to copy. Copy manually from the row.');
        }
        state.perUserCooldownUntil.set(key, Date.now() + 500);
        return;
      }
        if (action === 'platform-shell-load-retry-action') {
          const contributionKind = String(actionEl.getAttribute('data-contribution-kind') || '').trim() === 'appearance'
            ? 'appearance'
            : 'panel';
          const contributionId = String(actionEl.getAttribute('data-contribution-id') || '').trim();
          const descriptor = contributionKind === 'appearance'
            ? (Array.isArray(state.viewPolicy?.appearanceSections)
              ? state.viewPolicy.appearanceSections.find((item) => item.id === contributionId)
              : null)
            : (state.viewPolicy?.appPanelById instanceof Map
              ? state.viewPolicy.appPanelById.get(contributionId)
              : null);
          if (descriptor) {
            await mountContributionSurface(contributionKind, descriptor, { force: true });
          }
          return;
        }
        if (action === 'main-tab') {
          const tab = String(actionEl.getAttribute('data-tab') || '').trim();
          const next = state.viewPolicy.visiblePanelSet.has(tab)
            ? tab
            : state.viewPolicy.initialPanel;
          state.activePanel = next;
          const currentRootUsesReferenceLayout = Boolean(state.mountEl.querySelector('[data-kg-settings-reference-v2="true"]'));
          const nextUsesReferenceLayout = isSettingsReferenceV2Active()
            && TUTOR_DESK_SETTINGS_REFERENCE_V2_PANEL_SET.has(next);
          if (currentRootUsesReferenceLayout !== nextUsesReferenceLayout) {
            renderSettingsChrome();
          }
          syncActivePanelPresentation();
          // Update active styles without full re-render.
          const buttons = state.mountEl.querySelectorAll('button[data-action="main-tab"]');
          buttons.forEach((b) => {
            const t = String(b.getAttribute('data-tab') || '').trim();
            const isActive = t === next;
            b.classList.toggle('active', isActive);
            b.classList.toggle('is-active', isActive);
            b.classList.toggle('is-inactive', !isActive);
            b.setAttribute('aria-selected', isActive ? 'true' : 'false');
            if (b.closest('[data-settings-reference-v2-nav]')) b.setAttribute('tabindex', isActive ? '0' : '-1');
          });
          closeSettingsReferenceV2Navigation();
          if (next === 'appearance') {
            await mountAppearanceContributions();
          } else {
            await mountActiveContributionPanel();
          }
          return;
        }
        if (action === 'settings-reference-v2-nav-toggle') {
          const nav = state.mountEl.querySelector('[data-settings-reference-v2-nav]');
          const open = nav?.getAttribute('data-open') !== 'true';
          setSettingsReferenceV2NavigationOpen(open, actionEl);
          return;
        }
        if (action === 'settings-reference-v2-nav-close') {
          closeSettingsReferenceV2Navigation();
          return;
        }
        if (action === 'users-refresh') {
          setUsersStatus('muted', 'Refreshing...');
          await loadAndRender();
          setUsersStatus('ok', 'Refreshed.');
          state.perUserCooldownUntil.set(key, Date.now() + 800);
          return;
        }
        if (action === 'users-filter') {
          const filter = String(actionEl.getAttribute('data-filter') || '').trim().toLowerCase();
          if (filter === 'mfaoff') {
            state.filters.adminsOnly = true;
            state.filters.mfaOffOnly = true;
          } else if (filter === 'admins') {
            state.filters.adminsOnly = true;
            state.filters.mfaOffOnly = false;
          } else {
            state.filters.adminsOnly = false;
            state.filters.mfaOffOnly = false;
          }
          const buttons = state.mountEl.querySelectorAll('button[data-action="users-filter"]');
          buttons.forEach((b) => {
            const f = String(b.getAttribute('data-filter') || '').trim().toLowerCase();
            const isActive = (filter === 'mfaoff' && f === 'mfaoff')
              || (filter === 'admins' && f === 'admins')
              || ((filter !== 'mfaoff' && filter !== 'admins') && f === 'all');
            b.classList.toggle('active', isActive);
            b.classList.toggle('is-active', isActive);
            b.classList.toggle('is-inactive', !isActive);
            b.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });
          rerenderUsersListOnly();
          state.perUserCooldownUntil.set(key, Date.now() + 250);
          return;
        }
        if (action === 'appearance-pick') {
          const themeModeId = String(actionEl.getAttribute('data-theme-id') || '').trim().toLowerCase();
          const themeMode = getAppearanceThemeModes().find((entry) => entry.id === themeModeId);
          if (!themeMode) {
            setAppearanceStatus('error', 'Unknown theme mode selected.');
            return;
          }
          if (!isThemeLifecycleSelectable(themeMode)) {
            setAppearanceStatus('error', 'This theme has been removed and can no longer be selected.');
            return;
          }
          syncAppearanceThemeState({ selectedThemeModeId: themeModeId });
          rerenderAppearanceOnly();
          setAppearanceStatus('muted', 'Selection updated. Click Apply to save.');
          state.perUserCooldownUntil.set(key, Date.now() + 200);
          return;
        }
        if (action === 'appearance-preview') {
          const scopeId = getPreferredAppearanceScopeId(state.appearance.selectedScopeId || SETTINGS_ONLY_SCOPE_ID);
          const requestedThemeModeId = normalizeAppearanceThemeModeId(
            state.appearance.selectedThemeModeId || getDefaultThemeModeId()
          );
          const themeModeId = resolveEffectiveAppearanceThemeModeId(requestedThemeModeId);
          if (!canApplyThemeScope(scopeId)) {
            setAppearanceStatus('error', 'The selected scope is not currently available.');
            return;
          }
          const result = applyThemeScope(scopeId, themeModeId, { mode: 'preview' });
          if (!result.applied.length) {
            setAppearanceStatus('error', 'No theme-capable surface was available for preview.');
            return;
          }
          if (result.failed.length) {
            setAppearanceStatus('error', result.failed[0].errorMessage || 'Theme preview failed.');
            return;
          }
          syncAppearanceThemeState({ previewThemeModeId: requestedThemeModeId });
          rerenderAppearanceOnly();
          setAppearanceStatus('muted', `Previewing '${getAppearanceThemeLabel(requestedThemeModeId)}' in ${getAppearanceScopeDefinition(scopeId).label}.`);
          state.perUserCooldownUntil.set(key, Date.now() + 200);
          return;
        }
        if (action === 'appearance-apply') {
          const scopeId = getPreferredAppearanceScopeId(state.appearance.selectedScopeId || SETTINGS_ONLY_SCOPE_ID);
          const requestedThemeModeId = normalizeAppearanceThemeModeId(
            state.appearance.selectedThemeModeId || getDefaultThemeModeId()
          );
          const themeModeId = resolveEffectiveAppearanceThemeModeId(requestedThemeModeId);
          if (!canApplyThemeScope(scopeId)) {
            setAppearanceStatus('error', 'The selected scope is not currently available.');
            return;
          }
          let persisted;
          if (themePreferenceBridge) {
            try {
              const saved = await themePreferenceBridge.save(requestedThemeModeId);
              if (saved?.persisted !== true || saved.themeModeId !== requestedThemeModeId) {
                throw new Error('The server did not confirm the requested account theme.');
              }
              persisted = { scopeId: FULL_APP_SCOPE_ID, themeId: requestedThemeModeId };
            } catch (error) {
              setAppearanceStatus('error', String(error?.message || 'The account theme could not be saved.'));
              return;
            }
          } else {
            persisted = scopeId === SETTINGS_ONLY_SCOPE_ID
              ? setPersistedSettingsThemeMode({
                  version: 1,
                  themeId,
                  scopeId
                })
              : setPersistedPlatformThemeMode({
                  version: 1,
                  themeId,
                  scopeId
                });
          }
          const result = applyThemeScope(scopeId, themeModeId, { mode: 'apply' });
          if (!result.applied.length) {
            setAppearanceStatus('error', 'No theme-capable surface was available to apply.');
            return;
          }
          if (result.failed.length) {
            setAppearanceStatus('error', result.failed[0].errorMessage || 'Applying the theme mode failed.');
            return;
          }
          syncAppearanceThemeState({
            selectedScopeId: persisted.scopeId,
            selectedThemeModeId: persisted.themeId,
            appliedThemeModeId: persisted.themeId,
            previewThemeModeId: null,
            platformThemeState: scopeId === SETTINGS_ONLY_SCOPE_ID
              ? state.appearance.platformThemeState
              : persisted
          });
          const settingsRootEl = state.mountEl.querySelector('.kg-settings-root');
          if (settingsRootEl) {
            applySettingsThemeMode(settingsRootEl, themeModeId, { mode: 'apply' });
          }
          rerenderAppearanceOnly();
          setAppearanceStatus('ok', `Applied '${getAppearanceThemeLabel(requestedThemeModeId)}' for ${getAppearanceScopeDefinition(scopeId).label}.`);
          state.perUserCooldownUntil.set(key, Date.now() + 250);
          return;
        }
        if (action === 'appearance-reset') {
          const scopeId = getPreferredAppearanceScopeId(state.appearance.selectedScopeId || SETTINGS_ONLY_SCOPE_ID);
          const requestedThemeModeId = getDefaultThemeModeId();
          const themeModeId = resolveEffectiveAppearanceThemeModeId(requestedThemeModeId);
          let persisted;
          if (themePreferenceBridge) {
            try {
              const saved = await themePreferenceBridge.save(requestedThemeModeId);
              if (saved?.persisted !== true || saved.themeModeId !== requestedThemeModeId) {
                throw new Error('The server did not confirm the default account theme.');
              }
              persisted = { scopeId: FULL_APP_SCOPE_ID, themeId: requestedThemeModeId };
            } catch (error) {
              setAppearanceStatus('error', String(error?.message || 'The account theme could not be reset.'));
              return;
            }
          } else {
            persisted = scopeId === SETTINGS_ONLY_SCOPE_ID
              ? setPersistedSettingsThemeMode({
                  version: 1,
                  themeId,
                  scopeId
                })
              : setPersistedPlatformThemeMode({
                  version: 1,
                  themeId,
                  scopeId
                });
          }
          const result = applyThemeScope(scopeId, themeModeId, { mode: 'apply' });
          if (!result.applied.length) {
            setAppearanceStatus('error', 'The selected theme scope is not currently available.');
            return;
          }
          if (result.failed.length) {
            setAppearanceStatus('error', result.failed[0].errorMessage || 'Resetting the theme mode failed.');
            return;
          }
          syncAppearanceThemeState({
            selectedScopeId: persisted.scopeId,
            selectedThemeModeId: persisted.themeId,
            appliedThemeModeId: persisted.themeId,
            previewThemeModeId: null,
            platformThemeState: scopeId === SETTINGS_ONLY_SCOPE_ID
              ? state.appearance.platformThemeState
              : persisted
          });
          rerenderAppearanceOnly();
          setAppearanceStatus('ok', `Theme mode reset to Default for ${getAppearanceScopeDefinition(scopeId).label}.`);
          state.perUserCooldownUntil.set(key, Date.now() + 250);
          return;
        }
        if (action === 'edit-access') {
          if (!userId) throw new Error('Missing user ID');
          setUsersStatus('muted', 'Loading access...');
          const payload = await fetchJson(`/api/admin/users/${encodeURIComponent(userId)}/access`, { signal: state.controller.signal });
          const user = payload && payload.user ? payload.user : { id: userId, email };
          const access = payload && payload.access ? payload.access : { list: [] };
          const recoveryMode = !(state.permissions && state.permissions.canWrite) && isCurrentUserEmail(user && user.email ? user.email : email);
          openAccessModal(recoveryMode ? `Access Recovery for ${String(user.email || email || userId)}` : `Access for ${String(user.email || email || userId)}`, user, access, { recoveryMode });
          setUsersStatus('muted', '');
          state.perUserCooldownUntil.set(key, Date.now() + 500);
          return;
        }
        if (action === 'delete-user-by-id') {
          if (!userId) throw new Error('Missing user ID');
          setUsersStatus('muted', 'Loading delete preview...');
          const resp = await postJsonWithMeta(`/api/admin/users/${encodeURIComponent(userId)}/delete`, { dryRun: true });
          if (!resp.ok) {
            throw new Error(String(resp.data && resp.data.error || `HTTP ${resp.status}`));
          }
          const preview = resp.data || null;
          if (!preview || !preview.target) {
            setUsersStatus('error', 'User not found.');
            return;
          }
          openDeleteUserModal(`Delete user ${String(preview.target.email || preview.target.id || '')}?`, preview);
          setUsersStatus('muted', '');
          state.perUserCooldownUntil.set(key, Date.now() + 800);
          return;
        }
      if (action === 'delete-user-preview') {
        const targetEmail = normalizeEmail(String(state.mountEl.querySelector('#deleteUserEmail')?.value || ''));
        if (!targetEmail) {
          setDeleteUserStatus('error', 'Enter an email address.');
          return;
        }
        setDeleteUserStatus('muted', `Previewing deletion for ${targetEmail}...`, null);
        const resp = await postJsonWithMeta('/api/admin/users/delete-by-email', { email: targetEmail, dryRun: true });
        if (!resp.ok) {
          setDeleteUserStatus('error', String(resp.data && resp.data.error || `HTTP ${resp.status}`), null);
          return;
        }
        const preview = resp.data || null;
        state.deleteUser.preview = preview;
        if (!preview || !preview.target) {
          setDeleteUserStatus('error', 'No matching user.', preview);
          return;
        }
        if (preview.blocked) {
          setDeleteUserStatus('error', preview.blockReason || 'Deletion is blocked.', preview);
          return;
        }
        setDeleteUserStatus('ok', 'Preview ready. Type DELETE_USER then click Delete.', preview);
        state.perUserCooldownUntil.set(key, Date.now() + 800);
        return;
      }
      if (action === 'delete-user-exec') {
        const targetEmail = normalizeEmail(String(state.mountEl.querySelector('#deleteUserEmail')?.value || ''));
        const confirmText = String(state.mountEl.querySelector('#deleteConfirmText')?.value || '').trim();
        if (confirmText !== 'DELETE_USER') {
          setDeleteUserStatus('error', 'Type DELETE_USER to enable deletion.', state.deleteUser.preview);
          return;
        }
        if (!targetEmail) {
          setDeleteUserStatus('error', 'Enter an email address.', state.deleteUser.preview);
          return;
        }
        if (!state.deleteUser.preview) {
          setDeleteUserStatus('error', 'Preview first.', null);
          return;
        }
        if (state.deleteUser.preview.blocked) {
          setDeleteUserStatus('error', state.deleteUser.preview.blockReason || 'Deletion is blocked.', state.deleteUser.preview);
          return;
        }
        const ok = await openConfirmModal({
          title: 'Delete user?',
          message: `Delete ${targetEmail} and anonymize their logs? This cannot be undone.`,
          confirmLabel: 'Delete',
          danger: true
        });
        if (!ok) return;
        setDeleteUserStatus('muted', `Deleting ${targetEmail}...`, state.deleteUser.preview);
        const resp = await postJsonWithMeta('/api/admin/users/delete-by-email', { email: targetEmail, dryRun: false, confirm: 'DELETE_USER' });
        if (!resp.ok) {
          setDeleteUserStatus('error', String(resp.data && resp.data.error || `HTTP ${resp.status}`), state.deleteUser.preview);
          return;
        }
        setDeleteUserStatus('ok', 'Deleted user and anonymized logs.', null);
        state.deleteUser.email = '';
        state.deleteUser.confirm = '';
        state.deleteUser.preview = null;
        await loadAndRender();
        state.perUserCooldownUntil.set(key, Date.now() + 1500);
        return;
      }
      if (action === 'mfa-enroll') {
        setMfaStatus('muted', 'Starting enrollment...');
        const payload = await postJson('/auth/mfa/enroll', {});
        const secret = String(payload && payload.secret || '').trim();
        const otpauthUrl = String(payload && payload.otpauthUrl || '').trim();
        state.mfaEnrollment = { secret, otpauthUrl };
        openMessageModal('MFA Enrollment Started', {
          kind: 'ok',
          copyText: otpauthUrl,
          copyLabel: 'Copy URL',
          bodyHtml: `
            [#div class="kg-settings-modal-details-shell">
              [#div class="modal-message">Add this secret to your authenticator app, then enter the MFA 6 digit code below and click Verify &amp; Enable.</div>
              [#div class="kg-settings-modal-details-scroll">
                ${renderStructured({
                  Secret: secret || null,
                  'otpauth URL': otpauthUrl || null
                }, { maxDepth: 2, maxItems: 10 })}
              </div>
            </div>
          `
        });
        return;
      }
      if (action === 'mfa-copy-otpauth') {
        const value = String(state.mfaEnrollment && state.mfaEnrollment.otpauthUrl || '').trim();
        if (!value) {
          openMessageModal('Admin MFA Error', { kind: 'error', message: 'No otpauth URL to copy. Start enrollment first.' });
          return;
        }
        try {
          await navigator.clipboard.writeText(value);
        } catch {
          openMessageModal('Admin MFA Error', { kind: 'error', message: 'Unable to copy.' });
        }
        return;
      }
      if (action === 'events-tab') {
        const tab = String(actionEl.getAttribute('data-tab') || '').trim();
        if (tab === 'audit' || tab === 'auth') {
          state.events.tab = tab;
          state.events.modal = { open: false, title: '', payload: null };
          rerenderAuditOnly();
        }
        return;
      }
      if (action === 'events-toggle-filters') {
        state.events.moreFilters = !state.events.moreFilters;
        rerenderAuditOnly();
        return;
      }
      if (action === 'events-load-more') {
        setUsersStatus('muted', 'Loading more events...');
        await loadMoreEvents();
        setUsersStatus('ok', 'Loaded more events.');
        rerenderAuditOnly();
        return;
      }
      if (action === 'events-apply-filters') {
        const from = localInputToIso(state.mountEl.querySelector('#eventsFrom')?.value || '');
        const to = localInputToIso(state.mountEl.querySelector('#eventsTo')?.value || '');
        if (state.events.tab === 'audit') {
          state.events.server.audit.from = from;
          state.events.server.audit.to = to;
          state.events.server.audit.module = String(state.mountEl.querySelector('#eventsModule')?.value || '').trim();
          state.events.server.audit.action = String(state.mountEl.querySelector('#eventsAction')?.value || '').trim();
          state.events.server.audit.actorEmail = String(state.mountEl.querySelector('#eventsActorEmail')?.value || '').trim();
        } else {
          state.events.server.auth.from = from;
          state.events.server.auth.to = to;
          state.events.server.auth.email = String(state.mountEl.querySelector('#eventsEmail')?.value || '').trim();
          state.events.server.auth.eventType = String(state.mountEl.querySelector('#eventsEventType')?.value || '').trim();
        }
        setUsersStatus('muted', 'Applying filters...');
        // Reload first page for both lists (keeps behavior predictable).
        await loadEventsInitial();
        setUsersStatus('ok', 'Filters applied.');
        rerenderAuditOnly();
        return;
      }
      if (action === 'events-reset-filters') {
        state.events.server.audit = { module: '', action: '', actorEmail: '', from: '', to: '' };
        state.events.server.auth = { email: '', eventType: '', from: '', to: '' };
        state.events.search = '';
        state.events.moreFilters = false;
        setUsersStatus('muted', 'Resetting filters...');
        await loadEventsInitial();
        setUsersStatus('ok', 'Filters reset.');
        rerenderAuditOnly();
        return;
      }
      if (action === 'usage-refresh') {
        const from = localInputToIso(state.mountEl.querySelector('#usageFrom')?.value || '');
        const to = localInputToIso(state.mountEl.querySelector('#usageTo')?.value || '');
        const params = new URLSearchParams();
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        setUsageStatus('muted', 'Refreshing usage...');
        const usageEl = state.mountEl.querySelector('#settingsUsage');
        if (usageEl && state.sharedState?.asyncFeedback?.enabled !== true) {
          setElementHtml(usageEl, renderSettingsNotice({
            kind: 'loading',
            className: 'kg-settings-notice-compact',
            message: 'Loading...'
          }));
        }
        const payload = await fetchJson(`/api/admin/usage/summary?${params.toString()}`, { signal: state.controller.signal })
          .catch((e) => ({ enabled: true, summary: [], __error: String(e && e.message || e || 'Failed') }));
        if (payload && payload.__error) {
          setUsageStatus('error', `Usage refresh failed: ${payload.__error}`);
          if (usageEl && state.sharedState?.asyncFeedback?.enabled !== true) {
            setElementHtml(usageEl, renderSettingsNotice({
              kind: 'error',
              className: 'kg-settings-notice-compact',
              message: `Usage refresh failed: ${payload.__error}`
            }));
          }
          throw new Error(payload.__error);
        } else if (usageEl) {
          state.usage.summaryPayload = payload || null;
          setElementHtml(usageEl, renderUsagePanel(state.usage.summaryPayload));
          setUsageStatus('ok', `Usage refreshed at ${new Date().toLocaleTimeString()}.`);
        }
        return;
      }
      if (action === 'usage-tab') {
        const tab = String(actionEl.getAttribute('data-tab') || '').trim();
        state.usage.tab = (tab === 'events') ? 'events' : 'summary';
        // Lazy-load usage events when switching to Events tab.
        if (state.usage.tab === 'events' && state.usage.explorer.items.length === 0) {
          setUsageStatus('muted', 'Loading usage events...');
          await loadUsageEventsInitial(state.controller.signal);
          setUsageStatus('ok', 'Usage events loaded.');
        }
        rerenderUsageOnly();
        return;
      }
      if (action === 'usage-toggle-filters') {
        state.usage.explorer.moreFilters = !state.usage.explorer.moreFilters;
        rerenderUsageOnly();
        return;
      }
      if (action === 'usage-refresh-events') {
        setUsageStatus('muted', 'Refreshing usage events...');
        await loadUsageEventsInitial(state.controller.signal);
        setUsageStatus('ok', `Usage events refreshed at ${new Date().toLocaleTimeString()}.`);
        rerenderUsageOnly();
        return;
      }
      if (action === 'usage-load-more') {
        setUsageStatus('muted', 'Loading more usage events...');
        await loadMoreUsageEvents();
        setUsageStatus('ok', 'Loaded more usage events.');
        rerenderUsageOnly();
        return;
      }
      if (action === 'usage-apply-filters') {
        const from = localInputToIso(state.mountEl.querySelector('#usageEventsFrom')?.value || '');
        const to = localInputToIso(state.mountEl.querySelector('#usageEventsTo')?.value || '');
        state.usage.explorer.server.from = from;
        state.usage.explorer.server.to = to;
        state.usage.explorer.server.email = String(state.mountEl.querySelector('#usageEventsEmail')?.value || '').trim();
        state.usage.explorer.server.module = String(state.mountEl.querySelector('#usageEventsModule')?.value || '').trim();
        state.usage.explorer.server.action = String(state.mountEl.querySelector('#usageEventsAction')?.value || '').trim();
        setUsageStatus('muted', 'Applying usage filters...');
        await loadUsageEventsInitial(state.controller.signal);
        setUsageStatus('ok', 'Usage filters applied.');
        rerenderUsageOnly();
        return;
      }
      if (action === 'usage-reset-filters') {
        state.usage.explorer.server = { email: '', module: '', action: '', from: '', to: '' };
        state.usage.explorer.search = '';
        state.usage.explorer.moreFilters = false;
        setUsageStatus('muted', 'Resetting usage filters...');
        await loadUsageEventsInitial(state.controller.signal);
        setUsageStatus('ok', 'Usage filters reset.');
        rerenderUsageOnly();
        return;
      }
      if (action === 'usage-download') {
        const fromIso = localInputToIso(state.mountEl.querySelector('#usageExportFrom')?.value || state.usage.explorer.export.from || '');
        const toIso = localInputToIso(state.mountEl.querySelector('#usageExportTo')?.value || state.usage.explorer.export.to || '');
        const format = String(state.mountEl.querySelector('#usageExportFormat')?.value || state.usage.explorer.export.format || 'ndjson').trim().toLowerCase();
        const params = new URLSearchParams();
        params.set('format', (format === 'csv') ? 'csv' : 'ndjson');
        if (fromIso) params.set('from', fromIso);
        if (toIso) params.set('to', toIso);
        window.open(`/api/admin/usage/export?${params.toString()}`, '_blank', 'noopener');
        setUsageStatus('ok', 'Download started.');
        return;
      }
      if (action === 'usage-details') {
        const id = String(actionEl.getAttribute('data-id') || '').trim();
        const item = state.usage.explorer.items.find((e) => String(e && e.id || '') === id);
        if (!item) {
          setUsageStatus('error', 'Usage event not found in current page.');
          return;
        }

        const safeParseJson = (raw, fallback) => {
          if (raw == null) return fallback;
          if (typeof raw === 'object') return raw;
          const text = String(raw || '').trim();
          if (!text) return fallback;
          try { return JSON.parse(text); } catch { return fallback; }
        };
        const shouldDropKey = (key) => {
          const k = String(key || '').toLowerCase();
          if (!k) return false;
          return k.includes('token') || k.includes('secret') || k.includes('password') || k.includes('mfa_secret');
        };
        const redactObject = (value) => {
          if (Array.isArray(value)) return value.map(redactObject);
          if (!value || typeof value !== 'object') return value;
          const out = {};
          for (const [k, v] of Object.entries(value)) {
            if (shouldDropKey(k)) continue;
            out[k] = redactObject(v);
          }
          return out;
        };

        const payload = { ...item };
        payload.metadata = redactObject(safeParseJson(item.metadata_json || item.metadata, {}));
        if (payload.metadata_json != null) delete payload.metadata_json;
        const title = `${String(item.user_email || '')} / ${String(item.module || '')} / ${String(item.action || '')}`;
        openPortalModal(title, payload);
        return;
      }
      if (action === 'events-download') {
        const fromIso = localInputToIso(state.mountEl.querySelector('#exportFrom')?.value || state.events.export.from || '');
        const toIso = localInputToIso(state.mountEl.querySelector('#exportTo')?.value || state.events.export.to || '');
        const format = String(state.mountEl.querySelector('#exportFormat')?.value || state.events.export.format || 'ndjson').trim().toLowerCase();
        const params = new URLSearchParams();
        params.set('format', (format === 'csv') ? 'csv' : 'ndjson');
        if (fromIso) params.set('from', fromIso);
        if (toIso) params.set('to', toIso);
        const base = state.events.tab === 'auth' ? '/api/admin/auth/export' : '/api/admin/audit/export';
        window.open(`${base}?${params.toString()}`, '_blank', 'noopener');
        setUsersStatus('ok', 'Download started.');
        return;
      }
        if (action === 'event-details') {
          const kind = String(actionEl.getAttribute('data-kind') || '').trim();
          const id = String(actionEl.getAttribute('data-id') || '').trim();
          const list = kind === 'auth' ? state.events.auth.items : state.events.audit.items;
          const item = list.find((e) => String(e && e.id || '') === id);
          if (!item) {
            setUsersStatus('error', 'Event not found in current page.');
            return;
          }

          // Present readable JSON for modal details without leaking secrets.
          const shouldDropKey = (key) => {
            const k = String(key || '').toLowerCase();
            if (!k) return false;
            return k.includes('token') || k.includes('secret') || k.includes('password') || k.includes('mfa_secret');
          };
          const redactObject = (value) => {
            if (Array.isArray(value)) return value.map(redactObject);
            if (!value || typeof value !== 'object') return value;
            const out = {};
            for (const [k, v] of Object.entries(value)) {
              if (shouldDropKey(k)) continue;
              out[k] = redactObject(v);
            }
            return out;
          };
          const safeParseJson = (raw, fallback) => {
            if (raw == null) return fallback;
            if (typeof raw === 'object') return raw;
            const text = String(raw || '').trim();
            if (!text) return fallback;
            try { return JSON.parse(text); } catch { return fallback; }
          };

          const payload = { ...item };
          if (kind === 'auth') {
            payload.metadata = redactObject(safeParseJson(item.metadata_json || item.metadata, {}));
            if (payload.metadata_json != null) delete payload.metadata_json;
          } else {
            payload.changed_fields = safeParseJson(item.changed_fields_json || item.changed_fields, []);
            payload.metadata = redactObject(safeParseJson(item.metadata_json || item.metadata, {}));
            if (payload.changed_fields_json != null) delete payload.changed_fields_json;
            if (payload.metadata_json != null) delete payload.metadata_json;
          }

          const title = kind === 'auth'
            ? `${String(item.email || '')} / ${String(item.event_type || '')}`
            : `${String(item.actor_email || '')} / ${String(item.module || '')} / ${String(item.action || '')}`;
          openPortalModal(title, payload);
          return;
        }
      if (action === 'mfa-verify') {
        const code = String(state.mountEl.querySelector('#mfaCode')?.value || '').trim();
        if (!/^[0-9]{6}$/.test(code)) {
          setMfaStatus('error', 'Enter a valid 6-digit code.');
          return;
        }
        setMfaStatus('muted', 'Verifying code...');
        await postJson('/auth/mfa/verify', { code });
        state.mfaEnrollment = null;
        setMfaStatus('ok', 'MFA enabled for this admin.');
        await loadAndRender();
        return;
      }
      if (action === 'send-setup') {
        if (!userId) throw new Error('Missing user ID');
        setUsersStatus('muted', 'Requesting setup email...');
        const resp = await postJsonWithMeta(`/api/admin/users/${encodeURIComponent(userId)}/send-setup-email`, {});
        if (resp.status === 429) {
          const wait = Number(resp.retryAfterSeconds || 60);
          state.perUserCooldownUntil.set(key, Date.now() + (wait * 1000));
          setUsersStatus('error', `Rate limited. Try again in ${wait}s.`);
          return;
        }
        if (!resp.ok) {
          throw new Error(String(resp.data && resp.data.error || `HTTP ${resp.status}`));
        }
        // Success path: cool down for 60 seconds to prevent accidental repeats.
        state.perUserCooldownUntil.set(key, Date.now() + 60000);
        const payload = resp.data || {};
        if (payload.webhookConfigured === false) {
          setUsersStatus('error', 'Reset email webhook is not configured.');
          return;
        }
        if (payload.emailQueued === false) {
          const upstreamStatus = payload && payload.upstreamStatus ? ` (upstream ${payload.upstreamStatus})` : '';
          if (payload && payload.reason === 'forbidden') {
            setUsersStatus('error', `Setup email blocked by upstream (403). Check Access token / n8n auth.${upstreamStatus}`);
          } else {
            setUsersStatus('error', `Setup email request failed. Check n8n executions/email provider.${upstreamStatus}`);
          }
          return;
        }
        setUsersStatus('ok', `Setup email delivered to n8n for ${email || 'user'}. Check inbox/spam.`);
        return;
      }
      if (action === 'mfa-reset') {
        const enforced = Boolean(state.sessionInfo && state.sessionInfo.flags && state.sessionInfo.flags.mfaRequiredAdmins);
        const prompt = enforced
          ? 'Reset MFA for this admin? MFA enforcement is ON. This user will NOT be able to log in again until they re-enroll MFA.'
          : 'Reset MFA for this admin? They can re-enroll later if needed.';
        const ok = await openConfirmModal({
          title: 'Reset MFA?',
          message: prompt,
          confirmLabel: 'Reset MFA',
          danger: true
        });
        if (!ok) return;
        setUsersStatus('muted', 'Resetting MFA...');
        await postJson(`/api/admin/users/${encodeURIComponent(userId)}/mfa/reset`, {});
        state.perUserCooldownUntil.set(key, Date.now() + 10000);
        setUsersStatus('ok', 'MFA reset.');
        await loadAndRender();
        return;
      }
      if (action === 'disable' || action === 'enable') {
        const status = action === 'disable' ? 'disabled' : 'active';
        setUsersStatus('muted', `Updating user status (${status})...`);
        await patchJson(`/api/admin/users/${encodeURIComponent(userId)}`, { status });
        setUsersStatus('ok', 'User updated.');
        await loadAndRender();
        return;
      }
      if (action === 'make-admin' || action === 'make-user') {
        const nextRole = action === 'make-admin' ? 'admin' : 'user';
        if (nextRole === 'admin') {
          const ok = await openConfirmModal({
            title: 'Promote to admin?',
            message: 'Promote this user to admin?',
            confirmLabel: 'Make admin'
          });
          if (!ok) return;
        }
        setUsersStatus('muted', `Updating role (${nextRole})...`);
        await patchJson(`/api/admin/users/${encodeURIComponent(userId)}`, { role: nextRole });
        setUsersStatus('ok', 'User updated.');
        await loadAndRender();
        return;
      }
      if (action === 'revoke-sessions') {
        setUsersStatus('muted', 'Revoking sessions...');
        await postJson(`/api/admin/users/${encodeURIComponent(userId)}/revoke-sessions`, {});
        setUsersStatus('ok', 'Sessions revoked.');
        await loadAndRender();
        return;
      }
    } catch (error) {
      setUsersStatus('error', String(error && error.message || 'Action failed'));
      throw error;
    } finally {
      state.inFlightActions.delete(key);
    }
  }

  const onClick = async (event) => {
    const actionEl = event.target && event.target.closest
      ? event.target.closest('[data-action]')
      : null;
    if (!actionEl) return;
    const action = String(actionEl.getAttribute('data-action') || '').trim();
    if (
      action !== 'platform-shell-load-retry-action'
      && actionEl.closest('[data-settings-contribution-owned="true"]')
    ) {
      return;
    }
    await runSettingsActivity(actionEl, () => handleActionClick(actionEl, event));
  };
  state.mountEl.addEventListener('click', onClick);
  state.cleanup.push(() => state.mountEl.removeEventListener('click', onClick));

  const onReferenceV2NavigationKeydown = (event) => {
    const nav = event.target?.closest?.('[data-settings-reference-v2-nav]');
    if (event.key === 'Escape' && nav?.getAttribute('data-open') === 'true') {
      event.preventDefault();
      closeSettingsReferenceV2Navigation();
      return;
    }
    const tab = event.target?.closest?.('button[data-action="main-tab"]');
    if (!tab || !nav) return;
    const buttons = [...nav.querySelectorAll('button[data-action="main-tab"]')]
      .filter((button) => !button.disabled && button.getAttribute('aria-disabled') !== 'true');
    const currentIndex = buttons.indexOf(tab);
    if (currentIndex < 0) return;
    let nextIndex = currentIndex;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % buttons.length;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = buttons.length - 1;
    else return;
    event.preventDefault();
    buttons[nextIndex]?.focus?.({ preventScroll: true });
  };
  state.mountEl.addEventListener('keydown', onReferenceV2NavigationKeydown);
  state.cleanup.push(() => state.mountEl.removeEventListener('keydown', onReferenceV2NavigationKeydown));

  const rerenderUsersListOnly = () => {
    if (!state.mounted) return;
    const listEl = state.mountEl.querySelector('#usersList');
    const countEl = state.mountEl.querySelector('#usersCount');
    if (!listEl) return;
    const { html, count } = renderUsersList(state.users);
    setElementHtml(listEl, html);
    if (countEl) countEl.textContent = String(count);
  };

  const onFilterInput = (event) => {
    if (!state.mounted) return;
    const t = event && event.target;
    if (!t || !t.id) return;
    if (t.id === 'deleteUserEmail') {
      state.deleteUser.email = String(t.value || '').slice(0, 320);
      return;
    }
    if (t.id === 'deleteConfirmText') {
      state.deleteUser.confirm = String(t.value || '').slice(0, 40);
      const execBtn = state.mountEl.querySelector('button[data-action="delete-user-exec"]');
      if (execBtn) {
        const confirmText = String(t.value || '').trim();
        execBtn.disabled = confirmText !== 'DELETE_USER' || !state.deleteUser.preview || Boolean(state.deleteUser.preview && state.deleteUser.preview.blocked);
      }
      return;
    }
    if (t.id === 'filterQuery') {
      state.filters.query = String(t.value || '').slice(0, 200);
      rerenderUsersListOnly();
      return;
    }
    if (t.id === 'eventsSearch') {
      state.events.search = String(t.value || '').slice(0, 400);
      rerenderAuditOnly();
      return;
    }
    if (t.id === 'usageEventsSearch') {
      state.usage.explorer.search = String(t.value || '').slice(0, 400);
      rerenderUsageOnly();
      return;
    }
    if (t.id === 'exportFrom') {
      state.events.export.from = String(t.value || '');
      return;
    }
    if (t.id === 'exportTo') {
      state.events.export.to = String(t.value || '');
      return;
    }
    if (t.id === 'exportFormat') {
      const v = String(t.value || '').trim().toLowerCase();
      state.events.export.format = v === 'csv' ? 'csv' : 'ndjson';
      return;
    }
    if (t.id === 'usageExportFrom') {
      state.usage.explorer.export.from = String(t.value || '');
      return;
    }
    if (t.id === 'usageExportTo') {
      state.usage.explorer.export.to = String(t.value || '');
      return;
    }
    if (t.id === 'usageExportFormat') {
      const v = String(t.value || '').trim().toLowerCase();
      state.usage.explorer.export.format = v === 'csv' ? 'csv' : 'ndjson';
      return;
    }
  };
  state.mountEl.addEventListener('input', onFilterInput);
  state.mountEl.addEventListener('change', onFilterInput);
  state.cleanup.push(() => state.mountEl.removeEventListener('input', onFilterInput));
  state.cleanup.push(() => state.mountEl.removeEventListener('change', onFilterInput));

  if (!renderInitialDestinationSkeleton()) renderSettingsChrome();
  state.cleanup.push(() => {
    unregisterSettingsThemeConsumer();
  });

  if (events && typeof events.addEventListener === 'function') {
    const onPlatformAccessChanged = (event) => {
      if (!state.mounted) return;
      if (suppressNextAccessChangedEvent) {
        suppressNextAccessChangedEvent = false;
        return;
      }
      const nextAccess = event && event.detail && typeof event.detail === 'object'
        ? event.detail.access
        : (sharedState && sharedState.access ? sharedState.access : null);
      reconcileAccessState(nextAccess);
      void loadAndRender();
    };
    events.addEventListener('platform:access-changed', onPlatformAccessChanged);
    state.cleanup.push(() => {
      events.removeEventListener('platform:access-changed', onPlatformAccessChanged);
    });
  }

  installSettingsDateTimeFields({ mountEl: state.mountEl, cleanup: state.cleanup });
  state.cleanup.push(() => {
    destroyInlineSelectDsFlow();
    destroyAppearanceDsFlow();
  });

  await loadAndRender();

  return {
    __kgSource: 'settings-module-v1',
    refresh: async () => {
      await loadAndRender();
    },
    unmount: async () => {
      state.mounted = false;
      try { state.controller.abort(); } catch {}
      state.contribution.pending.clear();
      if (state.contribution.runtime) {
        await state.contribution.runtime.unmountAll({ disposeProvider: true }).catch(() => {});
        state.contribution.runtime = null;
        state.contribution.providerPluginId = '';
      }
      state.cleanup.forEach((fn) => {
        try { fn(); } catch {}
      });
      state.cleanup.length = 0;
      // Clear DOM for safety; shell controls overall layout.
      try { state.mountEl.innerHTML = ''; } catch {}
    }
  };
}

function normalizeLifecycleCtx(ctx) {
  if (!ctx || typeof ctx !== 'object') return {};
  const root = ctx.root || ctx.mountEl || null;
  if (!root || ctx.root === root) return ctx;
  return { ...ctx, root };
}

let pluginRuntime = null;
let pluginMountedCtx = null;

async function pluginMount(ctx) {
  const nextCtx = normalizeLifecycleCtx(ctx);
  if (pluginRuntime && typeof pluginRuntime.unmount === 'function') {
    await pluginRuntime.unmount(nextCtx);
  }
  pluginMountedCtx = nextCtx;
  pluginRuntime = await init(nextCtx);
}

async function pluginRefresh(ctx) {
  const nextCtx = normalizeLifecycleCtx(ctx || pluginMountedCtx);
  pluginMountedCtx = nextCtx;
  if (!pluginRuntime || typeof pluginRuntime.refresh !== 'function') return;
  await pluginRuntime.refresh(nextCtx);
}

async function pluginUnmount(ctx) {
  const nextCtx = normalizeLifecycleCtx(ctx || pluginMountedCtx);
  if (pluginRuntime && typeof pluginRuntime.unmount === 'function') {
    await pluginRuntime.unmount(nextCtx);
  }
  pluginRuntime = null;
  pluginMountedCtx = null;
}

export {
  pluginMount as mount,
  pluginRefresh as refresh,
  pluginUnmount as unmount
};
